import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// ─── AUTH CONTEXT ─────────────────────────────────────────────────────────────
function useAuth() {
  const [user, setUser]     = useState(null);
  const [token, setToken]   = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setUser(data.user))
      .catch(() => { localStorage.removeItem('token'); setToken(null); })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback((tok, usr) => {
    localStorage.setItem('token', tok);
    setToken(tok);
    setUser(usr);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const authFetch = useCallback((url, opts = {}) => {
    return fetch(`${API}${url}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
  }, [token]);

  return { user, token, loading, login, logout, authFetch };
}

// ─── SHARED LAYOUT ────────────────────────────────────────────────────────────
function Header({ user, onLogout, onDashboard, navigate }) {
  return (
    <header className="site-header">
      <div className="container">
        <a href="/" className="site-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          my<span>TN</span>divorce<span style={{color:'rgba(250,247,242,0.4)'}}>.com</span>
        </a>
        <div style={{display:'flex', alignItems:'center', gap:16}}>
          {user ? (
            <>
              <button
                className="btn btn-outline"
                style={{color:'var(--cream)',borderColor:'rgba(250,247,242,0.4)',padding:'8px 16px',fontSize:'0.85rem'}}
                onClick={onDashboard}
              >
                My Account
              </button>
              <button
                className="btn"
                style={{color:'rgba(250,247,242,0.6)',background:'none',padding:'8px 12px',fontSize:'0.82rem'}}
                onClick={onLogout}
              >
                Log Out
              </button>
            </>
          ) : (
            <>
              <span className="header-tagline" style={{display:'none'}} className="hide-mobile">Tennessee Uncontested Divorce Documents</span>
              <button
                className="btn btn-outline"
                style={{color:'var(--cream)',borderColor:'rgba(250,247,242,0.4)',padding:'8px 16px',fontSize:'0.85rem'}}
                onClick={onDashboard}
              >
                Log In / My Account
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="site-footer">
      <p>
        <strong>myTNdivorce.com</strong> is a document preparation service, not a law firm.<br />
        These forms do not constitute legal advice. You are encouraged to consult a licensed Tennessee attorney.<br />
        Documents prepared for <strong>Tennessee Circuit and Chancery Courts</strong>.
      </p>
      {/* Social links */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:20,margin:'12px 0'}}>
        <span style={{color:'rgba(250,247,242,0.6)',fontSize:'0.85rem'}}>Follow us:</span>
        <a href="https://x.com/myTNdivorce" target="_blank" rel="noopener noreferrer"
          style={{display:'inline-flex',alignItems:'center',gap:7,color:'rgba(250,247,242,0.85)',textDecoration:'none',fontSize:'0.85rem',fontWeight:600}}
          title="Follow myTNdivorce on X">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          @myTNdivorce
        </a>
        <a href="https://www.facebook.com/myTNdivorce" target="_blank" rel="noopener noreferrer"
          style={{display:'inline-flex',alignItems:'center',gap:7,color:'rgba(250,247,242,0.85)',textDecoration:'none',fontSize:'0.85rem',fontWeight:600}}
          title="Follow myTNdivorce on Facebook">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook
        </a>
      </div>
      <p style={{marginTop:8}}>
        © {new Date().getFullYear()} myTNdivorce.com &nbsp;·&nbsp;
        <a href="mailto:info@mytndivorce.com">info@mytndivorce.com</a>
        &nbsp;·&nbsp;<button onClick={() => navigate('/terms')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Terms of Service</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/privacy')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Privacy Policy</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>File by County</button>
      </p>
      <p style={{marginTop:6,fontSize:'0.8rem',opacity:0.7}}>
        <button onClick={() => navigate('/how-it-works')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>How It Works</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/how-to-file-divorce-tennessee')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>How to File</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-divorce-faq')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Divorce FAQ</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-divorce-requirements')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Requirements</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-child-support-calculator')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Child Support</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-divorce-checklist')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Checklist</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-divorce-waiting-periods')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Waiting Periods</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-parenting-plan-requirements')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Parenting Plan</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-divorce-property-division')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Property Division</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-alimony-guide')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Alimony</button>
        &nbsp;·&nbsp;<button onClick={() => navigate('/tennessee-name-change-after-divorce')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:'inherit',textDecoration:'underline'}}>Name Change</button>
      </p>
    </footer>
  );
}

const STEPS = ['Eligibility', 'Your Package', 'Create Account', 'Your Information', 'Review'];

function ProgressBar({ currentStep }) {
  return (
    <div className="progress-bar-wrap">
      <div className="container">
        <div className="progress-steps">
          {STEPS.map((label, i) => {
            const stepNum = i + 1;
            const isDone   = stepNum < currentStep;
            const isActive = stepNum === currentStep;
            return (
              <React.Fragment key={i}>
                {i > 0 && <div className={`step-connector${isDone ? ' done' : ''}`} />}
                <div className={`progress-step${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}>
                  <div className="step-num">{isDone ? '✓' : stepNum}</div>
                  <div className="step-label">{label}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
const DOC_SLIDES = [
  { filename: 'Complaint_for_Divorce.docx',          img: '/sample-complaint.png' },
  { filename: 'Marital_Dissolution_Agreement.docx',  img: '/sample-mda.png' },
  { filename: 'Permanent_Parenting_Plan_Order.docx', img: '/sample-parenting-plan.png' },
  { filename: 'Child_Support_Worksheet.pdf',         img: '/sample-cs-worksheet.png' },
  { filename: 'Final_Decree_of_Divorce.docx',        img: '/sample-decree.png' },
];
function DocumentSlideshow() {
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % DOC_SLIDES.length);
        setFading(false);
      }, 400);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const slide = DOC_SLIDES[idx];

  return (
    <div>
      <div style={{
        border:'1px solid #c8c0b0',
        borderRadius:6,
        boxShadow:'0 8px 32px rgba(0,0,0,0.12)',
        overflow:'hidden',
        background:'white',
        position:'relative',
        transition:'opacity 0.4s ease',
        opacity: fading ? 0 : 1,
      }}>
        {/* Document toolbar chrome */}
        <div style={{background:'#f0ece4',borderBottom:'1px solid #d8d0c0',padding:'8px 14px',display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:10,height:10,borderRadius:'50%',background:'#ff5f57'}} />
          <div style={{width:10,height:10,borderRadius:'50%',background:'#ffbd2e'}} />
          <div style={{width:10,height:10,borderRadius:'50%',background:'#28c840'}} />
          <span style={{marginLeft:8,fontSize:11,color:'#888',fontFamily:'monospace'}}>{slide.filename}</span>
        </div>
        {/* Document image */}
        <div style={{position:'relative',maxHeight:420,overflow:'hidden'}}>
          <img
            src={slide.img}
            alt={slide.filename}
            style={{width:'100%',display:'block'}}
          />
          {/* Fade out bottom */}
          <div style={{position:'absolute',bottom:0,left:0,right:0,height:80,background:'linear-gradient(to bottom, transparent, white)',pointerEvents:'none'}} />
        </div>
        {/* SAMPLE badge */}
        <div style={{position:'absolute',top:48,right:-16,background:'var(--gold)',color:'var(--forest)',fontWeight:700,fontSize:10,padding:'3px 22px',transform:'rotate(12deg)',boxShadow:'0 2px 8px rgba(0,0,0,0.15)',letterSpacing:1}}>SAMPLE</div>
      </div>
      {/* Dot indicators */}
      <div style={{display:'flex',justifyContent:'center',gap:6,marginTop:10}}>
        {DOC_SLIDES.map((_, i) => (
          <button key={i} onClick={() => { setFading(true); setTimeout(() => { setIdx(i); setFading(false); }, 400); }}
            style={{width:8,height:8,borderRadius:'50%',border:'none',cursor:'pointer',padding:0,
              background: i === idx ? 'var(--forest)' : '#d8d0c0',
              transition:'background 0.3s'}} />
        ))}
      </div>
      <p style={{fontSize:'0.78rem',color:'var(--stone)',marginTop:6,textAlign:'center'}}>
        Sample — actual documents populated with your names, dates, and agreed terms.
      </p>
    </div>
  );
}

function PageLanding({ onStart, navigate, coupon, setCoupon, authFetch }) {
  const [couponCode, setCouponCode] = useState(coupon?.code || '');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);

  async function handleApplyCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError(null);
    try {
      const res  = await fetch(`${API}/coupons/validate-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCoupon(data);
    } catch (err) { setCouponError(err.message); setCoupon(null); }
    finally { setCouponLoading(false); }
  }
  return (
    <>
      <div className="hero">
        <div className="container">
          <h1>Tennessee Divorce Documents<br /><span>Online. Fast. Affordable.</span></h1>
          <p>Answer a few questions and receive court-ready divorce documents for any Tennessee Circuit or Chancery Court — instantly, for a fraction of attorney fees.</p>
          <button className="btn btn-gold btn-lg" onClick={onStart}>Get Started →</button>
          <div style={{marginTop:12}}>
            <button onClick={() => navigate('/how-it-works')}
              style={{background:'none',border:'2px solid rgba(250,247,242,0.5)',color:'var(--cream)',borderRadius:6,padding:'10px 24px',fontSize:'0.95rem',cursor:'pointer',fontFamily:'var(--ff-body)'}}>
              Show Me How It Works
            </button>
          </div>
          {/* Coupon entry in hero */}
          <div style={{marginTop:20,display:'flex',gap:8,maxWidth:340,margin:'20px auto 0'}}>
            <input
              type="text"
              value={couponCode}
              onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCoupon(null); setCouponError(null); }}
              placeholder="Have a coupon code?"
              style={{flex:1,padding:'9px 14px',borderRadius:6,border:'none',fontSize:'0.88rem',color:'var(--ink)',textTransform:'uppercase'}}
            />
            <button onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()}
              style={{background:'var(--gold)',color:'white',border:'none',borderRadius:6,padding:'9px 16px',cursor:'pointer',fontWeight:600,fontSize:'0.85rem',flexShrink:0}}>
              {couponLoading ? '...' : 'Apply'}
            </button>
          </div>
          {coupon && (
            <div style={{marginTop:6,fontSize:'0.82rem',color:'var(--gold-lt)',fontWeight:600,textAlign:'center'}}>
              ✅ Coupon <span style={{fontFamily:'monospace'}}>{coupon.code}</span> applied!
            </div>
          )}
          {couponError && (
            <div style={{marginTop:6,fontSize:'0.82rem',color:'#ffb3b3',textAlign:'center'}}>❌ {couponError}</div>
          )}
          <div className="badge-row" style={{marginTop:24}}>
            <button onClick={() => navigate('/divorce')} className="badge" style={{textDecoration:'none',cursor:'pointer'}} type="button">✓ All Tennessee Counties →</button>
            <span className="badge">✓ Instant Download</span>
            <span className="badge">✓ Edit Anytime (30 days)</span>
            <span className="badge">✓ Only $29.99</span>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="container">

          {/* How it works */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:20,marginBottom:48}}>
            {[
              {icon:'📋', title:'Answer Questions', desc:'A step-by-step questionnaire collects everything needed for your documents.'},
              {icon:'👤', title:'Create Account', desc:'Your answers are saved. Edit and regenerate documents free for 30 days.'},
              {icon:'💳', title:'Pay Securely', desc:'One-time payment via Stripe. No hidden fees or subscription.'},
              {icon:'⬇️', title:'Download in Word', desc:'Court-ready .docx files — edit freely in Microsoft Word or Google Docs before filing.'},
            ].map((s, i) => (
              <div key={i} className="card" style={{textAlign:'center'}}>
                <div style={{fontSize:'2.2rem',marginBottom:10}}>{s.icon}</div>
                <h3 style={{marginBottom:6}}>{s.title}</h3>
                <p style={{color:'var(--muted)',fontSize:'0.88rem',margin:0}}>{s.desc}</p>
              </div>
            ))}
          </div>

          {/* ── TRUST BADGES ───────────────────────────────────────────────── */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:32}}>
            {[
              { icon:'📧', title:'Share With Your Spouse', desc:'Email documents directly from your dashboard' },
              { icon:'🔒', title:'SSL Encrypted', desc:'Bank-level security on every page' },
              { icon:'💳', title:'Secure Checkout', desc:'Payments processed by Stripe' },
              { icon:'📅', title:'30-Day Access', desc:'Edit and regenerate anytime' },
              { icon:'⚖️', title:'Tennessee Courts', desc:'Formatted for all 95 counties' },
              { icon:'💬', title:'Technical Support', desc:'Contact us at info@mytndivorce.com' },
              { icon:'🔐', title:'Private by Design', desc:'We never access your documents' },
            ].map((b, i) => (
              <div key={i} style={{textAlign:'center',padding:'16px 12px',border:'1px solid var(--border)',borderRadius:8,background:'white'}}>
                <div style={{fontSize:'1.6rem',marginBottom:6}}>{b.icon}</div>
                <div style={{fontWeight:700,fontSize:'0.82rem',color:'var(--forest)',marginBottom:3}}>{b.title}</div>
                <div style={{fontSize:'0.75rem',color:'var(--muted)',lineHeight:1.4}}>{b.desc}</div>
              </div>
            ))}
          </div>

          {/* Technical support callout */}
          <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:8,padding:'14px 20px',marginBottom:48,display:'flex',alignItems:'center',gap:14}}>
            <span style={{fontSize:'1.5rem',flexShrink:0}}>💬</span>
            <div style={{fontSize:'0.88rem',color:'var(--ink)',lineHeight:1.6}}>
              <strong>Questions or technical issues?</strong> Contact us at{' '}
              <a href="mailto:info@mytndivorce.com" style={{color:'var(--forest)'}}>info@mytndivorce.com</a>
              {' '}and we will respond as soon as possible. myTNdivorce.com is a document preparation service — we do not provide legal advice.
            </div>
          </div>

          {/* ── VIDEO ──────────────────────────────────────────────────── */}
          <div style={{marginBottom:48}}>
            <h2 style={{fontFamily:'var(--ff-display)',fontSize:'1.35rem',color:'var(--forest)',marginBottom:8,textAlign:'center'}}>
              See How It Works
            </h2>
            <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.88rem',marginBottom:20}}>
              Watch our short walkthrough and see how easy it is to prepare your Tennessee divorce documents.
            </p>
            {/* Limited-time pricing notice — videos may show older prices */}
            <div style={{maxWidth:640,margin:'0 auto 18px',background:'linear-gradient(135deg,#fff8e6 0%,#fdf0cf 100%)',border:'2px solid var(--gold)',borderRadius:10,padding:'14px 18px',textAlign:'center'}}>
              <div style={{fontFamily:'var(--ff-display)',fontWeight:700,fontSize:'1.05rem',color:'var(--forest)',letterSpacing:'0.02em',marginBottom:4}}>
                ★ LIMITED-TIME PRICING — ALL PACKAGES $29.99
              </div>
              <div style={{fontSize:'0.85rem',color:'var(--ink)'}}>
                Every package — with or without children or real property — is <strong style={{color:'var(--forest)'}}>$29.99</strong> for a limited time. The pricing shown in the video below is outdated — please disregard any older amounts.
              </div>
            </div>
            <div style={{maxWidth:640,margin:'0 auto',borderRadius:10,overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,0.12)'}}>
              <div style={{padding:'56.25% 0 0 0',position:'relative'}}>
                <iframe
                  src="https://player.vimeo.com/video/1187912640?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=0"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{position:'absolute',top:0,left:0,width:'100%',height:'100%'}}
                  title="myTNdivorce.com - How It Works"
                />
              </div>
            </div>
            <p style={{textAlign:'center',fontSize:'0.85rem',color:'var(--stone)',marginTop:10}}>
              Want more detail?{' '}
              <button onClick={() => navigate('/how-it-works')} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',padding:0,textDecoration:'underline',fontSize:'inherit'}}>
                Watch our step-by-step video guides →
              </button>
            </p>
          </div>

          {/* ── REASSURANCE SECTION ────────────────────────────────────────── */}
          <div style={{
            background:'linear-gradient(135deg, #f0f7f2 0%, #f8f6f0 100%)',
            border:'1.5px solid var(--forest)',
            borderRadius:12,
            padding:'36px 40px',
            marginBottom:48,
          }}>
            <h2 style={{fontFamily:'var(--ff-display)',fontSize:'1.4rem',color:'var(--forest)',marginBottom:8,textAlign:'center'}}>
              Take Your Time. There's No Pressure.
            </h2>
            <p style={{textAlign:'center',color:'var(--muted)',fontSize:'0.9rem',marginBottom:32,maxWidth:580,margin:'0 auto 32px'}}>
              Divorce is a big decision. myTNdivorce.com is designed to work at your pace — not ours.
            </p>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:24}}>
              {[
                {
                  icon: '✏️',
                  title: 'No Pressure to Get It Perfect',
                  body: "Your questionnaire answers are saved in your browser so you can stop and come back later without losing your progress. Once you purchase, you can change your answers and regenerate your documents as many times as you need for 30 days — at no extra charge.",
                },
                {
                  icon: '\uD83D\uDCC5',
                  title: '30 Days to Review and Edit',
                  body: 'Once you pay, your documents are stored securely for 30 full days. Use that time to review everything with your spouse, discuss the terms, make changes, and regenerate updated documents whenever you are ready. Need more time? Extend storage for another 30 days for a small fee.',
                },
                {
                  icon: '🧭',
                  title: 'Guided Every Step of the Way',
                  body: "myTNdivorce.com walks you through the entire process with a step-by-step questionnaire. Every question includes plain-language guidance explaining what it means and why it matters. You are never left guessing.",
                },
                {
                  icon: '⚖️',
                  title: 'You Can Always Hire an Attorney',
                  body: "If at any point you feel overwhelmed or realize your situation is more complicated than you thought, you can always consult or hire a Tennessee attorney. The $29.99 you spent is not wasted — you will have thought through the key issues and have a head start. Our documents are court-ready and attorney-friendly.",
                },
              ].map((item, i) => (
                <div key={i} style={{background:'white',borderRadius:8,padding:'20px 22px',boxShadow:'0 2px 8px rgba(0,0,0,0.06)'}}>
                  <div style={{fontSize:'1.8rem',marginBottom:10}}>{item.icon}</div>
                  <div style={{fontWeight:700,color:'var(--forest)',marginBottom:8,fontSize:'0.95rem'}}>{item.title}</div>
                  <div style={{fontSize:'0.84rem',color:'var(--ink)',lineHeight:1.65}}>{item.body}</div>
                </div>
              ))}
            </div>
            <div style={{textAlign:'center',marginTop:32}}>
              <button className="btn btn-gold btn-lg" onClick={onStart}>Start Your Questionnaire →</button>
              <p style={{fontSize:'0.78rem',color:'var(--stone)',marginTop:10}}>
                No commitment until you pay. Your answers are saved automatically.
              </p>
            </div>
          </div>

          {/* Sample document preview + Word callout — side by side on desktop */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:32,marginBottom:48,alignItems:'start'}} className="sample-preview-grid">

            {/* Left: Document slideshow preview */}
            <div>
              <h2 style={{fontFamily:'var(--ff-display)',fontSize:'1.35rem',color:'var(--forest)',marginBottom:8}}>
                See What You'll Receive
              </h2>
              <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:16}}>
                Every document is prepared in proper Tennessee legal format — the same style used by attorneys in Circuit and Chancery courts statewide.
              </p>
              <DocumentSlideshow />
            </div>

            {/* Right: Word format callout + what's included */}
            <div>
              <h2 style={{fontFamily:'var(--ff-display)',fontSize:'1.35rem',color:'var(--forest)',marginBottom:8}}>
                Your Documents, Your Way
              </h2>
              <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:20}}>
                You are your own attorney — filing pro se. Our documents are delivered in Microsoft Word (.docx) format so you remain in full control.
              </p>

              {/* Word format callout card */}
              <div style={{
                background:'#f0f7f2',
                border:'1.5px solid var(--forest)',
                borderRadius:8,
                padding:'20px 22px',
                marginBottom:20,
              }}>
                <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
                  <div style={{fontSize:'2rem',lineHeight:1,flexShrink:0}}>📝</div>
                  <div>
                    <div style={{fontWeight:700,color:'var(--forest)',marginBottom:4,fontSize:'0.95rem'}}>
                      Delivered as Word Documents (.docx)
                    </div>
                    <div style={{fontSize:'0.84rem',color:'var(--ink)',lineHeight:1.6}}>
                      Open, review, and edit your documents in Microsoft Word, Google Docs, or any compatible word processor before signing, notarizing, and filing. You are the pro se party — you have every right to review and refine your own filings.
                    </div>
                  </div>
                </div>
              </div>

              {/* What's included by category */}
              <div style={{fontSize:'0.84rem'}}>
                <div style={{fontWeight:700,color:'var(--forest)',marginBottom:10}}>Documents included by package:</div>
                {[
                  { cat:'No Children · No Real Property', price:'$29.99', docs:['Complaint for Divorce','Marital Dissolution Agreement','Final Decree of Divorce'] },
                  { cat:'No Children · With Real Property', price:'$29.99', docs:['Complaint for Divorce','Marital Dissolution Agreement (Real Property)','Final Decree of Divorce'] },
                  { cat:'With Children · No Real Property', price:'$29.99', docs:['Complaint for Divorce','Marital Dissolution Agreement','Permanent Parenting Plan Order','Child Support Worksheet','Final Decree of Divorce'] },
                  { cat:'With Children · With Real Property', price:'$29.99', docs:['Complaint for Divorce','Marital Dissolution Agreement (Real Property)','Permanent Parenting Plan Order','Child Support Worksheet','Final Decree of Divorce'] },
                ].map((pkg, i) => (
                  <div key={i} style={{
                    border:'1px solid var(--border)',
                    borderRadius:6,
                    padding:'12px 14px',
                    marginBottom:10,
                    background:'white',
                  }}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                      <div style={{fontWeight:600,color:'var(--forest)',fontSize:'0.82rem'}}>{pkg.cat}</div>
                      <div style={{fontFamily:'var(--ff-display)',fontWeight:700,color:'var(--gold)',fontSize:'1rem'}}>{pkg.price}</div>
                    </div>
                    <ul style={{margin:0,padding:'0 0 0 16px',color:'var(--muted)',lineHeight:1.7}}>
                      {pkg.docs.map((d,j) => <li key={j}>{d}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom info box */}
          <div className="alert alert-info">
            <span>⚖️</span>
            <div style={{fontSize:'0.88rem'}}>
              <strong>Available for uncontested divorces across all Tennessee counties.</strong>{' '}
              At least one spouse must reside in Tennessee and both must agree on all terms.
              These are self-help legal forms — not legal advice.{' '}
              <button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontWeight:600,textDecoration:'underline'}}>Find your county's courthouse information →</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .sample-preview-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

// ─── SCREENER ─────────────────────────────────────────────────────────────────
function StepScreener({ onNext, onExcluded }) {
  const [hasKids,        setHasKids]        = useState(null);
  const [hasProperty,    setHasProperty]    = useState(null);
  const [isContested,    setIsContested]    = useState(null);
  const [inHamilton,     setInHamilton]     = useState(null);
  const [hasRetirement,  setHasRetirement]  = useState(null);

  const canProceed = hasKids !== null && hasProperty !== null && isContested !== null && inHamilton !== null && hasRetirement !== null;
  const excluded   = isContested === true || inHamilton === false || hasRetirement === true;

  function handleNext() {
    if (excluded) { onExcluded(hasRetirement ? 'retirement' : 'other'); return; }
    // cat 1=no kids/no prop, 2=no kids/prop, 3=kids/no prop, 4=kids/prop
    const cat = hasKids ? (hasProperty ? 4 : 3) : (hasProperty ? 2 : 1);
    onNext(cat);
  }

  const Btn = ({ value, current, set, label, desc }) => (
    <button className={`choice-btn${current === value ? ' selected' : ''}`} onClick={() => set(value)} type="button">
      <span className="choice-label">{label}</span>
      {desc && <span className="choice-desc">{desc}</span>}
    </button>
  );

  return (
    <div className="main-content"><div className="container"><div className="screener-wrap">
      <div className="card">
        <h2 className="screener-title">Let's make sure we can help you</h2>
        <p className="screener-sub">Five quick questions to confirm you qualify.</p>

        {[
          { q:'1. Is your divorce uncontested? (Both spouses agree on all terms.)', set:setIsContested,
            opts:[{v:false,l:'Yes, we agree on everything',d:'No disputes'},{v:true,l:'No, we disagree',d:"We have unresolved disputes"}] },
          { q:'2. Does at least one spouse currently reside in Tennessee?', set:setInHamilton,
            opts:[{v:true,l:'Yes',d:'At least one of us lives in Tennessee and has for 6+ months'},{v:false,l:'No',d:'Neither of us currently lives in Tennessee'}] },
          { q:'3. Do you and your spouse have minor children together?', set:setHasKids,
            opts:[{v:false,l:'No children',d:'We have no minor children together'},{v:true,l:'Yes, we have children',d:'One or more minor children'}] },
          { q:'4. Do you or your spouse own real property (a home, land, etc.)?', set:setHasProperty,
            opts:[{v:false,l:'No real property',d:"We don't own any real estate"},{v:true,l:'Yes, we own real property',d:'A home, land, or other real estate'}] },
          { q:'5. Does your divorce involve dividing a retirement account, 401(k), pension, or IRA between the parties?', set:setHasRetirement,
            opts:[{v:false,l:'No retirement division',d:'Neither party is awarding the other a share of their retirement'},{v:true,l:'Yes, we are dividing a retirement account',d:'One party will receive a portion of the other\'s retirement, 401(k), pension, or IRA'}] },
        ].map(({ q, set, opts }, qi) => (
          <div className="form-section" key={qi} style={qi===4?{marginBottom:0}:{}}>
            <label>{q}</label>
            <div className="choice-grid">
              {opts.map(o => {
                const curr = qi===0 ? isContested : qi===1 ? inHamilton : qi===2 ? hasKids : qi===3 ? hasProperty : hasRetirement;
                return <Btn key={String(o.v)} value={o.v} current={curr} set={set} label={o.l} desc={o.d} />;
              })}
            </div>
          </div>
        ))}

        {canProceed && excluded && (
          <div className="alert alert-warning mt-24">
            <span>⚠️</span>
            <span>
              {isContested === true && 'Our documents are for uncontested divorces only. '}
              {inHamilton === false && 'Our documents require that at least one spouse resides in Tennessee. '}
              {hasRetirement === true && 'Dividing a retirement account requires a Qualified Domestic Relations Order (QDRO), which must be prepared by an attorney. '}
              Please contact an attorney for assistance.
            </span>
          </div>
        )}

        <div className="nav-buttons" style={{justifyContent:'flex-end'}}>
          <button className="btn btn-primary btn-lg" disabled={!canProceed || excluded} onClick={handleNext} type="button">
            See My Package →
          </button>
        </div>
      </div>
    </div></div></div>
  );
}

// ─── EXCLUDED ─────────────────────────────────────────────────────────────────
function PageExcluded({ reason, onBack }) {
  return (
    <div className="main-content"><div className="container">
      <div className="excluded-wrap">
        <div className="excluded-icon">⚖️</div>
        <h2>We're unable to process this divorce</h2>
        <p>{reason === 'kids'
          ? "Divorces with minor children require a Parenting Plan. Support for this is coming soon."
          : reason === 'retirement'
          ? "Dividing a retirement account, 401(k), pension, or IRA requires a Qualified Domestic Relations Order (QDRO), which must be prepared by a licensed attorney. This cannot be handled through our self-help document service."
          : "Our documents are for uncontested divorces where at least one party resides in Tennessee and both parties agree on all terms."
        }</p>
        <p>We recommend consulting a licensed Tennessee family law attorney.</p>
        <div className="mt-24">
          <button className="btn btn-outline" onClick={onBack} type="button">← Start Over</button>
        </div>
      </div>
    </div></div>
  );
}

// ─── PACKAGE SELECTION ────────────────────────────────────────────────────────
function StepPackage({ category, onNext, onBack, coupon }) {
  const BASE_PRICES = { 1:29.99, 2:29.99, 3:29.99, 4:29.99 };
  const pkgs = [
    { cat:1, title:'No Children · No Real Property', basePrice:29.99, desc:'No minor children and no real estate to divide.', docs:['Complaint for Divorce','Marital Dissolution Agreement','Final Decree of Divorce','TN Certificate of Divorce or Annulment'] },
    { cat:2, title:'No Children · With Real Property', basePrice:29.99, desc:'Includes real property provisions — who keeps the home, equity buyouts, refinancing, or sale.', docs:['Complaint for Divorce','Marital Dissolution Agreement (with Real Property)','Final Decree of Divorce','TN Certificate of Divorce or Annulment'] },
    { cat:3, title:'With Children · No Real Property', basePrice:29.99, desc:'Includes a Permanent Parenting Plan and Child Support Worksheet. No real estate to divide.', docs:['Complaint for Divorce','Marital Dissolution Agreement','Permanent Parenting Plan Order','Child Support Worksheet','Final Decree of Divorce','TN Certificate of Divorce or Annulment'] },
    { cat:4, title:'With Children · With Real Property', basePrice:29.99, desc:'Full package — Parenting Plan, Child Support Worksheet, and real property provisions.', docs:['Complaint for Divorce','Marital Dissolution Agreement (with Real Property)','Permanent Parenting Plan Order','Child Support Worksheet','Final Decree of Divorce','TN Certificate of Divorce or Annulment'] },
  ];

  function getCouponPrice(cat) {
    if (!coupon) return null;
    const base = BASE_PRICES[cat];
    if (coupon.discount_type === 'per_package') {
      const perPkg = { 1: coupon.price_cat1, 2: coupon.price_cat2, 3: coupon.price_cat3, 4: coupon.price_cat4 };
      return perPkg[cat] != null ? Number(perPkg[cat]) : null;
    } else if (coupon.discount_type === 'flat') {
      return Number(coupon.discount_value);
    } else if (coupon.discount_type === 'percent') {
      return Math.max(0, Math.round((base - base * coupon.discount_value / 100) * 100) / 100);
    } else {
      return Math.max(0, Math.round((base - coupon.discount_value) * 100) / 100);
    }
  }

  return (
    <div className="main-content"><div className="container">
      <h2 style={{marginBottom:8}}>Your Package</h2>
      <p className="text-muted" style={{marginBottom:coupon?4:28}}>Based on your answers, your package has been pre-selected.</p>
      {coupon && (
        <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:8,padding:'10px 16px',marginBottom:20,fontSize:'0.88rem',color:'var(--forest)',fontWeight:600}}>
          ✅ Coupon <span style={{fontFamily:'monospace'}}>{coupon.code}</span> applied — discounted prices shown below
        </div>
      )}
      <div className="packages-grid">
        {pkgs.map(pkg => {
          const couponPrice = getCouponPrice(pkg.cat);
          return (
            <div key={pkg.cat} className={`package-card${pkg.cat !== category ? ' locked' : ''}`}
              style={pkg.cat === category ? {borderColor:'var(--forest)',boxShadow:'0 0 0 3px rgba(26,58,42,0.12)'} : {}}>
              {pkg.cat === category && (
                <div style={{position:'absolute',top:14,right:14,background:'var(--forest)',color:'white',borderRadius:99,padding:'3px 12px',fontSize:'0.75rem',fontWeight:700}}>Your Package</div>
              )}
              <div className="package-title">{pkg.title}</div>
              <div className="package-price">
                {couponPrice != null ? (
                  <>
                    <span style={{textDecoration:'line-through',color:'var(--stone)',fontSize:'0.9rem',fontWeight:400}}>${pkg.basePrice.toFixed(2)}</span>
                    {' '}
                    <span style={{color:'var(--forest)'}}>${Number(couponPrice).toFixed(2)}</span>
                  </>
                ) : `$${pkg.basePrice.toFixed(2)}`}
              </div>
              <p className="package-desc">{pkg.desc}</p>
              <ul className="package-includes">{pkg.docs.map((d,i) => <li key={i}>{d}</li>)}</ul>
            </div>
          );
        })}
      </div>
      <div className="alert alert-info mt-24">
        <span>ℹ️</span>
        <span>After payment, you'll be able to <strong>edit your answers and regenerate documents for free</strong> for 30 days from your account dashboard.</span>
      </div>
      <div className="nav-buttons">
        <button className="btn btn-outline" onClick={onBack} type="button">← Back</button>
        <button className="btn btn-primary btn-lg" onClick={onNext} type="button">Create Account & Continue →</button>
      </div>
    </div></div>
  );
}

// ─── ACCOUNT CREATION / LOGIN ────────────────────────────────────────────────
function StepAccount({ onNext, onBack, auth }) {
  const [mode, setMode]         = useState('register'); // 'register' | 'login'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  // If already logged in, skip this step
  useEffect(() => { if (auth.user) onNext(); }, [auth.user, onNext]);

  async function handleSubmit() {
    setError(null);
    if (mode === 'register' && password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/${mode}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      auth.login(data.token, data.user);
      onNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="main-content"><div className="container"><div className="screener-wrap">
      <div className="card">
        <h2 style={{marginBottom:8, color:'var(--forest)'}}>
          {mode === 'register' ? 'Create Your Account' : 'Log In to Your Account'}
        </h2>
        <p className="text-muted" style={{marginBottom:24}}>
          {mode === 'register'
            ? 'Your account lets you edit answers and re-download documents for 30 days.'
            : 'Log in to continue with your existing account.'}
        </p>

        <div className="form-field">
          <label>Email Address<span className="required-star">*</span></label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="form-field">
          <label>Password<span className="required-star">*</span></label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" />
        </div>
        {mode === 'register' && (
          <div className="form-field">
            <label>Confirm Password<span className="required-star">*</span></label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter your password" />
          </div>
        )}

        {error && <div className="alert alert-error" style={{marginBottom:16}}><span>⚠️</span><span>{error}</span></div>}

        <button className="btn btn-primary btn-lg" style={{width:'100%',marginBottom:16}} onClick={handleSubmit} disabled={loading} type="button">
          {loading ? 'Please wait…' : mode === 'register' ? 'Create Account →' : 'Log In →'}
        </button>

        <p style={{textAlign:'center', fontSize:'0.88rem', color:'var(--muted)'}}>
          {mode === 'register' ? 'Already have an account? ' : "Don't have an account? "}
          <button className="btn" style={{background:'none',color:'var(--forest)',padding:0,fontWeight:600,fontSize:'0.88rem',textDecoration:'underline'}}
            onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setError(null); }} type="button">
            {mode === 'register' ? 'Log in instead' : 'Create one'}
          </button>
        </p>

        <div className="nav-buttons" style={{marginTop:24}}>
          <button className="btn btn-outline" onClick={onBack} type="button">← Back</button>
        </div>
      </div>
    </div></div></div>
  );
}

// ─── QUESTIONNAIRE ────────────────────────────────────────────────────────────
function Field({ field, value, onChange, errors = {}, formData = {} }) {
  const err = errors[field.id];

  // Christmas select — filters options based on New Year's Day selection for consistency
  if (field.type === 'christmas_select') {
    const err = errors[field.id];
    const newYearMother = formData.pp_holiday_new_year_mother || '';
    const ALL_OPTS = [
      {value:'opt1',       label:"Odd years: Father has school-out through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Jan 2 at 6pm. Rotates each year.",      nyMother:'ODD'},
      {value:'opt2',       label:"Even years: Father has school-out through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Jan 2 at 6pm. Rotates each year.",     nyMother:'EVEN'},
      {value:'opt3',       label:"Odd years: Father has Dec 24 at 2pm through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Dec 26 at 6pm. Rotates each year.", nyMother:'ODD'},
      {value:'opt4',       label:"Even years: Father has Dec 24 at 2pm through Dec 25 at 2pm; Mother has Dec 25 at 2pm through Dec 26 at 6pm. Rotates each year.",nyMother:'EVEN'},
      {value:'day_to_day', label:'Day to day schedule applies',    nyMother:null},
      {value:'custom',     label:'Custom — enter my own language', nyMother:null},
    ];
    const opts = ALL_OPTS.filter(o =>
      o.nyMother === null ||
      !newYearMother ||
      newYearMother === 'EVERY' ||
      o.nyMother === newYearMother
    );
    const hint = newYearMother === 'ODD'  ? 'Showing options consistent with Mother having New Year\'s in ODD years.' :
                 newYearMother === 'EVEN' ? 'Showing options consistent with Mother having New Year\'s in EVEN years.' : '';
    return (
      <div className="form-field">
        <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
        {hint && <div className="field-help" style={{color:'var(--forest)',fontWeight:600}}>{hint}</div>}
        <select value={value||''} onChange={e => onChange(field.id, e.target.value)} className={err?'error':''}>
          <option value="">— Select —</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {err && <div className="error-msg">{err}</div>}
      </div>
    );
  }

  // Joint PRP warning
  if (field.type === 'joint_prp_warning') {
    if (formData.pp_prp !== 'joint') return null;
    return (
      <div className="alert alert-info" style={{marginBottom:16}}>
        <span>ℹ️</span>
        <div style={{fontSize:'0.85rem'}}>
          <strong>Joint Primary Residential Parents — by agreement only.</strong> Both parents must agree, and parenting time must be exactly equal: <strong>182.5 days each per year.</strong> The days fields below have been locked to 182.5. If you need unequal time, select a single PRP instead.
        </div>
      </div>
    );
  }

  // Days field — locked to 182.5 when joint PRP (issue 6)
  if (field.type === 'pp_days_field') {
    const err = errors[field.id];
    const isJoint = formData.pp_prp === 'joint';
    const displayVal = isJoint ? '182.5' : (value || '');
    const md = Number(formData.pp_mother_days || 0);
    const fd = Number(formData.pp_father_days || 0);
    const daysWarn = !isJoint && formData.pp_mother_days && formData.pp_father_days && Math.abs(md + fd - 365) > 0.5;
    return (
      <div className="form-field">
        <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
        {field.help && <div className="field-help">{field.help}</div>}
        <input type="number" step="0.5" min="0" max="365"
          value={displayVal}
          readOnly={isJoint}
          style={{background: isJoint ? '#f0f7f0' : undefined, cursor: isJoint ? 'not-allowed' : undefined}}
          onChange={e => { if (!isJoint) onChange(field.id, e.target.value); }}
          className={err ? 'error' : ''}
          placeholder={field.placeholder || ''}
        />
        {isJoint && <div className="field-help" style={{color:'var(--forest)'}}>🔒 Locked at 182.5 — required for Joint Primary Residential Parents</div>}
        {daysWarn && <div style={{color:'var(--error)',fontSize:12,marginTop:4}}>⚠️ Mother + Father days total {md+fd}. They must equal 365.</div>}
        {err && <div className="error-msg">{err}</div>}
      </div>
    );
  }

  // CS auto-populated field — editable but pre-filled from calculator (issues 2, 3)
  if (field.type === 'cs_auto_field') {
    const err = errors[field.id];
    return (
      <div className="form-field">
        <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
        {field.help && <div className="field-help">{field.help}</div>}
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{color:'var(--muted)',fontSize:13,flexShrink:0}}>$</span>
          <input type="number" step="0.01" min="0"
            value={value || ''}
            onChange={e => onChange(field.id, e.target.value)}
            placeholder="0.00"
            style={{flex:1}}
            className={err ? 'error' : ''}
          />
        </div>
        {err && <div className="error-msg">{err}</div>}
      </div>
    );
  }

  if (field.type === 'holiday_pair') {
    const motherKey = field.id + '_mother';
    const fatherKey = field.id + '_father';
    const motherVal = formData[motherKey] !== undefined ? formData[motherKey] : (field.defaultMotherValue || '');
    const fatherVal = formData[fatherKey] || '';
    const opposite = { ODD: 'EVEN', EVEN: 'ODD' };

    function handleMotherChange(v) {
      onChange(motherKey, v);
      if (v === 'ODD' || v === 'EVEN') {
        onChange(fatherKey, opposite[v]);
      } else if (v === 'DAY_TO_DAY') {
        onChange(fatherKey, 'DAY_TO_DAY');
      } else if (v === 'NA') {
        onChange(fatherKey, 'NA');
      } else {
        onChange(fatherKey, '');
      }
    }

    const err2 = errors[motherKey] || errors[fatherKey];
    const conflict = motherVal === fatherVal && (motherVal === 'ODD' || motherVal === 'EVEN');
    const isNA = motherVal === 'NA';

    return (
      <div className="form-field" style={{marginBottom:12}}>
        <label style={{fontWeight:600,fontSize:12,color:'#333',marginBottom:4,display:'block'}}>{field.label}</label>
        {isNA ? (
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <select value={motherVal} onChange={e => handleMotherChange(e.target.value)}
              style={{flex:1,padding:'8px 10px',border:'1.5px solid #ccc',borderRadius:4,fontSize:14}}>
              <option value="">— Select —</option>
              <option value="ODD">Mother — ODD years</option>
              <option value="EVEN">Mother — EVEN years</option>
              <option value="EVERY">Mother — EVERY year</option>
              <option value="DAY_TO_DAY">Day to day schedule applies</option>
              <option value="NA">Not applicable</option>
            </select>
            <div style={{fontSize:12,color:'var(--muted)',fontStyle:'italic'}}>Not included in plan</div>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,alignItems:'center'}}>
            <div>
              <div style={{fontSize:11,color:'#888',marginBottom:4}}>Mother gets:</div>
              <select value={motherVal} onChange={e => handleMotherChange(e.target.value)}
                style={{width:'100%',padding:'8px 10px',border:'1.5px solid #ccc',borderRadius:4,fontSize:14}}>
                <option value="">— Select —</option>
                <option value="ODD">ODD years</option>
                <option value="EVEN">EVEN years</option>
                <option value="EVERY">EVERY year</option>
                <option value="DAY_TO_DAY">Day to day schedule</option>
                <option value="NA">Not applicable</option>
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:'#888',marginBottom:4}}>Father gets:</div>
              <div style={{padding:'8px 10px',border:'1.5px solid #ccc',borderRadius:4,fontSize:14,background:'#f8f8f8',color:'#555'}}>
                {fatherVal === 'DAY_TO_DAY' ? 'Day to day schedule' : fatherVal === 'NA' ? 'Not applicable' : fatherVal || '(auto-filled)'}
              </div>
            </div>
          </div>
        )}
        {conflict && <div style={{color:'var(--error)',fontSize:12,marginTop:4}}>⚠️ Both parents cannot have the same year pattern for {field.label}.</div>}
        {err2 && <div className="error-msg">{err2}</div>}
      </div>
    );
  }

  // Child support disclaimer banner
  if (field.type === 'cs_disclaimer') {
    return (
      <div className="alert alert-warning" style={{marginBottom:16}}>
        <span>⚠️</span>
        <div style={{fontSize:'0.85rem'}}>
          <strong>Important:</strong> The child support amount calculated below is an <strong>estimate only</strong> based on the Tennessee Income Shares Guidelines (2021). Only a court can set child support. The calculated amount will be pre-filled into your documents but you may adjust it. Consult a licensed Tennessee attorney if you have questions.
        </div>
      </div>
    );
  }

  // Child support live calculator — runs Income Shares calculation and auto-fills FCSO
  if (field.type === 'cs_result') {
    const n = (v) => parseFloat(String(v || '0').replace(/[$,]/g,'')) || 0;

    // ── BCSO TABLE (compact — full table in TnChildSupportCalc.jsx) ───────────
    // Official TN Income Shares Schedule — 563 rows extracted directly from state xlsm
    // Format: [combined_agi_ceiling, c1, c2, c3, c4, c5]
    const BCSO_TABLE = [
      [150,100,100,100,100,100],[200,100,100,100,100,100],[250,100,100,100,100,100],[300,100,100,100,100,100],[350,100,100,100,100,100],[400,100,100,100,100,100],[450,100,100,100,100,100],[500,100,100,100,100,100],[550,100,100,100,100,100],[600,100,100,100,100,100],[650,100,100,100,100,100],[700,100,100,100,100,100],[750,100,100,100,100,100],[800,100,100,100,100,100],[850,100,100,100,100,100],[900,100,100,100,100,100],[950,100,100,100,100,100],[1000,100,100,107,119,131],[1050,100,127,142,154,166],[1100,135,162,177,189,201],[1150,170,197,212,224,236],[1200,205,232,247,259,271],[1250,240,267,282,294,306],[1300,275,302,317,329,341],[1350,295,337,352,364,376],[1400,305,372,387,399,411],[1450,315,407,422,434,446],[1500,325,442,457,469,481],[1550,335,477,492,504,516],[1600,345,487,527,539,551],[1650,355,500,562,574,586],[1700,365,514,597,609,621],[1750,375,528,612,644,656],[1800,384,542,628,679,691],[1850,394,555,644,714,726],[1900,403,568,657,733,761],[1950,412,580,671,748,796],[2000,421,592,685,764,831],[2050,430,604,699,779,857],[2100,439,616,713,795,874],[2150,448,628,727,810,891],[2200,457,641,741,826,908],[2250,466,653,754,841,925],[2300,475,665,768,857,942],[2350,484,677,782,872,959],[2400,493,689,796,887,976],[2450,501,701,809,902,992],[2500,510,712,821,916,1007],[2550,518,724,834,930,1023],[2600,527,735,847,945,1039],[2650,536,747,860,959,1055],[2700,544,758,873,973,1070],[2750,553,770,886,987,1086],[2800,561,781,898,1002,1102],[2850,569,792,911,1015,1117],[2900,577,802,922,1028,1130],[2950,584,812,933,1040,1144],[3000,592,822,945,1053,1159],[3050,600,833,957,1067,1174],[3100,608,844,970,1081,1190],[3150,616,855,982,1095,1205],[3200,624,866,995,1109,1220],[3250,632,877,1007,1123,1236],[3300,640,888,1020,1137,1251],[3350,648,899,1032,1151,1266],[3400,656,910,1045,1165,1282],[3450,664,921,1058,1179,1297],[3500,672,932,1070,1193,1312],[3550,680,943,1083,1207,1328],[3600,688,954,1095,1221,1343],[3650,695,964,1106,1233,1356],[3700,702,973,1116,1244,1368],[3750,709,982,1126,1255,1381],[3800,715,991,1136,1266,1393],[3850,722,1000,1145,1277,1405],[3900,729,1009,1155,1288,1417],[3950,735,1018,1165,1299,1429],[4000,742,1027,1175,1310,1441],[4050,749,1036,1185,1322,1454],[4100,756,1045,1195,1333,1466],[4150,762,1054,1205,1344,1478],[4200,769,1063,1215,1355,1490],[4250,776,1072,1225,1366,1502],[4300,779,1076,1228,1370,1507],[4350,782,1079,1231,1372,1510],[4400,785,1082,1233,1375,1512],[4450,788,1085,1235,1377,1515],[4500,791,1088,1238,1380,1518],[4550,794,1091,1240,1383,1521],[4600,797,1094,1242,1385,1524],[4650,800,1097,1245,1388,1527],[4700,803,1100,1247,1390,1529],[4750,806,1104,1249,1393,1532],[4800,809,1107,1252,1395,1535],[4850,812,1110,1254,1398,1538],[4900,815,1113,1256,1401,1541],[4950,819,1117,1261,1406,1546],[5000,823,1122,1266,1411,1552],[5050,826,1126,1270,1417,1558],[5100,830,1131,1275,1422,1564],[5150,834,1135,1280,1427,1570],[5200,838,1140,1285,1432,1576],[5250,841,1145,1290,1438,1582],[5300,845,1149,1294,1443,1587],[5350,849,1154,1299,1448,1593],[5400,853,1158,1304,1454,1599],[5450,856,1163,1309,1459,1605],[5500,860,1167,1313,1464,1611],[5550,864,1172,1318,1470,1617],[5600,868,1177,1324,1476,1623],[5650,872,1182,1329,1482,1630],[5700,876,1187,1334,1488,1636],[5750,880,1192,1339,1493,1643],[5800,884,1197,1345,1499,1649],[5850,888,1201,1350,1505,1656],[5900,892,1206,1355,1511,1662],[5950,896,1211,1361,1517,1669],[6000,900,1216,1366,1523,1675],[6050,904,1221,1371,1528,1681],[6100,907,1225,1376,1534,1687],[6150,911,1230,1381,1540,1694],[6200,915,1235,1386,1545,1700],[6250,919,1239,1391,1551,1706],[6300,923,1244,1396,1557,1712],[6350,926,1249,1401,1562,1718],[6400,930,1254,1406,1568,1725],[6450,934,1258,1411,1573,1731],[6500,938,1263,1416,1579,1737],[6550,941,1267,1420,1583,1742],[6600,942,1268,1421,1584,1743],[6650,943,1269,1422,1585,1744],[6700,944,1270,1423,1586,1745],[6750,945,1271,1424,1587,1746],[6800,946,1272,1424,1588,1747],[6850,947,1273,1425,1589,1748],[6900,948,1274,1426,1590,1749],[6950,949,1275,1427,1591,1750],[7000,950,1276,1428,1592,1751],[7050,951,1277,1429,1593,1752],[7100,952,1278,1430,1594,1753],[7150,953,1279,1430,1595,1754],[7200,954,1280,1431,1596,1755],[7250,955,1281,1432,1597,1757],[7300,956,1282,1433,1598,1758],[7350,957,1283,1434,1599,1759],[7400,958,1284,1435,1600,1760],[7450,959,1285,1436,1601,1761],[7500,960,1286,1437,1602,1762],[7550,961,1288,1438,1603,1763],[7600,962,1289,1439,1604,1765],[7650,963,1290,1440,1605,1766],[7700,964,1291,1441,1606,1767],[7750,965,1292,1442,1607,1768],[7800,967,1293,1442,1608,1769],[7850,969,1297,1446,1613,1774],[7900,974,1304,1454,1621,1783],[7950,979,1310,1461,1629,1792],[8000,984,1317,1469,1637,1801],[8050,990,1324,1476,1646,1810],[8100,995,1331,1483,1654,1819],[8150,1000,1337,1491,1662,1829],[8200,1005,1344,1498,1671,1838],[8250,1010,1351,1506,1679,1847],[8300,1015,1358,1513,1687,1856],[8350,1020,1364,1521,1695,1865],[8400,1025,1371,1528,1704,1874],[8450,1030,1378,1535,1712,1883],[8500,1035,1385,1543,1720,1892],[8550,1040,1391,1550,1728,1901],[8600,1045,1398,1558,1737,1910],[8650,1050,1405,1565,1745,1920],[8700,1055,1412,1572,1753,1929],[8750,1060,1418,1580,1762,1938],[8800,1065,1425,1587,1770,1947],[8850,1070,1432,1595,1778,1956],[8900,1075,1439,1602,1786,1965],[8950,1080,1445,1610,1795,1974],[9000,1085,1452,1617,1803,1983],[9050,1090,1459,1624,1811,1992],[9100,1094,1464,1629,1817,1998],[9150,1098,1468,1634,1822,2004],[9200,1101,1472,1639,1827,2010],[9250,1105,1477,1643,1832,2016],[9300,1108,1481,1648,1838,2021],[9350,1112,1486,1653,1843,2027],[9400,1115,1490,1657,1848,2033],[9450,1119,1495,1662,1853,2038],[9500,1122,1499,1667,1858,2044],[9550,1126,1504,1671,1863,2050],[9600,1129,1508,1676,1869,2055],[9650,1133,1513,1681,1874,2061],[9700,1136,1517,1685,1879,2067],[9750,1140,1521,1690,1884,2073],[9800,1143,1526,1694,1889,2078],[9850,1147,1530,1699,1894,2084],[9900,1150,1535,1704,1900,2090],[9950,1154,1539,1708,1905,2095],[10000,1158,1544,1713,1910,2101],[10050,1161,1548,1718,1915,2107],[10100,1165,1553,1722,1920,2112],[10150,1168,1557,1727,1926,2118],[10200,1172,1562,1732,1931,2124],[10250,1175,1566,1736,1936,2130],[10300,1179,1570,1741,1941,2135],[10350,1182,1575,1746,1946,2141],[10400,1186,1579,1750,1951,2147],[10450,1189,1584,1755,1957,2152],[10500,1193,1588,1759,1962,2158],[10550,1196,1593,1764,1967,2164],[10600,1200,1597,1769,1972,2169],[10650,1203,1602,1773,1977,2175],[10700,1207,1606,1778,1983,2181],[10750,1210,1610,1783,1988,2187],[10800,1214,1615,1787,1993,2192],[10850,1217,1619,1792,1998,2198],[10900,1221,1624,1797,2003,2204],[10950,1224,1628,1801,2008,2209],[11000,1227,1632,1805,2013,2214],[11050,1230,1636,1809,2018,2219],[11100,1233,1639,1814,2022,2225],[11150,1236,1643,1818,2027,2230],[11200,1239,1647,1822,2032,2235],[11250,1242,1651,1826,2037,2240],[11300,1245,1655,1831,2041,2245],[11350,1248,1659,1835,2046,2251],[11400,1251,1663,1839,2051,2256],[11450,1254,1667,1844,2056,2261],[11500,1257,1671,1848,2060,2266],[11550,1260,1674,1852,2065,2272],[11600,1263,1678,1856,2070,2277],[11650,1266,1682,1861,2075,2282],[11700,1269,1686,1865,2079,2287],[11750,1272,1690,1869,2084,2292],[11800,1275,1694,1873,2089,2298],[11850,1278,1698,1878,2094,2303],[11900,1281,1702,1882,2098,2308],[11950,1284,1706,1886,2103,2313],[12000,1287,1709,1890,2108,2319],[12050,1289,1713,1895,2113,2324],[12100,1292,1717,1899,2117,2329],[12150,1295,1721,1903,2122,2334],[12200,1298,1725,1907,2127,2340],[12250,1301,1729,1912,2132,2345],[12300,1304,1733,1916,2136,2350],[12350,1307,1737,1920,2141,2355],[12400,1310,1741,1925,2146,2360],[12450,1313,1744,1929,2151,2366],[12500,1316,1748,1933,2155,2371],[12550,1319,1752,1937,2160,2376],[12600,1322,1756,1942,2165,2381],[12650,1325,1760,1946,2170,2387],[12700,1328,1764,1950,2174,2391],[12750,1331,1767,1954,2178,2396],[12800,1334,1771,1958,2183,2401],[12850,1336,1774,1962,2187,2406],[12900,1339,1778,1966,2192,2411],[12950,1342,1782,1970,2196,2416],[13000,1345,1785,1974,2201,2421],[13050,1347,1789,1978,2205,2425],[13100,1350,1793,1982,2209,2430],[13150,1353,1796,1985,2214,2435],[13200,1356,1800,1989,2218,2440],[13250,1358,1803,1993,2223,2445],[13300,1361,1807,1997,2227,2450],[13350,1364,1811,2001,2231,2455],[13400,1367,1814,2005,2236,2459],[13450,1370,1818,2009,2240,2464],[13500,1372,1821,2013,2245,2469],[13550,1375,1825,2017,2249,2474],[13600,1378,1829,2021,2254,2479],[13650,1381,1832,2025,2258,2484],[13700,1383,1836,2029,2262,2489],[13750,1386,1839,2033,2267,2493],[13800,1388,1842,2036,2270,2497],[13850,1391,1845,2038,2273,2500],[13900,1393,1848,2041,2276,2503],[13950,1395,1850,2044,2279,2506],[14000,1398,1853,2046,2282,2510],[14050,1400,1856,2049,2285,2513],[14100,1402,1858,2052,2288,2516],[14150,1405,1861,2054,2291,2520],[14200,1407,1864,2057,2294,2523],[14250,1409,1867,2060,2297,2526],[14300,1411,1869,2062,2300,2529],[14350,1414,1872,2065,2303,2533],[14400,1416,1875,2068,2306,2536],[14450,1418,1877,2070,2309,2539],[14500,1421,1880,2073,2312,2543],[14550,1423,1883,2076,2315,2546],[14600,1425,1885,2078,2317,2549],[14650,1428,1888,2081,2320,2553],[14700,1430,1891,2084,2323,2556],[14750,1432,1894,2087,2326,2559],[14800,1434,1896,2089,2329,2562],[14850,1437,1899,2092,2332,2566],[14900,1439,1902,2095,2335,2569],[14950,1441,1904,2097,2338,2572],[15000,1444,1907,2100,2341,2576],[15050,1446,1910,2103,2344,2579],[15100,1448,1913,2105,2347,2582],[15150,1451,1915,2108,2350,2585],[15200,1453,1918,2111,2353,2589],[15250,1455,1921,2113,2356,2592],[15300,1457,1923,2116,2359,2595],[15350,1460,1926,2119,2362,2599],[15400,1462,1929,2121,2365,2602],[15450,1464,1932,2124,2368,2605],[15500,1467,1934,2127,2371,2609],[15550,1469,1937,2130,2374,2612],[15600,1471,1940,2132,2377,2615],[15650,1474,1942,2135,2380,2618],[15700,1476,1945,2138,2383,2622],[15750,1478,1948,2140,2386,2625],[15800,1480,1950,2143,2389,2628],[15850,1483,1953,2146,2392,2632],[15900,1485,1956,2148,2395,2635],[15950,1487,1959,2151,2398,2638],[16000,1490,1961,2154,2401,2641],[16050,1492,1964,2156,2404,2645],[16100,1494,1967,2159,2407,2648],[16150,1497,1969,2162,2410,2651],[16200,1499,1972,2164,2413,2655],[16250,1501,1975,2167,2416,2658],[16300,1503,1978,2170,2419,2661],[16350,1506,1980,2172,2422,2665],[16400,1508,1983,2175,2425,2668],[16450,1510,1986,2178,2428,2671],[16500,1513,1988,2181,2431,2674],[16550,1515,1991,2183,2434,2678],[16600,1517,1994,2186,2437,2681],[16650,1520,1997,2189,2440,2684],[16700,1522,1999,2191,2443,2688],[16750,1524,2002,2194,2446,2691],[16800,1526,2005,2197,2449,2694],[16850,1529,2007,2199,2452,2697],[16900,1531,2010,2202,2455,2701],[16950,1533,2013,2205,2458,2704],[17000,1536,2015,2207,2461,2707],[17050,1538,2018,2210,2464,2711],[17100,1540,2021,2213,2467,2714],[17150,1543,2024,2215,2470,2717],[17200,1545,2026,2218,2473,2721],[17250,1547,2029,2221,2476,2724],[17300,1550,2032,2223,2479,2727],[17350,1552,2034,2226,2482,2730],[17400,1554,2037,2229,2485,2734],[17450,1556,2040,2232,2488,2737],[17500,1559,2043,2234,2491,2740],[17550,1561,2045,2237,2494,2744],[17600,1563,2048,2240,2497,2747],[17650,1566,2051,2242,2500,2750],[17700,1568,2053,2245,2503,2753],[17750,1570,2056,2248,2506,2757],[17800,1573,2059,2250,2509,2760],[17850,1575,2062,2253,2512,2763],[17900,1577,2064,2256,2515,2767],[17950,1579,2067,2258,2518,2770],[18000,1582,2070,2261,2521,2773],[18050,1584,2072,2264,2524,2777],[18100,1586,2075,2266,2527,2780],[18150,1589,2078,2269,2530,2783],[18200,1591,2081,2272,2533,2786],[18250,1593,2083,2275,2536,2790],[18300,1596,2086,2277,2539,2793],[18350,1598,2089,2280,2542,2796],[18400,1600,2091,2283,2545,2800],[18450,1602,2094,2285,2548,2803],[18500,1605,2097,2288,2551,2806],[18550,1607,2099,2291,2554,2809],[18600,1609,2102,2293,2557,2813],[18650,1612,2105,2296,2560,2816],[18700,1614,2108,2299,2563,2819],[18750,1616,2110,2301,2566,2823],[18800,1619,2113,2304,2569,2826],[18850,1621,2116,2307,2572,2829],[18900,1623,2118,2309,2575,2833],[18950,1625,2121,2312,2578,2836],[19000,1628,2124,2315,2581,2839],[19050,1630,2127,2318,2584,2842],[19100,1633,2130,2321,2588,2847],[19150,1637,2134,2324,2592,2851],[19200,1640,2138,2328,2596,2855],[19250,1643,2141,2331,2600,2859],[19300,1646,2145,2335,2603,2864],[19350,1650,2149,2338,2607,2868],[19400,1653,2152,2342,2611,2872],[19450,1656,2156,2345,2615,2877],[19500,1660,2160,2349,2619,2881],[19550,1663,2163,2352,2623,2885],[19600,1666,2167,2356,2627,2889],[19650,1669,2171,2359,2631,2894],[19700,1673,2175,2363,2634,2898],[19750,1676,2178,2366,2638,2902],[19800,1679,2182,2370,2642,2906],[19850,1683,2186,2373,2646,2911],[19900,1686,2189,2377,2650,2915],[19950,1689,2193,2380,2654,2919],[20000,1692,2197,2384,2658,2923],[20050,1696,2200,2387,2662,2928],[20100,1699,2204,2390,2665,2932],[20150,1702,2208,2394,2669,2936],[20200,1705,2211,2397,2673,2940],[20250,1709,2215,2401,2677,2945],[20300,1712,2219,2404,2681,2949],[20350,1715,2223,2408,2685,2953],[20400,1719,2226,2411,2689,2958],[20450,1722,2230,2415,2693,2962],[20500,1725,2234,2418,2696,2966],[20550,1728,2237,2422,2700,2970],[20600,1732,2241,2425,2704,2975],[20650,1735,2245,2429,2708,2979],[20700,1738,2248,2432,2712,2983],[20750,1741,2252,2436,2716,2987],[20800,1745,2256,2439,2720,2992],[20850,1748,2259,2443,2724,2996],[20900,1751,2263,2446,2727,3000],[20950,1755,2267,2450,2731,3004],[21000,1758,2271,2453,2735,3009],[21050,1761,2274,2457,2739,3013],[21100,1764,2278,2460,2743,3017],[21150,1768,2282,2463,2747,3021],[21200,1771,2285,2467,2751,3026],[21250,1774,2289,2470,2755,3030],[21300,1778,2293,2474,2758,3034],[21350,1781,2296,2477,2762,3038],[21400,1784,2300,2481,2766,3043],[21450,1787,2304,2484,2770,3047],[21500,1791,2307,2488,2774,3051],[21550,1794,2311,2491,2778,3056],[21600,1797,2315,2495,2782,3060],[21650,1800,2318,2498,2786,3064],[21700,1804,2322,2502,2789,3068],[21750,1807,2326,2505,2793,3073],[21800,1810,2330,2509,2797,3077],[21850,1814,2333,2512,2801,3081],[21900,1817,2337,2516,2805,3085],[21950,1820,2341,2519,2809,3090],[22000,1823,2344,2523,2813,3094],[22050,1827,2348,2526,2817,3098],[22100,1830,2352,2530,2820,3102],[22150,1833,2355,2533,2824,3107],[22200,1837,2359,2536,2828,3111],[22250,1840,2363,2540,2832,3115],[22300,1843,2366,2543,2836,3119],[22350,1846,2370,2547,2840,3124],[22400,1850,2374,2550,2844,3128],[22450,1853,2378,2554,2848,3132],[22500,1856,2381,2557,2851,3137],[22550,1859,2385,2561,2855,3141],[22600,1863,2389,2564,2859,3145],[22650,1866,2392,2568,2863,3149],[22700,1869,2396,2571,2867,3154],[22750,1873,2400,2575,2871,3158],[22800,1876,2403,2578,2875,3162],[22850,1879,2407,2582,2879,3166],[22900,1882,2411,2585,2882,3171],[22950,1886,2414,2589,2886,3175],[23000,1889,2418,2592,2890,3179],[23050,1892,2422,2596,2894,3183],[23100,1896,2426,2599,2898,3188],[23150,1899,2429,2602,2902,3192],[23200,1902,2433,2606,2906,3196],[23250,1905,2437,2609,2910,3200],[23300,1909,2440,2613,2913,3205],[23350,1912,2444,2616,2917,3209],[23400,1915,2448,2620,2921,3213],[23450,1918,2451,2623,2925,3218],[23500,1922,2455,2627,2929,3222],[23550,1925,2459,2630,2933,3226],[23600,1928,2462,2634,2937,3230],[23650,1932,2466,2637,2941,3235],[23700,1935,2470,2641,2944,3239],[23750,1938,2473,2644,2948,3243],[23800,1941,2477,2648,2952,3247],[23850,1945,2481,2651,2956,3252],[23900,1948,2485,2655,2960,3256],[23950,1951,2488,2658,2964,3260],[24000,1955,2492,2662,2968,3264],[24050,1958,2496,2665,2972,3269],[24100,1961,2499,2669,2975,3273],[24150,1964,2503,2672,2979,3277],[24200,1968,2507,2675,2983,3281],[24250,1971,2510,2679,2987,3286],[24300,1974,2514,2682,2991,3290],[24350,1977,2518,2686,2995,3294],[24400,1981,2521,2689,2999,3299],[24450,1984,2525,2693,3003,3303],[24500,1987,2529,2696,3006,3307],[24550,1991,2533,2700,3010,3311],[24600,1994,2536,2703,3014,3316],[24650,1997,2540,2707,3018,3320],[24700,2000,2544,2710,3022,3324],[24750,2004,2547,2714,3026,3328],[24800,2007,2551,2717,3030,3333],[24850,2010,2555,2721,3034,3337],[24900,2014,2558,2724,3037,3341],[24950,2017,2562,2728,3041,3345],[25000,2020,2566,2731,3045,3350],[25050,2023,2569,2735,3049,3354],[25100,2027,2573,2738,3053,3358],[25150,2030,2577,2742,3057,3362],[25200,2033,2581,2745,3061,3367],[25250,2036,2584,2748,3065,3371],[25300,2040,2588,2752,3068,3375],[25350,2043,2592,2755,3072,3380],[25400,2046,2595,2759,3076,3384],[25450,2050,2599,2762,3080,3388],[25500,2053,2603,2766,3084,3392],[25550,2056,2606,2769,3088,3397],[25600,2059,2610,2773,3092,3401],[25650,2063,2614,2776,3096,3405],[25700,2066,2617,2780,3099,3409],[25750,2069,2621,2783,3103,3414],[25800,2073,2625,2787,3107,3418],[25850,2076,2628,2790,3111,3422],[25900,2079,2632,2794,3115,3426],[25950,2082,2636,2797,3119,3431],[26000,2086,2640,2801,3123,3435],[26050,2089,2643,2804,3127,3439],[26100,2092,2647,2808,3130,3443],[26150,2095,2651,2811,3134,3448],[26200,2099,2654,2814,3138,3452],[26250,2102,2658,2818,3142,3456],[26300,2105,2662,2821,3146,3460],[26350,2109,2665,2825,3150,3465],[26400,2112,2669,2828,3154,3469],[26450,2115,2673,2832,3158,3473],[26500,2118,2676,2835,3161,3478],[26550,2122,2680,2839,3165,3482],[26600,2125,2684,2842,3169,3486],[26650,2128,2688,2846,3173,3490],[26700,2132,2691,2849,3177,3495],[26750,2135,2695,2853,3181,3499],[26800,2138,2699,2856,3185,3503],[26850,2141,2702,2860,3189,3507],[26900,2145,2706,2863,3192,3512],[26950,2148,2710,2867,3196,3516],[27000,2151,2713,2870,3200,3520],[27050,2154,2717,2874,3204,3524],[27100,2158,2721,2877,3208,3529],[27150,2161,2724,2880,3211,3533],[27200,2164,2728,2884,3215,3537],[27250,2167,2731,2887,3219,3541],[27300,2170,2735,2890,3223,3545],[27350,2173,2738,2894,3227,3549],[27400,2177,2742,2897,3230,3553],[27450,2180,2746,2900,3234,3557],[27500,2183,2749,2904,3238,3562],[27550,2186,2753,2907,3242,3566],[27600,2189,2756,2911,3245,3570],[27650,2193,2760,2914,3249,3574],[27700,2196,2764,2917,3253,3578],[27750,2199,2767,2921,3257,3582],[27800,2202,2771,2924,3260,3586],[27850,2205,2774,2927,3264,3590],[27900,2208,2778,2931,3268,3595],[27950,2212,2781,2934,3272,3599],[28000,2215,2785,2938,3275,3603],[28050,2218,2789,2941,3279,3607],[28100,2221,2792,2944,3283,3611],[28150,2224,2796,2948,3287,3615],[28200,2227,2799,2951,3290,3619],[28250,2231,2803,2954,3294,3624],
    ];
    const HIGH_RATES = [0.0681,0.0722,0.0777,0.0805,0.0866];

    function lookupBCSO(combinedAgi, nc) {
      if (combinedAgi <= 0) return 100;
      const nc_ = Math.min(Math.max(Math.round(nc), 1), 5);
      // Round UP to nearest $50 (matching state VBA)
      const rounded = Math.ceil(combinedAgi / 50) * 50;
      if (rounded > 28250) {
        const base = BCSO_TABLE[BCSO_TABLE.length - 1][nc_];
        return Math.round(base + (combinedAgi - 28250) * HIGH_RATES[nc_ - 1]);
      }
      const clamped = Math.max(150, Math.min(rounded, 28250));
      // Binary search for exact match (table is in $50 increments)
      let lo = 0, hi = BCSO_TABLE.length - 1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (BCSO_TABLE[mid][0] === clamped) return BCSO_TABLE[mid][nc_];
        if (BCSO_TABLE[mid][0] < clamped) lo = mid + 1;
        else hi = mid - 1;
      }
      return BCSO_TABLE[Math.min(lo, BCSO_TABLE.length - 1)][nc_];
    }

    const numKids = (formData.children || []).length || 1;
    // Compute in-home credits using Credit Worksheet formula:
    // Credit = 75% of theoretical CS order (BCSO for this parent's income + # in-home kids)
    const computeInhomeCredit = (parentGross, numKids) => {
      if (!numKids || !parentGross) return 0;
      // Look up BCSO using just this parent's income (rounded up to nearest $50)
      const theoretical = lookupBCSO(Math.ceil(parentGross / 50) * 50, numKids);
      return theoretical * 0.75;
    };
    const computeNotInhomeCredit = (parentGross, numKids, monthlySupport) => {
      if (!numKids || !parentGross) return 0;
      const theoretical = lookupBCSO(Math.ceil(parentGross / 50) * 50, numKids) * 0.75;
      return Math.min(theoretical, monthlySupport);
    };

    const motherInhomeKids   = formData.cs_mother_has_inhome === 'Yes' ? (formData.cs_mother_inhome_children || []).length : 0;
    const fatherInhomeKids   = formData.cs_father_has_inhome === 'Yes' ? (formData.cs_father_inhome_children || []).length : 0;
    const motherNotInhomeKids= formData.cs_mother_has_notinhome === 'Yes' ? (formData.cs_mother_notinhome_children || []).length : 0;
    const fatherNotInhomeKids= formData.cs_father_has_notinhome === 'Yes' ? (formData.cs_father_notinhome_children || []).length : 0;

    const motherInhomeCredit   = computeInhomeCredit(n(formData.cs_mother_gross), motherInhomeKids);
    const fatherInhomeCredit   = computeInhomeCredit(n(formData.cs_father_gross), fatherInhomeKids);
    const motherNotInhomeCredit= computeNotInhomeCredit(n(formData.cs_mother_gross), motherNotInhomeKids, n(formData.cs_mother_notinhome_support));
    const fatherNotInhomeCredit= computeNotInhomeCredit(n(formData.cs_father_gross), fatherNotInhomeKids, n(formData.cs_father_notinhome_support));
    // Credits calculated inline — NOT stored via onChange (would cause infinite render loop)

    const mg = n(formData.cs_mother_gross) + n(formData.cs_mother_fed_benefit) - n(formData.cs_mother_se_tax) - motherInhomeCredit - motherNotInhomeCredit;
    const fg = n(formData.cs_father_gross) + n(formData.cs_father_fed_benefit) - n(formData.cs_father_se_tax) - fatherInhomeCredit - fatherNotInhomeCredit;
    const motherAgi = Math.max(0, mg);
    const fatherAgi = Math.max(0, fg);
    const combined = motherAgi + fatherAgi;

    const isJointCs = formData.pp_prp === 'joint';
    // Determine which spouse is the mother strictly from their roles (Wife = Mother, Husband = Father).
    // Returns 'plaintiff', 'defendant', or null when the roles don't determine it.
    const motherParty =
      (formData.plaintiff_role === 'Wife' || formData.defendant_role === 'Husband') ? 'plaintiff'
      : (formData.plaintiff_role === 'Husband' || formData.defendant_role === 'Wife') ? 'defendant'
      : null;
    const plaintiffIsMother = motherParty === 'plaintiff';
    const prpIsPlaintiff = formData.pp_prp === 'plaintiff';
    // Joint custody: the lower-earning parent is the recipient (PRP); the higher earner pays.
    const prpIsMother = isJointCs
      ? (motherAgi <= fatherAgi)
      : (prpIsPlaintiff && plaintiffIsMother) || (!prpIsPlaintiff && !plaintiffIsMother);
    const payorIsMother = !prpIsMother;
    const motherDays = isJointCs ? 182.5 : n(formData.pp_mother_days);
    const fatherDays = isJointCs ? 182.5 : n(formData.pp_father_days);
    const arpDays = isJointCs ? 182.5 : (prpIsMother ? fatherDays : motherDays);
    const prpDays = isJointCs ? 182.5 : 365 - arpDays;

    if (!formData.cs_mother_gross && !formData.cs_father_gross) return null;

    // Roles don't establish who is mother vs father (e.g. both set to "Spouse") — don't guess a legal number.
    if (motherParty === null) {
      return (
        <div style={{background:'#fff3cd',border:'2px solid #ffc107',borderRadius:6,padding:'16px 20px',marginBottom:16,fontSize:'0.88rem',color:'#856404'}}>
          <strong>⚠️ Set each spouse's role to calculate child support.</strong> This calculator needs one spouse designated as <strong>Wife (Mother)</strong> and the other as <strong>Husband (Father)</strong>. Please go back and set the role for each spouse, or consult a licensed Tennessee attorney.
        </div>
      );
    }

    const bcso = lookupBCSO(combined, numKids);
    // Round PI to nearest whole percent — matches state VBA behavior
    const motherPiRaw = combined > 0 ? motherAgi / combined : 0;
    const fatherPiRaw = combined > 0 ? fatherAgi / combined : 0;
    const motherPi = Math.round(motherPiRaw * 100) / 100;
    const fatherPi = Math.round(fatherPiRaw * 100) / 100;
    const arpPi = prpIsMother ? fatherPi : motherPi;
    const prpPi = prpIsMother ? motherPi : fatherPi;
    const line4a = bcso * arpPi;

    // Parenting time adjustment — exact formula from state VBA:
    // line6a = ((2 * arpDays / 182.5) - 1) * bcso * prpPi * -1
    let line7 = line4a;
    if (arpDays >= 92) {
      const line6a = ((2 * arpDays / 182.5) - 1) * bcso * prpPi * -1;
      line7 = Math.max(0, line4a + line6a);
    }

    // Additional expenses
    const mExp = n(formData.cs_mother_health_ins) + n(formData.cs_mother_medical) + n(formData.cs_mother_childcare);
    const fExp = n(formData.cs_father_health_ins) + n(formData.cs_father_medical) + n(formData.cs_father_childcare);

    // PRP owes their share of ARP's expenses; ARP owes their share of PRP's expenses
    const prpOwes = prpPi * (prpIsMother ? fExp : mExp);   // Mother's share of Father's exp
    const arpOwes = arpPi * (prpIsMother ? mExp : fExp);   // Father's share of Mother's exp

    // ASO: what each parent owes total
    const prpAso = prpOwes;           // PRP's obligation = just their expense share
    const arpAso = line7 + arpOwes;   // ARP's obligation = BCSO + their expense share

    // PCSO = difference between the two ASOs (higher minus lower)
    let pcso = Math.floor(Math.abs(arpAso - prpAso));
    pcso = Math.max(0, pcso);

    const fmt = (v) => `$${Number(v).toLocaleString('en-US', {minimumFractionDigits:0, maximumFractionDigits:0})}`;

    // Defer state updates to avoid setState-during-render
    setTimeout(() => {
      if (String(formData.cs_pcso) !== String(pcso))
        onChange('cs_pcso', String(pcso));
      if (!formData.cs_deviation_type || formData.cs_deviation_type === 'none' || !formData.cs_fcso || String(formData.cs_fcso) === '0')
        if (String(formData.cs_fcso) !== String(pcso))
          onChange('cs_fcso', String(pcso));
      if (motherInhomeCredit && String(formData.cs_mother_inhome_credit) !== motherInhomeCredit.toFixed(2))
        onChange('cs_mother_inhome_credit', motherInhomeCredit.toFixed(2));
      if (fatherInhomeCredit && String(formData.cs_father_inhome_credit) !== fatherInhomeCredit.toFixed(2))
        onChange('cs_father_inhome_credit', fatherInhomeCredit.toFixed(2));
      if (motherNotInhomeCredit && String(formData.cs_mother_notinhome_credit) !== motherNotInhomeCredit.toFixed(2))
        onChange('cs_mother_notinhome_credit', motherNotInhomeCredit.toFixed(2));
      if (fatherNotInhomeCredit && String(formData.cs_father_notinhome_credit) !== fatherNotInhomeCredit.toFixed(2))
        onChange('cs_father_notinhome_credit', fatherNotInhomeCredit.toFixed(2));
    }, 0);

    return (
      <div style={{background:'#edf7f0',border:'2px solid #1a3a2a',borderRadius:6,padding:'16px 20px',marginBottom:16}}>
        <div style={{fontWeight:700,color:'#1a3a2a',fontSize:'1rem',marginBottom:12}}>📊 Calculated Child Support (Guideline Estimate)</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:'0.85rem',color:'#333'}}>
          <div>Mother AGI: <strong>{fmt(motherAgi)}</strong></div>
          <div>Father AGI: <strong>{fmt(fatherAgi)}</strong></div>
          <div>Combined AGI: <strong>{fmt(combined)}</strong></div>
          <div>Number of children: <strong>{numKids}</strong></div>
          <div>BCSO (from schedule): <strong>{fmt(bcso)}</strong></div>
          <div>ARP days / PRP days: <strong>{arpDays} / {prpDays}</strong>{isJointCs && ' (equal time)'}</div>
        </div>
        <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #c0d8c0',display:'flex',alignItems:'center',gap:16}}>
          <div>
            <div style={{fontSize:11,color:'#555',marginBottom:2}}>Presumptive Child Support Order</div>
            <div style={{fontWeight:700,fontSize:'1.5rem',color:'#1a3a2a'}}>{fmt(pcso)}/month</div>
          </div>
          <div style={{fontSize:11,color:'#666',fontStyle:'italic',flex:1}}>
            This amount has been pre-filled below. You may change it if you are agreeing to a deviation from the guidelines.
          </div>
        </div>
        <div style={{marginTop:8,fontSize:11,color:'#888',fontStyle:'italic'}}>
          ⚠️ Estimate only. Based on Tennessee Income Shares Guidelines (2021). Only a court can set child support.
        </div>
        <div style={{marginTop:6,fontSize:10,color:'#b5c9bb',textAlign:'right',letterSpacing:'0.3px'}}>calc engine v2 · joint-fix</div>
      </div>
    );
  }

  // Both parties out of state — hard stop warning
  if (field.type === 'both_out_of_state_warning') {
    const ps = formData.plaintiff_state;
    const ds = formData.defendant_state;
    if (!ps || !ds) return null;
    if (ps !== 'Tennessee' && ds !== 'Tennessee') {
      return (
        <div className="alert alert-error" style={{marginBottom:16}}>
          <span>🚫</span>
          <div>
            <strong>Neither party resides in Tennessee.</strong> At least one spouse must be a Tennessee resident to file for divorce in Tennessee. You indicated you reside in <strong>{ps}</strong> and your spouse resides in <strong>{ds}</strong>. You cannot proceed with this service. Please consult a licensed attorney in the state where one of you resides.
          </div>
        </div>
      );
    }
    return null;
  }

  // info_nonresident banner — shows when one party is out of state
  if (field.type === 'info_nonresident') {
    const ps = formData.plaintiff_state || 'Tennessee';
    const ds = formData.defendant_state || 'Tennessee';
    const bothTN = ps === 'Tennessee' && ds === 'Tennessee';
    if (bothTN) return null;
    const nonresident = ps !== 'Tennessee' ? 'You (the Plaintiff)' : 'Your Spouse (the Defendant)';
    const nonresidentState = ps !== 'Tennessee' ? ps : ds;
    return (
      <div className="alert alert-info" style={{marginBottom:16}}>
        <span>ℹ️</span>
        <span>
          <strong>{nonresident}</strong> is a resident of <strong>{nonresidentState}</strong>.
          Because one party lives outside Tennessee, your documents will include a <strong>waiver of venue and jurisdiction</strong> in the MDA, and the Complaint and Final Decree will reflect each party's state of residence. You must still file in a Tennessee county where the Tennessee-resident spouse lives.
        </span>
      </div>
    );
  }

  if (['text','tel','email','date'].includes(field.type)) return (
    <div className="form-field">
      <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
      <input type={field.type} value={value||''} onChange={e => onChange(field.id, e.target.value)} placeholder={field.placeholder||''} className={err?'error':''} />
      {field.help && <div className="field-help">{field.help}</div>}
      {err && <div className="error-msg">{err}</div>}
    </div>
  );
  if (field.type === 'select') return (
    <div className="form-field">
      <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
      <select value={value||''} onChange={e => onChange(field.id, e.target.value)} className={err?'error':''}>
        <option value="">— Select —</option>
        {field.options.map(o => { const v = typeof o==='object'?o.value:o; const l = typeof o==='object'?o.label:o; return <option key={v} value={v}>{l}</option>; })}
      </select>
      {field.help && <div className="field-help">{field.help}</div>}
      {err && <div className="error-msg">{err}</div>}
    </div>
  );
  if (field.type === 'radio') return (
    <div className="form-field">
      <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
      <div className="radio-group">{field.options.map(opt => (
        <label key={opt} className="radio-option">
          <input type="radio" name={field.id} value={opt} checked={value===opt} onChange={() => onChange(field.id, opt)} />
          <span className="radio-option-label">{opt}</span>
        </label>
      ))}</div>
      {err && <div className="error-msg">{err}</div>}
    </div>
  );
  if (field.type === 'checkbox') return (
    <div className="form-field">
      <label className="checkbox-option">
        <input type="checkbox" checked={!!value} onChange={e => onChange(field.id, e.target.checked)} />
        <span className="checkbox-label">{field.label}{field.required && <span className="required-star"> *</span>}</span>
      </label>
      {err && <div className="error-msg">{err}</div>}
    </div>
  );
  if (field.type === 'textarea') return (
    <div className="form-field">
      <label>{field.label}{field.required && <span className="required-star">*</span>}</label>
      <textarea value={value||''} onChange={e => onChange(field.id, e.target.value)} placeholder={field.placeholder||''} className={err?'error':''} />
      {field.help && <div className="field-help">{field.help}</div>}
      {err && <div className="error-msg">{err}</div>}
    </div>
  );
  return null;
}

function RepeaterField({ field, values=[], onChange }) {
  const items = values.length ? values : [];
  const add    = () => onChange(field.id, [...items, {}]);
  const remove = idx => onChange(field.id, items.filter((_,i) => i !== idx));
  const update = (idx, subId, val) => onChange(field.id, items.map((item,i) => i===idx ? {...item,[subId]:val} : item));
  return (
    <div className="form-field">
      <label>{field.label}</label>
      {field.help && <div className="field-help" style={{marginBottom:12}}>{field.help}</div>}
      {items.map((item, idx) => (
        <div key={idx} className="repeater-item">
          <div className="repeater-item-header">
            <span className="repeater-item-title">{field.item_label} {idx+1}</span>
            <button className="btn-remove" onClick={() => remove(idx)} type="button">Remove</button>
          </div>
          {field.item_fields.map(sf => {
            if (sf.conditional_on) {
              const { field:cf, value:cv } = sf.conditional_on;
              const condVals = Array.isArray(cv) ? cv : [cv];
              if (!condVals.includes(item[cf])) return null;
            }
            return <Field key={sf.id} field={sf} value={item[sf.id]} onChange={(id,val) => update(idx,id,val)} errors={{}} />;
          })}
        </div>
      ))}
      <button className="btn-add" onClick={add} type="button">+ Add {field.item_label}</button>
    </div>
  );
}

// ─── TENNESSEE COUNTIES ───────────────────────────────────────────────────────
const TN_COUNTIES = [
  'Anderson','Bedford','Benton','Bledsoe','Blount','Bradley','Campbell',
  'Cannon','Carroll','Carter','Cheatham','Chester','Claiborne','Clay',
  'Cocke','Coffee','Crockett','Cumberland','Davidson','Decatur','DeKalb',
  'Dickson','Dyer','Fayette','Fentress','Franklin','Gibson','Giles',
  'Grainger','Greene','Grundy','Hamblen','Hamilton','Hancock','Hardeman',
  'Hardin','Hawkins','Haywood','Henderson','Henry','Hickman','Houston',
  'Humphreys','Jackson','Jefferson','Johnson','Knox','Lake','Lauderdale',
  'Lawrence','Lewis','Lincoln','Loudon','McMinn','McNairy','Macon',
  'Madison','Marion','Marshall','Maury','Meigs','Monroe','Montgomery',
  'Moore','Morgan','Obion','Overton','Perry','Pickett','Polk','Putnam',
  'Rhea','Roane','Robertson','Rutherford','Scott','Sequatchie','Sevier',
  'Shelby','Smith','Stewart','Sullivan','Sumner','Tipton','Trousdale',
  'Unicoi','Union','Van Buren','Warren','Washington','Wayne','Weakley',
  'White','Williamson','Wilson'
].map(c => ({ value: c, label: c + ' County' }));

function getSections(category, formData = {}) {
  const plaintiffIsWife = formData.plaintiff_role === 'Wife' || (!formData.plaintiff_role && true); // default show until role is known
  const realPropertyFields = [
    {id:'address',label:'Street Address',type:'text',placeholder:'456 Oak Drive',required:true},
    {id:'county',label:'County',type:'text',placeholder:'Hamilton County',required:true},
    {id:'state',label:'State and ZIP',type:'text',placeholder:'Tennessee 37411',required:true},
    {id:'title_holding',label:'How is title currently held?',type:'select',required:true,options:[{value:'joint',label:'Jointly — both spouses on the deed'},{value:'plaintiff_only',label:'In my name only'},{value:'defendant_only',label:"In my spouse's name only"}]},
    {id:'disposition',label:'What will happen to this property?',type:'select',required:true,options:[{value:'plaintiff_keeps',label:'I will keep the property'},{value:'defendant_keeps',label:'My spouse will keep the property'},{value:'sell',label:'We will sell it and divide the proceeds'},{value:'defer_sale',label:'We will defer the sale until a future event'}]},
    {id:'deed_transfer',label:'Type of deed for the transfer?',type:'select',required:true,conditional_on:{field:'disposition',value:['plaintiff_keeps','defendant_keeps']},options:[{value:'quitclaim',label:'Quitclaim Deed (most common in divorce)'},{value:'warranty',label:'Special Warranty Deed'},{value:'none_needed',label:"No deed needed — title already in keeping party's name"}]},
    {id:'equity_split',label:'How will equity be handled?',type:'select',required:true,conditional_on:{field:'disposition',value:['plaintiff_keeps','defendant_keeps']},options:[{value:'keeper_buys_out',label:'Keeping party pays the other for their equity share'},{value:'no_buyout',label:'No equity payment — keeping party takes all equity'}]},
    {id:'buyout_amount',label:'Agreed equity buyout amount',type:'text',placeholder:'$30,000.00',required:true,conditional_on:{field:'equity_split',value:'keeper_buys_out'},help:'e.g. $30,000.00'},
    {id:'buyout_method',label:'How will the buyout be paid?',type:'select',required:true,conditional_on:{field:'equity_split',value:'keeper_buys_out'},options:[{value:'refinance',label:'Refinance — pay buyout from new loan proceeds'},{value:'loan_assumption',label:'Loan Assumption — assume existing mortgage'},{value:'cash_at_closing',label:'Cash — lump sum payment'},{value:'payment_plan',label:'Installment Payments — paid over time'},{value:'deed_in_lieu',label:'Deed in Lieu — conveyance is full settlement'}]},
    {id:'buyout_deadline',label:'Deadline to complete the buyout',type:'text',placeholder:'within ninety (90) days of entry of the Final Decree',required:true,conditional_on:{field:'buyout_method',value:['refinance','loan_assumption','cash_at_closing']},help:"e.g. 'within ninety (90) days of the entry of the Final Decree of Divorce'"},
    {id:'buyout_plan_terms',label:'Describe the installment payment schedule',type:'textarea',placeholder:'e.g. $1,000 per month beginning the first of the month following entry of the Final Decree',required:true,conditional_on:{field:'buyout_method',value:'payment_plan'}},
    {id:'if_refinance_fails',label:'If refinance or assumption cannot be completed by the deadline, what happens?',type:'select',required:true,conditional_on:{field:'buyout_method',value:['refinance','loan_assumption']},options:[{value:'sell',label:'Property will be sold and proceeds divided equally'},{value:'extend',label:'Deadline extended by mutual agreement (up to 90 more days)'},{value:'other',label:'Other (describe below)'}]},
    {id:'if_refinance_fails_other',label:'Describe the fallback provision',type:'textarea',required:true,conditional_on:{field:'if_refinance_fails',value:'other'}},
    {id:'mortgage_responsible_party',label:'Who is responsible for mortgage payments until transfer or sale is complete?',type:'select',required:true,options:[{value:'plaintiff',label:'I am (the Plaintiff)'},{value:'defendant',label:'My spouse (the Defendant)'},{value:'joint',label:'We share responsibility equally'}]},
    {id:'sale_list_price_method',label:'How will the listing price be determined?',type:'select',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']},options:[{value:'agreed_value',label:'We have agreed on a specific price'},{value:'agent_recommendation',label:'A licensed agent will recommend the price'}]},
    {id:'sale_agreed_value',label:'Agreed listing price',type:'text',placeholder:'$275,000.00',required:true,conditional_on:{field:'sale_list_price_method',value:'agreed_value'}},
    {id:'sale_proceeds_split',label:'How will net sale proceeds be divided?',type:'text',placeholder:'50% to Wife and 50% to Husband',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']}},
    {id:'sale_expenses_split',label:'How will costs of sale be shared?',type:'select',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']},options:[{value:'50/50',label:'Equally — paid from sale proceeds'},{value:'plaintiff_pays',label:'I (the Plaintiff) pay all costs'},{value:'defendant_pays',label:'My spouse pays all costs'}]},
    {id:'deferred_sale_trigger',label:'What event will trigger the sale?',type:'text',placeholder:'e.g. on or before December 31, 2027',required:true,conditional_on:{field:'disposition',value:'defer_sale'}},
  ];

  const sections = [
    { id:'parties', title:'About You (the Plaintiff)', fields:[
      {id:'plaintiff_first_name',label:'Your Legal First Name',type:'text',placeholder:'Jane',required:true},
      {id:'plaintiff_middle_name',label:'Your Legal Middle Name (if applicable)',type:'text',placeholder:'Ann',required:false},
      {id:'plaintiff_last_name',label:'Your Legal Last Name (current married name)',type:'text',placeholder:'Smith',required:true},
      {id:'plaintiff_gender',label:'Your Gender (for legal document pronouns)',type:'select',required:true,options:['Female (she/her)','Male (he/him)','Non-binary (they/them)']},
      {id:'plaintiff_role',label:'How should you be referred to in the documents?',type:'select',required:true,options:['Wife','Husband','Spouse']},
      {id:'plaintiff_state',label:'State Where You Currently Reside',type:'select',required:true,
        options:['Tennessee','Alabama','Arkansas','Georgia','Kentucky','Mississippi','Missouri','North Carolina','Virginia','Other'],
        help:'Select Tennessee if you are a Tennessee resident. At least one spouse must be a Tennessee resident.'},
      {id:'plaintiff_county',label:'County Where You Currently Reside',type:'select',required:true,
        options:TN_COUNTIES,
        conditional_on:{field:'plaintiff_state',value:'Tennessee'},
        help:'Must have resided here for at least 6 months.'},
      {id:'plaintiff_county_other',label:'County Where You Currently Reside',type:'text',placeholder:'e.g. Whitfield County',required:true,
        conditional_on:{field:'plaintiff_state',value:['Alabama','Arkansas','Georgia','Kentucky','Mississippi','Missouri','North Carolina','Virginia','Other']},
        help:'Enter your county of residence.'},
      {id:'plaintiff_address',label:'Your Mailing Address',type:'text',placeholder:'123 Main Street, Chattanooga, TN 37402',required:true},
      {id:'plaintiff_phone',label:'Your Phone Number',type:'tel',placeholder:'(423) 555-0100',required:true},
      {id:'plaintiff_email',label:'Your Email Address',type:'email',placeholder:'you@email.com',required:true},
      ...(plaintiffIsWife ? [
        {id:'plaintiff_restore_name', label:'Would the Wife like her former/maiden name restored by the Court?', type:'radio', required:true, options:['Yes','No']},
        {id:'plaintiff_former_name', label:'Full legal former/maiden name to be restored (exactly as it should appear on the court order)', type:'text', placeholder:'Jane Ann Jones', required:true,
          conditional_on:{field:'plaintiff_restore_name', value:'Yes'},
          help:'Include full first, middle, and last name. The court order will use this exact name.'},
      ] : []),
    ]},
    { id:'defendant', title:'About Your Spouse (the Defendant)', fields:[
      {id:'defendant_first_name',label:"Spouse's Legal First Name",type:'text',placeholder:'John',required:true},
      {id:'defendant_middle_name',label:"Spouse's Legal Middle Name",type:'text',placeholder:'Robert',required:false},
      {id:'defendant_last_name',label:"Spouse's Legal Last Name",type:'text',placeholder:'Smith',required:true},
      {id:'defendant_gender',label:"Spouse's Gender (for pronouns)",type:'select',required:true,options:['Male (he/him)','Female (she/her)','Non-binary (they/them)']},
      {id:'defendant_role',label:'How should your spouse be referred to?',type:'select',required:true,options:['Husband','Wife','Spouse']},
      {id:'defendant_state',label:"State Where Your Spouse Currently Resides",type:'select',required:true,
        options:['Tennessee','Alabama','Arkansas','Georgia','Kentucky','Mississippi','Missouri','North Carolina','Virginia','Other'],
        help:'Select the state where your spouse currently lives.'},
      {id:'defendant_both_out_of_state_warning', label:'', type:'both_out_of_state_warning', required:false},
      {id:'defendant_county',label:"County Where Your Spouse Currently Resides",type:'select',required:true,
        options:TN_COUNTIES,
        conditional_on:{field:'defendant_state',value:'Tennessee'},
        help:'The Tennessee county where your spouse lives.'},
      {id:'defendant_county_other',label:"County Where Your Spouse Currently Resides",type:'text',placeholder:'e.g. Walker County',required:true,
        conditional_on:{field:'defendant_state',value:['Alabama','Arkansas','Georgia','Kentucky','Mississippi','Missouri','North Carolina','Virginia','Other']},
        help:'Enter your spouse\'s county of residence.'},
    ]},
    { id:'filing', title:'Filing Information', intro:'These questions determine which court will handle your divorce.', fields:[
      {id:'filing_info_nonresident', label:'', type:'info_nonresident', required:false},
      {id:'filing_county',label:'In which county do you intend to file for divorce?',type:'select',required:true,options:TN_COUNTIES,
        help:'You must file in a Tennessee county where at least one spouse resides. If neither spouse lives in the county you select, you will not be able to proceed.'},
      {id:'court_type',label:'Which court will you file in?',type:'select',required:true,
        options:[{value:'Circuit',label:'Circuit Court (default — most common for divorce)'},{value:'Chancery',label:'Chancery Court'}],
        help:'If you are unsure, select Circuit Court. Both handle uncontested divorces in most Tennessee counties.'},
    ]},
    { id:'marriage', title:'Marriage Information', fields:[
      {id:'date_of_marriage',label:'Date of Marriage',type:'date',required:true},
      {id:'county_of_marriage',label:'County and State Where You Were Married',type:'text',placeholder:'Hamilton County, Tennessee',required:true},
    ]},
    { id:'alimony', title:'Alimony', intro:'Answer these questions about spousal support. If neither party will pay alimony, simply select that option and move on.', fields:[
      {id:'alimony_type', label:'What is the alimony arrangement between the parties?', type:'select', required:true, options:[
        {value:'none',            label:'No alimony — neither party will pay or receive alimony'},
        {value:'in_futuro',       label:'Periodic alimony in futuro — ongoing monthly/periodic payments'},
        {value:'transitional',    label:'Transitional alimony — temporary support for a defined period'},
        {value:'rehabilitation',  label:'Rehabilitative alimony — support while a party gains education or job skills'},
        {value:'lump_sum',        label:'Alimony in solido — one-time lump sum payment'},
      ]},
      {id:'alimony_payor', label:'Who will pay alimony?', type:'select', required:true,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation','lump_sum']},
        options:[{value:'plaintiff',label:'I will pay (the Plaintiff)'},{value:'defendant',label:'My spouse will pay (the Defendant)'}]},
      {id:'alimony_amount', label:'Alimony amount per payment period', type:'text', placeholder:'$1,000.00', required:true,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']},
        help:"Enter the dollar amount per payment, e.g. '$1,000.00'"},
      {id:'alimony_frequency', label:'How often will alimony be paid?', type:'select', required:true,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']},
        options:['month','week','two weeks']},
      {id:'alimony_start', label:'When do payments begin?', type:'text', required:true,
        placeholder:'the first day of the month following entry of the Final Decree of Divorce',
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']},
        help:"Describe when the first payment is due, e.g. 'the first day of the month following entry of the Final Decree of Divorce'"},
      {id:'alimony_duration', label:'How long will alimony be paid?', type:'text', required:true,
        placeholder:'a period of thirty-six (36) months',
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']},
        help:"e.g. 'a period of thirty-six (36) months' or 'until further order of the Court'"},
      {id:'lump_sum_amount', label:'Total lump sum alimony amount', type:'text', placeholder:'$25,000.00', required:true,
        conditional_on:{field:'alimony_type', value:'lump_sum'}},
      {id:'lump_sum_when', label:'When will the lump sum be paid?', type:'text', required:true,
        placeholder:'within thirty (30) days of entry of the Final Decree of Divorce',
        conditional_on:{field:'alimony_type', value:'lump_sum'}},
      {id:'terminates_death', label:'Alimony terminates upon the death of either party', type:'checkbox', required:false,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']}},
      {id:'terminates_remarriage', label:'Alimony terminates upon the remarriage of the receiving party', type:'checkbox', required:false,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']}},
      {id:'terminates_cohabitation', label:'Alimony terminates if the receiving party cohabits with a romantic partner', type:'checkbox', required:false,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']}},
      {id:'alimony_modifiable', label:'Can alimony be modified by a court in the future?', type:'select', required:true,
        conditional_on:{field:'alimony_type', value:['in_futuro','transitional','rehabilitation']},
        options:[
          {value:'yes',              label:'Yes — modifiable upon a material change in circumstances (as provided by statute)'},
          {value:'no',               label:'No — not modifiable under any circumstances'},
          {value:'agreement_only',   label:'Only by written agreement of both parties'},
        ]},
      {id:'modification_terms', label:'Describe any specific circumstances under which modification is permitted (optional)', type:'textarea', required:false,
        placeholder:'e.g. a significant change in either party\'s income, loss of employment, or serious illness',
        conditional_on:{field:'alimony_modifiable', value:'yes'}},
    ]},
  ];

  if (category === 2) {
    sections.push({
      id:'real_property', title:'Real Property',
      intro:'Answer these questions for each piece of real property you and your spouse own.',
      fields:[{id:'real_property',label:'Properties',type:'repeater',item_label:'Property',item_fields:realPropertyFields}],
    });
  }

  if (category === 3 || category === 4) {
    // Children section
    sections.push({ id:'children', title:'Children', intro:'List all minor children born of or adopted during the marriage.', fields:[
      {id:'children', label:'Children', type:'repeater', item_label:'Child', item_fields:[
        {id:'first_name',  label:'First Name',         type:'text', required:true},
        {id:'middle_name', label:'Middle Name',         type:'text', required:false},
        {id:'last_name',   label:'Last Name',           type:'text', required:true},
        {id:'dob',         label:'Date of Birth',       type:'date', required:true},
      ]},
    ]});

    // Child support — runs the full BCSO calculation inline
    sections.push({ id:'child_support', title:'Child Support & Parenting Schedule', intro:'Answer these questions to calculate child support under the Tennessee Income Shares Guidelines. All amounts are monthly.', fields:[
      {id:'cs_disclaimer', label:'', type:'cs_disclaimer', required:false},

      // PRP and days — moved here from Parenting Plan (item 3)
      {id:'pp_prp', label:'Who is the Primary Residential Parent (PRP)?', type:'select', required:true,
        options:[
          {value:'plaintiff',  label:'Plaintiff (filing spouse) — Plaintiff is PRP'},
          {value:'defendant',  label:'Defendant (other spouse) — Defendant is PRP'},
          {value:'joint',      label:'Joint — equal time (182.5 days each)'},
        ]},
      {id:'pp_joint_warning', label:'', type:'joint_prp_warning', required:false},
      {id:'pp_mother_days', label:'Total days per year with Mother', type:'pp_days_field', required:true, placeholder:'245',
        help:'Mother + Father days must equal 365. Locked to 182.5 for joint schedules.',
        conditional_on:{field:'pp_prp', value:['plaintiff','defendant']}},
      {id:'pp_father_days', label:'Total days per year with Father', type:'pp_days_field', required:true, placeholder:'120',
        conditional_on:{field:'pp_prp', value:['plaintiff','defendant']}},

      // Income
      {id:'cs_mother_gross', label:"Mother's Monthly Gross Income", type:'text', required:true, placeholder:'0.00', help:'All income from all sources before taxes'},
      {id:'cs_father_gross', label:"Father's Monthly Gross Income", type:'text', required:true, placeholder:'0.00', help:'All income from all sources before taxes'},
      {id:'cs_mother_fed_benefit', label:"Federal benefit paid to Mother for child (if any)", type:'text', required:false, placeholder:'0.00', help:"Social Security or VA benefit paid from Father's account to Mother"},
      {id:'cs_father_fed_benefit', label:"Federal benefit paid to Father for child (if any)", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_mother_se_tax', label:"Mother's self-employment tax paid monthly (if any)", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_father_se_tax', label:"Father's self-employment tax paid monthly (if any)", type:'text', required:false, placeholder:'0.00'},

      // Other children — in home (item 2)
      {id:'cs_mother_has_inhome', label:"Does Mother have other children living in her home (not children of this marriage)?", type:'radio', required:true, options:['Yes','No'],
        help:'Children from another relationship who live primarily with her'},
      {id:'cs_mother_inhome_children', label:"List Mother's other in-home children (name and date of birth)", type:'repeater',
        item_label:'Child', required:false,
        conditional_on:{field:'cs_mother_has_inhome', value:'Yes'},
        item_fields:[
          {id:'name', label:'Full Name', type:'text', required:true},
          {id:'dob',  label:'Date of Birth', type:'text', placeholder:'MM/DD/YYYY', required:true},
        ]},
      {id:'cs_father_has_inhome', label:"Does Father have other children living in his home (not children of this marriage)?", type:'radio', required:true, options:['Yes','No'],
        help:'Children from another relationship who live primarily with him'},
      {id:'cs_father_inhome_children', label:"List Father's other in-home children (name and date of birth)", type:'repeater',
        item_label:'Child', required:false,
        conditional_on:{field:'cs_father_has_inhome', value:'Yes'},
        item_fields:[
          {id:'name', label:'Full Name', type:'text', required:true},
          {id:'dob',  label:'Date of Birth', type:'text', placeholder:'MM/DD/YYYY', required:true},
        ]},

      // Other children — not in home (item 2)
      {id:'cs_mother_has_notinhome', label:"Does Mother pay court-ordered child support for other children not living with her?", type:'radio', required:true, options:['Yes','No']},
      {id:'cs_mother_notinhome_children', label:"List Mother's other not-in-home children (name and date of birth)", type:'repeater',
        item_label:'Child', required:false,
        conditional_on:{field:'cs_mother_has_notinhome', value:'Yes'},
        item_fields:[
          {id:'name', label:'Full Name', type:'text', required:true},
          {id:'dob',  label:'Date of Birth', type:'text', placeholder:'MM/DD/YYYY', required:true},
        ]},
      {id:'cs_mother_notinhome_support', label:"Monthly child support Mother is ordered to pay for those children", type:'text', required:true,
        conditional_on:{field:'cs_mother_has_notinhome', value:'Yes'}, placeholder:'0.00'},
      {id:'cs_father_has_notinhome', label:"Does Father pay court-ordered child support for other children not living with him?", type:'radio', required:true, options:['Yes','No']},
      {id:'cs_father_notinhome_children', label:"List Father's other not-in-home children (name and date of birth)", type:'repeater',
        item_label:'Child', required:false,
        conditional_on:{field:'cs_father_has_notinhome', value:'Yes'},
        item_fields:[
          {id:'name', label:'Full Name', type:'text', required:true},
          {id:'dob',  label:'Date of Birth', type:'text', placeholder:'MM/DD/YYYY', required:true},
        ]},
      {id:'cs_father_notinhome_support', label:"Monthly child support Father is ordered to pay for those children", type:'text', required:true,
        conditional_on:{field:'cs_father_has_notinhome', value:'Yes'}, placeholder:'0.00'},

      // Expenses
      {id:'cs_mother_health_ins', label:"Mother's monthly health insurance premium for the children", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_father_health_ins', label:"Father's monthly health insurance premium for the children", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_mother_medical',    label:"Mother's monthly recurring uninsured medical expenses for the children", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_father_medical',    label:"Father's monthly recurring uninsured medical expenses for the children", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_mother_childcare',  label:"Mother's monthly work-related childcare expenses", type:'text', required:false, placeholder:'0.00'},
      {id:'cs_father_childcare',  label:"Father's monthly work-related childcare expenses", type:'text', required:false, placeholder:'0.00'},

      // Calculator result
      {id:'cs_result_display', label:'', type:'cs_result', required:false},

      // How paid (item 9.1 — moved here from Decision-Making)
      {id:'pp_cs_payment', label:'How will child support be paid?', type:'select', required:true,
        options:[
          {value:'central', label:'Through the Central Child Support Receipting Unit (Nashville) — recommended'},
          {value:'direct',  label:'Directly to the other parent'},
        ]},

      // Deviation
      {id:'cs_deviation_type', label:'Is the child support amount a deviation from the calculated guideline amount?', type:'select', required:true,
        options:[
          {value:'none',     label:'No — I agree to pay the calculated guideline amount'},
          {value:'upward',   label:'Yes — Upward deviation (paying more than guidelines require)'},
          {value:'downward', label:'Yes — Downward deviation (paying less than guidelines require — requires written findings)'},
        ]},
      {id:'cs_deviation_reason', label:'Reason for deviation', type:'textarea', required:true,
        conditional_on:{field:'cs_deviation_type', value:['upward','downward']},
        placeholder:'Describe why the guideline amount is being adjusted'},
      {id:'cs_deviation_unjust', label:'How is application of the Guidelines unjust or inappropriate?', type:'textarea', required:true,
        conditional_on:{field:'cs_deviation_type', value:'downward'},
        placeholder:'Required finding for downward deviations'},
      {id:'cs_deviation_best_interest', label:'How is the best interest of the child served by the deviation?', type:'textarea', required:true,
        conditional_on:{field:'cs_deviation_type', value:'downward'},
        placeholder:'Required finding for downward deviations'},
      {id:'cs_deviation_provides', label:"Does the deviated amount provide for all of the child(ren)'s needs taking into consideration both parents' income?", type:'text', required:true,
        conditional_on:{field:'cs_deviation_type', value:'downward'}, placeholder:'Yes / Explain'},
      {id:'cs_fcso', label:'Final Child Support Order (FCSO) per month', type:'cs_auto_field', required:true,
        help:'Auto-calculated from the guidelines above. Adjust only if entering a deviation amount.'},
      {id:'cs_pcso', label:'Calculated Guideline Amount (PCSO) per month', type:'cs_auto_field', required:false,
        help:'Auto-calculated guideline amount. Used on deviation forms.'},
    ]});

    // Parenting plan — residential schedule (PRP and days now on CS page)
    sections.push({ id:'parenting_plan', title:'Parenting Plan — Residential Schedule', intro:'These questions build your Permanent Parenting Plan Order.', fields:[
      {id:'pp_schedule_type', label:'What type of parenting schedule will you use?', type:'select', required:true,
        options:[
          {value:'standard',        label:'PRP has primary custody; ARP has every other weekend + one weeknight'},
          {value:'week_on_week_off', label:'Week On / Week Off — equal 50/50 alternating weeks'},
          {value:'custom',          label:'Custom — describe your own schedule below'},
        ],
        conditional_on:{field:'pp_prp', value:['plaintiff','defendant']}},
      {id:'pp_joint_exchange_day', label:'Exchange day for week-on/week-off schedule', type:'select', required:false,
        conditional_on:{field:'pp_prp', value:'joint'},
        options:['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']},
      {id:'pp_joint_exchange_time', label:'Exchange time', type:'text', required:false,
        conditional_on:{field:'pp_prp', value:'joint'},
        placeholder:'6:00 p.m.'},
      {id:'pp_weeknight', label:'Which weeknight does the ARP have?', type:'select', required:false,
        conditional_on:{field:'pp_schedule_type', value:'standard'},
        options:['Monday','Tuesday','Wednesday','Thursday']},
      {id:'pp_exchange_day', label:'Exchange day for week-on/week-off', type:'select', required:false,
        conditional_on:{field:'pp_schedule_type', value:'week_on_week_off'},
        options:['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']},
      {id:'pp_exchange_time', label:'Exchange time', type:'text', required:false,
        conditional_on:{field:'pp_schedule_type', value:'week_on_week_off'},
        placeholder:'6:00 p.m.'},
      {id:'pp_custom_schedule', label:'Describe your parenting schedule in full', type:'textarea', required:false,
        conditional_on:{field:'pp_schedule_type', value:'custom'},
        placeholder:'e.g. Children reside primarily with Mother. Father has parenting time on the 1st and 3rd weekends of each month from Friday at 6:00 p.m. to Sunday at 6:00 p.m...'},
    ]});

    // Holiday section — EVERY defaults other to blank; Christmas removed (issue 1, 3)
    sections.push({ id:'parenting_plan_holidays', title:'Parenting Plan — Holiday Schedule',
      intro:"For each holiday, select which years Mother gets it. Father automatically gets the opposite. Select EVERY if one parent always gets it (the other will be blank).",
      type:'holiday_section',
      fields:[
        {id:'pp_holiday_new_year',    label:"New Year's Day",            type:'holiday_pair', required:true},
        {id:'pp_holiday_mlk',         label:'Martin Luther King Day',    type:'holiday_pair', required:true},
        {id:'pp_holiday_presidents',  label:"Presidents' Day",           type:'holiday_pair', required:true},
        {id:'pp_holiday_easter',      label:'Easter Day',                type:'holiday_pair', required:true},
        {id:'pp_holiday_passover',    label:'Passover Day',              type:'holiday_pair', required:true},
        {id:'pp_holiday_memorial',    label:'Memorial Day',              type:'holiday_pair', required:true},
        {id:'pp_holiday_july4',       label:'July 4th',                  type:'holiday_pair', required:true},
        {id:'pp_holiday_labor',       label:'Labor Day',                 type:'holiday_pair', required:true},
        {id:'pp_holiday_halloween',   label:'Halloween',                 type:'holiday_pair', required:true},
        {id:'pp_holiday_thanksgiving',label:'Thanksgiving Day & Friday', type:'holiday_pair', required:true},
        {id:'pp_holiday_time_exception', label:'Holiday time exception (if any)', type:'text', required:false,
          placeholder:'e.g. Thanksgiving ends at 8:00 p.m. Sunday'},
      ]
    });

    // Vacation constants (issues 2, 3, 4)
    const FALL_OPTIONS = [
      {value:'father_even', label:"Father — even years / Mother — odd years"},
      {value:'father_odd',  label:"Father — odd years / Mother — even years"},
      {value:'day_to_day',  label:'Day to day schedule applies'},
      {value:'custom',      label:'Custom — enter my own language'},
    ];
    const SPRING_OPTIONS = [
      {value:'father_even', label:"Father — even years / Mother — odd years"},
      {value:'father_odd',  label:"Father — odd years / Mother — even years"},
      {value:'day_to_day',  label:'Day to day schedule applies'},
      {value:'custom',      label:'Custom — enter my own language'},
    ];
    const SUMMER_OPTIONS = [
      {value:'day_to_day',  label:'Day to day schedule applies'},
      {value:'week_on_off', label:'Parents alternate week on / week off'},
      {value:'two_weeks',   label:"Each parent has two non-consecutive weeks upon 30 days notice (Father chooses first in even years by April 1; Mother chooses first in odd years)"},
      {value:'custom',      label:'Custom — enter my own language'},
    ];

    // Vacation text values for each option
    const FALL_TEXT = {
      father_even: "Father shall parent the child(ren) in the even years and Mother shall parent the child(ren) in the odd years.",
      father_odd:  "Father shall parent the child(ren) in the odd years and Mother shall parent the child(ren) in the even years.",
      day_to_day:  "The day to day schedule shall apply.",
    };
    const SPRING_TEXT = {
      father_even: "Father shall parent the child(ren) in the even years and Mother shall parent the child(ren) in the odd years.",
      father_odd:  "Father shall parent the child(ren) in the odd years and Mother shall parent the child(ren) in the even years.",
      day_to_day:  "The day to day schedule shall apply.",
    };
    const CHRISTMAS_TEXT = {
      opt1:       "The day to day schedule shall apply except: In the odd years, the Father shall parent the child(ren) from the day and time school lets out until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until January 2nd at 6pm. This schedule shall rotate each year.",
      opt2:       "The day to day schedule shall apply except: In the even years, the Father shall parent the child(ren) from the day and time school lets out until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until January 2nd at 6pm. This schedule shall rotate each year.",
      opt3:       "The day to day schedule shall apply except: In the odd years, the Father shall parent the child(ren) from December 24th at 2pm until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until December 26th at 6pm. This schedule shall rotate each year.",
      opt4:       "The day to day schedule shall apply except: In the even years, the Father shall parent the child(ren) from December 24th at 2pm until December 25th at 2pm and the Mother shall parent the child(ren) from December 25th at 2pm until December 26th at 6pm. This schedule shall rotate each year.",
      day_to_day: "The day to day schedule shall apply.",
    };
    const SUMMER_TEXT = {
      day_to_day:  "The day to day schedule shall apply.",
      week_on_off: "The parents shall parent the child(ren) alternating week on and week off.",
      two_weeks:   "Each parent shall have two non-consecutive weeks of parenting time upon 30 days notice to the other parent. In the even years, Father shall choose his two weeks by April 1st each year and then Mother shall choose her two weeks by April 15. Mother shall choose first in the odd years.",
    };

    sections.push({ id:'parenting_plan_vacations', title:'Parenting Plan — School Breaks & Transportation', fields:[
      // Fall Break
      {id:'pp_fall_option',   label:'Fall Break arrangement', type:'select', required:true, options:FALL_OPTIONS},
      {id:'pp_fall_vacation', label:'Custom fall break language', type:'textarea', required:true,
        conditional_on:{field:'pp_fall_option', value:'custom'},
        placeholder:'Enter the fall break parenting arrangement...'},
      // Spring Break
      {id:'pp_spring_option',   label:'Spring Break arrangement', type:'select', required:true, options:SPRING_OPTIONS},
      {id:'pp_spring_vacation', label:'Custom spring break language', type:'textarea', required:true,
        conditional_on:{field:'pp_spring_option', value:'custom'},
        placeholder:'Enter the spring break parenting arrangement...'},
      // Christmas — use a special type so Field can filter options at render time based on live formData
      {id:'pp_winter_option', label:'Winter (Christmas) Break arrangement', type:'christmas_select', required:true},
      {id:'pp_winter_vacation', label:'Custom Christmas break language', type:'textarea', required:true,
        conditional_on:{field:'pp_winter_option', value:'custom'},
        placeholder:'Enter the Christmas break parenting arrangement...'},
      // Summer
      {id:'pp_summer_option',   label:'Summer Vacation arrangement', type:'select', required:true, options:SUMMER_OPTIONS},
      {id:'pp_summer_vacation', label:'Custom summer language', type:'textarea', required:true,
        conditional_on:{field:'pp_summer_option', value:'custom'},
        placeholder:'Enter the summer parenting arrangement...'},
      {id:'pp_summer_notice',     label:'Is written notice required for summer vacation?', type:'radio', required:true, options:['Yes','No']},
      {id:'pp_summer_notice_days',label:'Number of days notice required', type:'text', required:false,
        conditional_on:{field:'pp_summer_notice', value:'Yes'}, placeholder:'30'},
      // Transportation
      {id:'pp_exchange_location', label:'Exchange location for parenting time', type:'text', required:true,
        placeholder:'As agreed by the parties', defaultValue:'As agreed by the parties'},
      {id:'pp_transportation_costs', label:'Who pays long-distance transportation costs?', type:'select', required:true,
        defaultValue:'na',
        options:[
          {value:'na',     label:'Not applicable'},
          {value:'mother', label:'Mother'},
          {value:'father', label:'Father'},
          {value:'both',   label:'Both equally'},
        ]},
      {id:'pp_transportation_other', label:'Other transportation arrangements (if any)', type:'textarea', required:false},
    ],
    _fall_text: FALL_TEXT, _spring_text: SPRING_TEXT,
    _christmas_text: CHRISTMAS_TEXT, _summer_text: SUMMER_TEXT,
    });

    sections.push({ id:'parenting_plan_decisions', title:'Parenting Plan — Decision-Making', fields:[
      {id:'pp_decision_education',       label:'Educational decisions shall be made by:', type:'select', required:true, defaultValue:'joint',
        options:[{value:'joint',label:'Both jointly'},{value:'mother',label:'Mother'},{value:'father',label:'Father'}]},
      {id:'pp_decision_health',          label:'Non-emergency health care decisions shall be made by:', type:'select', required:true, defaultValue:'joint',
        options:[{value:'joint',label:'Both jointly'},{value:'mother',label:'Mother'},{value:'father',label:'Father'}]},
      {id:'pp_decision_religion',        label:'Religious upbringing decisions shall be made by:', type:'select', required:true, defaultValue:'joint',
        options:[{value:'joint',label:'Both jointly'},{value:'mother',label:'Mother'},{value:'father',label:'Father'}]},
      {id:'pp_decision_extracurricular', label:'Extracurricular activity decisions shall be made by:', type:'select', required:true, defaultValue:'joint',
        options:[{value:'joint',label:'Both jointly'},{value:'mother',label:'Mother'},{value:'father',label:'Father'}]},
    ]});

    // Tax exemptions — separate page (item 9.2)
    sections.push({ id:'parenting_plan_taxes', title:'Parenting Plan — Tax Exemptions', fields:[
      {id:'pp_mother_exemption', label:'Tax exemption — children claimed by Mother:', type:'text', required:false,
        placeholder:'e.g. All children / Child 1 in odd years / None'},
      {id:'pp_father_exemption', label:'Tax exemption — children claimed by Father:', type:'text', required:false,
        placeholder:'e.g. Child 2 / All children in even years / None'},
      {id:'pp_exemption_type', label:'How are exemptions claimed?', type:'select', required:true, defaultValue:'alternate',
        options:[{value:'alternate',label:'Alternating years'},{value:'every',label:'Every year as specified above'},{value:'other',label:'Other arrangement'}]},
    ]});

    // Special provisions and post-secondary — separate page (item 9.3)
    sections.push({ id:'parenting_plan_special', title:'Parenting Plan — Special Provisions', fields:[
      {id:'pp_special_provisions', label:'Special provisions — any other parenting provisions (optional):', type:'textarea', required:false,
        placeholder:'e.g. Neither parent shall allow overnight guests of the opposite sex who are not family members while the children are present.'},
      {id:'pp_college', label:'Post-secondary education provisions (optional):', type:'textarea', required:false,
        placeholder:"e.g. The parties agree to equally divide the cost of the children's tuition, room and board at an in-state public university."},
    ]});

    // Parent education — separate page, last before debts (item 9.4)
    sections.push({ id:'parenting_plan_education', title:'Parenting Plan — Parent Education Class', fields:[
      {id:'pp_parent_education', label:'Parent education class status:', type:'select', required:true, defaultValue:'neither',
        options:[
          {value:'both',    label:'Completed by both parents'},
          {value:'mother',  label:'Completed by Mother only'},
          {value:'father',  label:'Completed by Father only'},
          {value:'neither', label:'Not yet completed by either parent'},
        ]},
    ]});
  }

  if (category === 4) {
    sections.push({
      id:'real_property', title:'Real Property',
      intro:'Answer these questions for each piece of real property you and your spouse own.',
      fields:[{id:'real_property',label:'Properties',type:'repeater',item_label:'Property',item_fields:realPropertyFields}],
    });
  }

  sections.push(
    { id:'debts', title:'Debts & Vehicles', intro:'List any debts and vehicles each party is keeping. Leave blank if none.', fields:[
      {id:'plaintiff_debts',label:'Debts you are taking',type:'repeater',item_label:'Debt',item_fields:[{id:'description',label:"Debt description (e.g. 'Chase Visa, last 4 digits *1234')",type:'text',required:true}]},
      {id:'defendant_debts',label:"Debts your spouse is taking",type:'repeater',item_label:'Debt',item_fields:[{id:'description',label:'Debt description',type:'text',required:true}]},
      {id:'plaintiff_vehicles',label:'Vehicles you are keeping',type:'repeater',item_label:'Vehicle',item_fields:[{id:'description',label:'Year, Make, Model',type:'text',required:true}]},
      {id:'defendant_vehicles',label:"Vehicles your spouse is keeping",type:'repeater',item_label:'Vehicle',item_fields:[{id:'description',label:'Year, Make, Model',type:'text',required:true}]},
    ]},
    { id:'confirm', title:'Confirm & Continue', fields:[
      {id:'confirm_accurate',label:'I confirm that all information I have provided is accurate and complete to the best of my knowledge.',type:'checkbox',required:true},
      {id:'confirm_not_legal_advice',label:'I understand these are self-help legal forms and do not constitute legal advice. No attorney-client relationship is created by use of this service. I may consult a licensed Tennessee attorney before filing.',type:'checkbox',required:true},
      {id:'confirm_additional_forms',label:'I understand that my court may require additional documents beyond those provided here, such as cover sheets, divorce certificates, statistical forms, or other local forms. I am responsible for obtaining those forms, which are typically available from the clerk\'s office of the court where I file.',type:'checkbox',required:true},
    ]}
  );
  return sections;
}

function StepQuestionnaire({ category, onNext, onBack, initialData = {}, isEditMode = false, onRegenerate = null }) {
  const [idx, setIdx]           = useState(0);
  const [formData, setFormData] = useState(() => {
    // Apply defaultValues for fields not already in initialData
    const data = { ...initialData };
    const secs = getSections(category, data);
    secs.forEach(sec => (sec.fields || []).forEach(f => {
      if (f.defaultValue !== undefined && (data[f.id] === undefined || data[f.id] === '')) {
        data[f.id] = f.defaultValue;
      }
    }));
    return data;
  });
  const [errors, setErrors]     = useState({});
  const [showJumpMenu, setShowJumpMenu] = useState(false);
  // Track furthest section reached — prevents skipping ahead on first pass
  const [maxReached, setMaxReached] = useState(isEditMode ? 999 : 0);

  const sections = getSections(category, formData);
  const section  = sections[idx];

  function validate() {
    const errs = {};
    for (const f of section.fields) {
      // Skip non-input display types
      if (['cs_disclaimer','cs_result','info_nonresident','both_out_of_state_warning','filing_info_nonresident','joint_prp_warning'].includes(f.type)) continue;

      // Check conditional_on
      if (f.conditional_on) {
        const { field:cf, value:cv } = f.conditional_on;
        if (!(Array.isArray(cv)?cv:[cv]).includes(formData[cf])) continue;
      }

      if (!f.required) continue;

      // holiday_pair: validate the _mother sub-key (father is auto-filled)
      if (f.type === 'holiday_pair') {
        const motherKey = f.id + '_mother';
        const fatherKey = f.id + '_father';
        const mv = formData[motherKey];
        if (!mv) {
          errs[motherKey] = `Please select an option for ${f.label}.`;
        }
        // Conflict only for ODD/EVEN both same
        if (mv && formData[fatherKey] && mv === formData[fatherKey] &&
            (mv === 'ODD' || mv === 'EVEN')) {
          errs[motherKey] = `Both parents cannot have the same year pattern for ${f.label}.`;
        }
        continue;
      }

      // Standard check
      if (!formData[f.id] && formData[f.id] !== false) {
        errs[f.id] = 'This field is required.';
      }
    }

    // Joint PRP enforcement: days must be 182.5 each (issue 6)
    if (section.id === 'parenting_plan' && formData.pp_prp === 'joint') {
      const md = Number(formData.pp_mother_days || 0);
      const fd = Number(formData.pp_father_days || 0);
      if (Math.abs(md - 182.5) > 0.1 || Math.abs(fd - 182.5) > 0.1) {
        errs.pp_mother_days = 'Joint Primary Residential Parents requires exactly 182.5 days for each parent. Please update the days or change to a single PRP.';
      }
    }

    // PRP must have the MAJORITY of parenting days. By law the Primary Residential Parent
    // is the parent with whom the child resides more than 50% of the time. Block the
    // contradiction where the non-PRP parent is given the majority of days.
    if (section.id === 'parenting_plan' && (formData.pp_prp === 'plaintiff' || formData.pp_prp === 'defendant')
        && formData.pp_mother_days && formData.pp_father_days) {
      const motherParty =
        (formData.plaintiff_role === 'Wife' || formData.defendant_role === 'Husband') ? 'plaintiff'
        : (formData.plaintiff_role === 'Husband' || formData.defendant_role === 'Wife') ? 'defendant'
        : null;
      if (motherParty) {
        const prpIsMother = formData.pp_prp === motherParty;
        const md = Number(formData.pp_mother_days || 0);
        const fd = Number(formData.pp_father_days || 0);
        if (prpIsMother && md <= fd) {
          errs.pp_mother_days = 'The Mother is the Primary Residential Parent, so she must have the majority of days — more than the Father (over 182.5 of 365). Adjust the days, or change who the PRP is.';
        } else if (!prpIsMother && fd <= md) {
          errs.pp_father_days = 'The Father is the Primary Residential Parent, so he must have the majority of days — more than the Mother (over 182.5 of 365). Adjust the days, or change who the PRP is.';
        }
      }
    }

    // Cross-validation: both out of state
    if (section.id === 'defendant' && formData.plaintiff_state && formData.defendant_state) {
      if (formData.plaintiff_state !== 'Tennessee' && formData.defendant_state !== 'Tennessee') {
        errs.defendant_state = 'Neither party resides in Tennessee. At least one spouse must be a Tennessee resident to use this service.';
      }
    }

    // Special cross-validation: filing county must match at least one TN-resident party's county
    if (section.id === 'filing' && formData.filing_county) {
      const pc = formData.plaintiff_state === 'Tennessee' ? formData.plaintiff_county : null;
      const dc = formData.defendant_state === 'Tennessee'  ? formData.defendant_county  : null;
      const fc = formData.filing_county;
      if (!pc && !dc) {
        errs.filing_county = 'Neither party resides in Tennessee. We cannot prepare Tennessee divorce documents.';
      } else if (fc !== pc && fc !== dc) {
        errs.filing_county = `Neither party resides in ${fc} County. You may only file in a county where at least one spouse lives. If you need to file elsewhere, please consult an attorney.`;
      }
    }
    return errs;
  }

  function handleNext() {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    if (idx < sections.length - 1) {
      const next = idx + 1;
      setIdx(next);
      setMaxReached(m => Math.max(m, next));
      window.scrollTo(0,0);
    }
    else {
      // Resolve vacation dropdown selections to final text before submitting
      const resolved = { ...formData };
      const vacSec = sections.find(s => s.id === 'parenting_plan_vacations');
      if (vacSec) {
        const ft = vacSec._fall_text || {};
        const spt = vacSec._spring_text || {};
        const ct = vacSec._christmas_text || {};
        const sut = vacSec._summer_text || {};
        if (resolved.pp_fall_option && resolved.pp_fall_option !== 'custom')
          resolved.pp_fall_vacation = ft[resolved.pp_fall_option] || '';
        if (resolved.pp_spring_option && resolved.pp_spring_option !== 'custom')
          resolved.pp_spring_vacation = spt[resolved.pp_spring_option] || '';
        if (resolved.pp_winter_option && resolved.pp_winter_option !== 'custom')
          resolved.pp_winter_vacation = ct[resolved.pp_winter_option] || '';
        if (resolved.pp_summer_option && resolved.pp_summer_option !== 'custom')
          resolved.pp_summer_vacation = sut[resolved.pp_summer_option] || '';
      }
      onNext(resolved);
    }
  }

  const LS_KEY = 'mytndivorce_draft';

  function handleChange(id, val) {
    setFormData(p => {
      const next = {...p, [id]: val};
      // Joint PRP: auto-set both day fields to 182.5 and lock them
      if (id === 'pp_prp' && val === 'joint') {
        next.pp_mother_days = '182.5';
        next.pp_father_days = '182.5';
      }
      // Switching away from joint: clear the locked 182.5 values
      if (id === 'pp_prp' && val !== 'joint') {
        if (p.pp_mother_days === '182.5') next.pp_mother_days = '';
        if (p.pp_father_days === '182.5') next.pp_father_days = '';
      }
      // Save progress to localStorage so customer can return later
      try { localStorage.setItem(LS_KEY, JSON.stringify({data: next, category, savedAt: Date.now()})); } catch {}
      return next;
    });
    setErrors(p => { const e={...p}; delete e[id]; return e; });
  }

  function handleSaveAndRegenerate() {
    // Validate current section first
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    // Resolve vacation options then pass to onRegenerate (skips to confirm)
    const resolved = { ...formData };
    const vacSec = sections.find(s => s.id === 'parenting_plan_vacations');
    if (vacSec) {
      const ft = vacSec._fall_text || {};
      const spt = vacSec._spring_text || {};
      const ct = vacSec._christmas_text || {};
      const sut = vacSec._summer_text || {};
      if (resolved.pp_fall_option && resolved.pp_fall_option !== 'custom')
        resolved.pp_fall_vacation = ft[resolved.pp_fall_option] || '';
      if (resolved.pp_spring_option && resolved.pp_spring_option !== 'custom')
        resolved.pp_spring_vacation = spt[resolved.pp_spring_option] || '';
      if (resolved.pp_winter_option && resolved.pp_winter_option !== 'custom')
        resolved.pp_winter_vacation = ct[resolved.pp_winter_option] || '';
      if (resolved.pp_summer_option && resolved.pp_summer_option !== 'custom')
        resolved.pp_summer_vacation = sut[resolved.pp_summer_option] || '';
    }
    if (onRegenerate) onRegenerate(resolved);
  }

  function jumpTo(targetIdx) {
    setIdx(targetIdx);
    setErrors({});
    setShowJumpMenu(false);
    window.scrollTo(0, 0);
  }

  return (
    <div className="main-content"><div className="container">

      {/* Edit mode: section jump bar */}
      {isEditMode && (
        <div style={{
          background:'#f0f7f2', border:'1px solid var(--forest)',
          borderRadius:6, padding:'12px 16px', marginBottom:16,
          display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
        }}>
          <span style={{fontSize:'0.82rem',fontWeight:700,color:'var(--forest)',flexShrink:0}}>
            ✏ Editing — Jump to section:
          </span>
          <div style={{position:'relative', flex:1, minWidth:200}}>
            <button
              type="button"
              onClick={() => setShowJumpMenu(v => !v)}
              style={{
                width:'100%', textAlign:'left', padding:'6px 10px',
                border:'1.5px solid var(--forest)', borderRadius:4,
                background:'white', fontSize:'0.85rem', cursor:'pointer',
                color:'var(--forest)', fontWeight:600,
              }}>
              {idx + 1}. {section.title} ▾
            </button>
            {showJumpMenu && (
              <div style={{
                position:'absolute', top:'100%', left:0, right:0, zIndex:100,
                background:'white', border:'1.5px solid var(--forest)',
                borderRadius:4, boxShadow:'0 4px 16px rgba(0,0,0,0.12)',
                maxHeight:300, overflowY:'auto',
              }}>
                {sections.map((s, i) => {
                  const locked = !isEditMode && i > maxReached;
                  return (
                    <button key={i} type="button" onClick={() => !locked && jumpTo(i)}
                      style={{
                        display:'block', width:'100%', textAlign:'left',
                        padding:'8px 12px', border:'none',
                        background: i===idx ? '#edf7f0' : locked ? '#f8f8f8' : 'white',
                        fontSize:'0.84rem',
                        cursor: locked ? 'not-allowed' : 'pointer',
                        color: i===idx ? 'var(--forest)' : locked ? '#bbb' : 'var(--ink)',
                        fontWeight: i===idx ? 700 : 400,
                        borderBottom:'1px solid #eee',
                      }}>
                      {i + 1}. {s.title} {locked ? '🔒' : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleSaveAndRegenerate}
            style={{
              background:'var(--forest)', color:'white', border:'none',
              padding:'7px 18px', borderRadius:4, fontSize:'0.85rem',
              fontWeight:700, cursor:'pointer', flexShrink:0,
            }}>
            ⟳ Save & Regenerate
          </button>
        </div>
      )}

      <div style={{marginBottom:8,fontSize:'0.82rem',color:'var(--stone)'}}>Section {idx+1} of {sections.length}
        {!isEditMode && <span style={{marginLeft:12,color:'var(--forest)',fontWeight:500}}>
          💡 You can edit and regenerate your documents for 30 days after purchase — no need to get everything perfect now.
        </span>}
      </div>
      {/* Section progress dots */}
      {!isEditMode && (
        <div style={{display:'flex',gap:4,marginBottom:12,flexWrap:'wrap'}}>
          {sections.map((s,i) => {
            const done    = i < idx;
            const current = i === idx;
            const locked  = i > maxReached;
            return (
              <button key={i} type="button"
                onClick={() => !locked && (setIdx(i), window.scrollTo(0,0))}
                title={locked ? 'Complete previous sections first' : s.title}
                style={{
                  width:28, height:28, borderRadius:'50%', border:'2px solid',
                  borderColor: current ? 'var(--forest)' : done ? 'var(--forest)' : locked ? '#ccc' : '#aaa',
                  background: current ? 'var(--forest)' : done ? 'var(--forest)' : locked ? '#f5f5f5' : 'white',
                  color: done || current ? 'white' : locked ? '#bbb' : 'var(--ink)',
                  fontSize:'0.7rem', fontWeight:700, cursor: locked ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                {done ? '✓' : i+1}
              </button>
            );
          })}
        </div>
      )}
      <div className="card">
        <h2 style={{marginBottom:section.intro?8:24,color:'var(--forest)'}}>{section.title}</h2>
        {section.intro && <p className="form-section-intro">{section.intro}</p>}
        {section.fields.map(field => {
          if (field.conditional_on) {
            const {field:cf,value:cv} = field.conditional_on;
            if (!(Array.isArray(cv)?cv:[cv]).includes(formData[cf])) return null;
          }
          if (field.type === 'repeater') return <RepeaterField key={field.id} field={field} values={formData[field.id]} onChange={handleChange} />;
          return <Field key={field.id} field={field} value={formData[field.id]} onChange={handleChange} errors={errors} formData={formData} />;
        })}
        {Object.keys(errors).length > 0 && (
          <div className="alert alert-error"><span>⚠️</span><span>Please fill in all required fields before continuing.</span></div>
        )}
        <div className="nav-buttons">
          <button className="btn btn-outline" onClick={() => idx > 0 ? (setIdx(i=>i-1), window.scrollTo(0,0)) : onBack()} type="button">← Back</button>
          <div style={{display:'flex', gap:10}}>
            {isEditMode && (
              <button className="btn btn-outline" style={{borderColor:'var(--forest)',color:'var(--forest)',fontWeight:700}}
                onClick={handleSaveAndRegenerate} type="button">
                ⟳ Save & Regenerate
              </button>
            )}
            <button className="btn btn-primary btn-lg" onClick={handleNext} type="button">
              {idx < sections.length-1 ? 'Next →' : (isEditMode ? 'Review & Regenerate →' : 'Review My Information →')}
            </button>
          </div>
        </div>
      </div>
    </div></div>
  );
}

// ─── REVIEW & PAYMENT ─────────────────────────────────────────────────────────
function StepReview({ category, formData, onBack, authFetch, coupon, setCoupon }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [couponCode,   setCouponCode]   = useState(coupon?.code || '');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError,   setCouponError]   = useState(null);
  const [agreedTerms,   setAgreedTerms]   = useState(false);
  const Row = ({label, value}) => value ? <tr><th>{label}</th><td>{value}</td></tr> : null;
  const mid  = formData.plaintiff_middle_name ? ' '+formData.plaintiff_middle_name+' ' : ' ';
  const dmid = formData.defendant_middle_name ? ' '+formData.defendant_middle_name+' ' : ' ';

  const PRICES = { 1:29.99, 2:29.99, 3:29.99, 4:29.99 };
  const basePrice = PRICES[category] || 29.99;
  const finalPrice = coupon ? coupon.final_price : basePrice;

  async function handleApplyCoupon() {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError(null);
    try {
      const res  = await authFetch('/coupons/validate', { method:'POST', body: JSON.stringify({ code: couponCode, category }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCoupon(data);
    } catch (err) { setCouponError(err.message); setCoupon(null); }
    finally { setCouponLoading(false); }
  }

  async function handlePay() {
    setLoading(true); setError(null);
    try {
      const safeFormData = { ...formData };
      const csFields = ['cs_mother_gross','cs_father_gross','cs_mother_health_ins',
        'cs_father_health_ins','cs_mother_medical','cs_father_medical',
        'cs_mother_childcare','cs_father_childcare','cs_mother_fed_benefit',
        'cs_father_fed_benefit','cs_mother_se_tax','cs_father_se_tax'];
      csFields.forEach(id => {
        if (!safeFormData[id]) {
          const el = document.getElementById(id);
          if (el && el.value) safeFormData[id] = el.value;
        }
      });
      const body = { category, formData: safeFormData, accepted_terms: true };
      if (coupon) {
        body.coupon_id = coupon.coupon_id;
        body.final_price = coupon.final_price;
      }
      const res  = await authFetch('/create-checkout-session', { method:'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      try { localStorage.removeItem('mytndivorce_draft'); } catch {}
      window.location.href = data.url;
    } catch (err) { setError(err.message); setLoading(false); }
  }

  return (
    <div className="main-content"><div className="container">
      <h2 style={{marginBottom:8}}>Review Your Information</h2>
      <p className="text-muted" style={{marginBottom:28}}>Your documents will be generated exactly as entered. Review carefully.</p>
      <div className="card" style={{marginBottom:24}}>
        <h3 style={{marginBottom:16,color:'var(--forest)'}}>The Parties</h3>
        <table className="review-table"><tbody>
          <Row label="Plaintiff (You)" value={`${formData.plaintiff_first_name}${mid}${formData.plaintiff_last_name} (${formData.plaintiff_role})`} />
          <Row label="Email" value={formData.plaintiff_email} />
          <Row label="Phone" value={formData.plaintiff_phone} />
          <Row label="Address" value={formData.plaintiff_address} />
          <Row label="Restore Name?" value={formData.plaintiff_restore_name==='Yes' ? `Yes — ${formData.plaintiff_former_name}` : 'No'} />
          <Row label="Defendant (Spouse)" value={`${formData.defendant_first_name}${dmid}${formData.defendant_last_name} (${formData.defendant_role})`} />
        </tbody></table>
        <h3 style={{margin:'20px 0 16px',color:'var(--forest)'}}>Marriage</h3>
        <table className="review-table"><tbody>
          <Row label="Date of Marriage" value={formData.date_of_marriage} />
          <Row label="County of Marriage" value={formData.county_of_marriage} />
        </tbody></table>
        {category === 2 && (formData.real_property||[]).map((p,i) => (
          <div key={i}>
            <h3 style={{margin:'20px 0 16px',color:'var(--forest)'}}>Property {i+1}</h3>
            <table className="review-table"><tbody>
              <Row label="Address" value={`${p.address}, ${p.county}, ${p.state}`} />
              <Row label="Disposition" value={p.disposition} />
              <Row label="Buyout Amount" value={p.buyout_amount} />
              <Row label="Buyout Method" value={p.buyout_method} />
            </tbody></table>
          </div>
        ))}
        <h3 style={{margin:'20px 0 16px',color:'var(--forest)'}}>Debts & Vehicles</h3>
        <table className="review-table"><tbody>
          <Row label="Your Debts" value={(formData.plaintiff_debts||[]).map(d=>d.description).join('; ')||'None'} />
          <Row label="Spouse's Debts" value={(formData.defendant_debts||[]).map(d=>d.description).join('; ')||'None'} />
          <Row label="Your Vehicles" value={(formData.plaintiff_vehicles||[]).map(v=>v.description).join('; ')||'None'} />
          <Row label="Spouse's Vehicles" value={(formData.defendant_vehicles||[]).map(v=>v.description).join('; ')||'None'} />
        </tbody></table>
      </div>
      <div className="card" style={{marginBottom:24,borderColor:'var(--gold)',background:'#fffcf5'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:16}}>
          <div>
            <div style={{fontFamily:'var(--ff-display)',fontSize:'1.1rem',fontWeight:700,color:'var(--forest)',marginBottom:4}}>
              {category===1 ? 'No Children · No Real Property' : category===2 ? 'No Children · With Real Property' : category===3 ? 'With Children · No Real Property' : 'With Children · With Real Property'}
            </div>
            <div style={{fontSize:'0.85rem',color:'var(--muted)'}}>3{category >= 3 ? '–4' : ''} documents · instant download · edit free for 30 days</div>
          </div>
          <div style={{fontFamily:'var(--ff-display)',fontSize:'2rem',fontWeight:700,color:'var(--forest)'}}>
            {coupon
              ? <><span style={{textDecoration:'line-through',color:'var(--stone)',fontSize:'0.9rem'}}>${Number(basePrice).toFixed(2)}</span>{' '}<span style={{color:'var(--forest)',fontWeight:700}}>${Number(finalPrice).toFixed(2)}</span></>
              : `$${Number(basePrice).toFixed(2)}`}
          </div>
        </div>
      </div>

      {/* Coupon code field */}
      <div style={{background:'white',border:'1px solid var(--border)',borderRadius:8,padding:'16px 20px',marginBottom:16}}>
        <div style={{fontWeight:600,fontSize:'0.88rem',marginBottom:10,color:'var(--forest)'}}>Have a coupon code?</div>
        <div style={{display:'flex',gap:8}}>
          <input
            type="text"
            value={couponCode}
            onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCoupon(null); setCouponError(null); }}
            placeholder="Enter code"
            style={{flex:1,padding:'9px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',textTransform:'uppercase'}}
          />
          <button className="btn btn-outline" onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()} type="button" style={{flexShrink:0}}>
            {couponLoading ? 'Checking...' : 'Apply'}
          </button>
        </div>
        {coupon && (
          <div style={{marginTop:8,fontSize:'0.85rem',color:'var(--forest)',fontWeight:600}}>
            ✅ Code applied — you save ${(basePrice - finalPrice).toFixed(2)}!
          </div>
        )}
        {couponError && (
          <div style={{marginTop:8,fontSize:'0.85rem',color:'var(--error)'}}>
            ❌ {couponError}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error" style={{marginBottom:20}}>⚠️ {error}</div>}
      <div className="alert alert-info" style={{marginBottom:24}}>
        <span>🔒</span><span>Payment processed securely through Stripe. We never store your card information.</span>
      </div>
      <label style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:20,fontSize:'0.9rem',color:'var(--ink)',cursor:'pointer',lineHeight:1.5}}>
        <input
          type="checkbox"
          checked={agreedTerms}
          onChange={e => setAgreedTerms(e.target.checked)}
          style={{marginTop:3,flexShrink:0,width:16,height:16,cursor:'pointer'}}
        />
        <span>
          I have read and agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color:'var(--forest)',textDecoration:'underline'}}>Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:'var(--forest)',textDecoration:'underline'}}>Privacy Policy</a>.
        </span>
      </label>
      <div className="nav-buttons">
        <button className="btn btn-outline" onClick={onBack} disabled={loading} type="button">← Edit My Information</button>
        <button className="btn btn-gold btn-lg" onClick={handlePay} disabled={loading || !agreedTerms} type="button">
          {loading ? 'Redirecting to payment…' : `Pay $${Number(finalPrice).toFixed(2)} & Download →`}
        </button>
      </div>
      {!agreedTerms && (
        <p style={{fontSize:'0.8rem',color:'var(--muted)',textAlign:'right',marginTop:8}}>
          Please agree to the Terms of Service to continue.
        </p>
      )}
    </div></div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function PageDashboard({ authFetch, onStartEdit, onStartUpgrade, navigate }) {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus]   = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { orderId, customerName }
  const [emailTo, setEmailTo]       = useState('');
  const [emailMsg, setEmailMsg]     = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult]   = useState(null);

  const sessionId          = new URLSearchParams(window.location.search).get('session_id');
  const upgradeSessionId   = new URLSearchParams(window.location.search).get('upgrade_session_id');
  const extensionSessionId = new URLSearchParams(window.location.search).get('extension_session_id');

  useEffect(() => {
    loadOrders();
    if (sessionId)          pollSession(sessionId, false);
    if (upgradeSessionId)   pollSession(upgradeSessionId, true);
    if (extensionSessionId) pollExtension(extensionSessionId);
    // Auto-fix admin orders silently on dashboard load
    authFetch('/admin/fix-admin-orders', { method:'POST' }).catch(() => {});
    authFetch('/admin/activate-unpaid', { method:'POST' }).then(() => loadOrders()).catch(() => {});
    // Fire Google Ads conversion on successful purchase
    if (sessionId && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18127808505/M5_lCPOC0KQcEPnPgcRD',
        value: 29.99,
        currency: 'USD',
        transaction_id: sessionId,
      });
    }
  }, []); // eslint-disable-line

  async function loadOrders() {
    setLoading(true);
    try {
      const res  = await authFetch('/dashboard/orders');
      const data = await res.json();
      setOrders(data.orders || []);
    } catch {}
    finally { setLoading(false); }
  }

  async function pollSession(sid, isUpgrade) {
    // Admin bypass or free coupon — documents already ready, just load orders
    if (sid && (sid.startsWith('admin-bypass-') || sid.startsWith('coupon-free-'))) {
      window.history.replaceState({}, '', '/dashboard');
      setStatus({ state:'ready' });
      await loadOrders();
      return;
    }
    setStatus({ state:'processing' });
    let attempts = 0;
    const endpoint = isUpgrade ? `/upgrade-status/${sid}` : `/session-status/${sid}`;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res  = await authFetch(endpoint);
        const data = await res.json();
        if (data.status === 'ready') {
          setStatus({ state:'ready', orderId: data.order_id, upgraded: isUpgrade });
          clearInterval(poll);
          loadOrders();
          window.history.replaceState({}, '', '/dashboard');
        } else if (data.status === 'error') {
          setStatus({ state:'error' });
          clearInterval(poll);
          window.history.replaceState({}, '', '/dashboard');
        } else if (attempts > 30) {
          // After 60 seconds of polling, stop and show error
          setStatus({ state:'error' });
          clearInterval(poll);
          window.history.replaceState({}, '', '/dashboard');
        }
        // 'pending' or 'processing' — keep polling
      } catch {
        if (attempts > 30) {
          setStatus({ state:'error' });
          clearInterval(poll);
        }
      }
    }, 2000);
  }

  async function pollExtension(sid) {
    setStatus({ state:'processing' });
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res  = await authFetch(`/extension-status/${sid}`);
        const data = await res.json();
        if (data.status === 'extended') {
          setStatus({ state:'extended' });
          clearInterval(poll);
          loadOrders();
          window.history.replaceState({}, '', '/dashboard');
        } else if (attempts > 30) {
          setStatus({ state:'error' });
          clearInterval(poll);
          window.history.replaceState({}, '', '/dashboard');
        }
      } catch {
        if (attempts > 30) { setStatus({ state:'error' }); clearInterval(poll); }
      }
    }, 2000);
  }

  async function handleExtension(order) {
    try {
      const res  = await authFetch('/create-extension-session', {
        method: 'POST',
        body: JSON.stringify({ orderId: order.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.url;
    } catch (err) {
      alert('Could not start extension checkout: ' + err.message);
    }
  }

  async function handleDeleteOrder(order) {
    if (!window.confirm('Delete this order and all its documents? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/dashboard/orders/${order.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      loadOrders();
    } catch (err) { alert(err.message); }
  }

  async function handleEmailDocs() {
    if (!emailTo) return;
    setEmailSending(true); setEmailResult(null);
    try {
      const res  = await authFetch(`/dashboard/orders/${emailModal.orderId}/email-docs`, {
        method: 'POST',
        body: JSON.stringify({ recipientEmail: emailTo, message: emailMsg }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEmailResult({ success: true });
      setEmailTo(''); setEmailMsg('');
    } catch (err) {
      setEmailResult({ error: err.message });
    } finally {
      setEmailSending(false);
    }
  }

  async function handleDownload(orderId) {
    const url   = `${API}/dashboard/orders/${orderId}/download`;
    const token = localStorage.getItem('token');
    const res   = await fetch(url, { headers:{ Authorization:`Bearer ${token}` } });
    if (!res.ok) { alert('Download failed. Please try again.'); return; }
    const blob = await res.blob();
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = 'myTNdivorce_documents.zip';
    a.click();
  }

  const catLabels = { 1:'No Children · No Real Property', 2:'No Children · With Real Property', 3:'With Children · No Real Property', 4:'With Children · With Real Property' };

  return (
    <div className="main-content"><div className="container">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <h2 style={{margin:0}}>My Account Dashboard</h2>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-outline" style={{fontSize:'0.8rem',padding:'6px 14px'}}
            onClick={() => navigate('/admin/coupons')} type="button">
            🏷 Coupons
          </button>
          <button className="btn btn-outline" style={{fontSize:'0.8rem',padding:'6px 14px'}}
            onClick={() => navigate('/account-settings')} type="button">
            ⚙ Account Settings
          </button>
        </div>
      </div>
      <p className="text-muted" style={{marginBottom:32}}>Download your documents, edit your answers, or regenerate updated documents here.</p>

      {status && (
        <div className={`alert ${status.state==='ready'||status.state==='extended' ? 'alert-success' : status.state==='error' ? 'alert-error' : 'alert-info'}`} style={{marginBottom:28}}>
          {status.state==='processing' && <><div className="spinner" style={{width:20,height:20,marginRight:12,marginBottom:0,flexShrink:0}} />Preparing your documents — this takes just a moment…</>}
          {status.state==='ready'      && <><span>✅</span><span>{status.upgraded ? 'Your package has been upgraded and new documents are ready! ' : 'Your documents are ready! '}Click <strong>Download</strong> below.</span></>}
          {status.state==='extended'   && <><span>✅</span><span>Your document storage has been extended by 30 days.</span></>}
          {status.state==='error'      && <><span>⚠️</span><span>There was a problem preparing your documents. Please refresh the page or contact us.</span></>}
        </div>
      )}

      {loading ? (
        <div style={{textAlign:'center',padding:'40px 0'}}><div className="spinner" /></div>
      ) : orders.length === 0 ? (
        <div className="card" style={{textAlign:'center',padding:'48px 32px'}}>
          <div style={{fontSize:'2.5rem',marginBottom:16}}>📄</div>
          <h3 style={{marginBottom:8}}>No orders yet</h3>
          <p style={{color:'var(--muted)'}}>Complete the questionnaire and purchase your package to get started.</p>
        </div>
      ) : orders.map(order => (
        <div key={order.id} className="card" style={{marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12,marginBottom:16}}>
            <div>
              <div style={{fontFamily:'var(--ff-display)',fontWeight:700,fontSize:'1.05rem',color:'var(--forest)',marginBottom:4}}>
                {catLabels[order.category] || 'Divorce Package'}
              </div>
              <div style={{fontSize:'0.82rem',color:'var(--stone)'}}>
                Purchased {order.paid_at ? new Date(order.paid_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : '—'}
                {order.days_remaining !== null && !order.is_expired && (
                  <span style={{marginLeft:12,color: order.days_remaining <= 5 ? 'var(--error)' : 'var(--stone)'}}>
                    · {order.days_remaining} {order.days_remaining===1?'day':'days'} remaining
                  </span>
                )}
              </div>
            </div>
            {/* 5-day expiry warning */}
            {order.days_remaining !== null && order.days_remaining <= 5 && !order.is_expired && (
              <div style={{background:'#fff3cd',border:'1px solid #ffc107',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:'0.85rem',color:'#856404',marginBottom:8}}>
                ⚠️ <strong>Your documents expire in {order.days_remaining} {order.days_remaining===1?'day':'days'}.</strong> Extend document storage for another 30 days for $4.99.
                <button className="btn btn-outline" style={{marginLeft:12,padding:'4px 12px',fontSize:'0.8rem',borderColor:'#856404',color:'#856404'}}
                  onClick={() => handleExtension(order)} type="button">
                  Extend Storage — $4.99
                </button>
              </div>
            )}
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {order.is_expired ? (
                <span style={{fontSize:'0.85rem',color:'var(--error)',fontWeight:600,padding:'8px 0'}}>Expired</span>
              ) : order.is_paid ? (
                <>
                  <button className="btn btn-primary" onClick={() => handleDownload(order.id)} type="button">
                    ⬇ Download (.zip)
                  </button>
                  <button className="btn btn-outline" onClick={() => {
                    const fd = order.form_data || {};
                    const name = `${fd.plaintiff_first_name || ''} ${fd.plaintiff_last_name || ''}`.trim() || 'Customer';
                    setEmailModal({ orderId: order.id, customerName: name });
                    setEmailResult(null); setEmailTo(''); setEmailMsg('');
                  }} type="button">
                    📧 Email Documents
                  </button>
                  <button className="btn btn-outline" onClick={() => onStartEdit(order)} type="button">
                    ✏ Edit & Regenerate
                  </button>
                  {/* Filing instructions link — county-specific */}
                  {order.form_data?.filing_county && (() => {
                    const county = order.form_data.filing_county;
                    const slug = county.toLowerCase().replace(/ /g, '-') + '-county';
                    return (
                      <a href={`/divorce/${slug}`}
                        className="btn btn-outline"
                        style={{borderColor:'#2d6a4f',color:'#2d6a4f',fontWeight:600,display:'inline-flex',alignItems:'center',gap:6,textDecoration:'none'}}
                        target="_blank" rel="noopener noreferrer">
                        📋 How to File in {county} County →
                      </a>
                    );
                  })()}
                  {(order.category === 1 || order.category === 3) && (
                    <button className="btn btn-outline" style={{borderColor:'var(--gold)',color:'var(--gold)'}} onClick={() => onStartUpgrade(order)} type="button">
                      ⬆ Upgrade — Add Real Property (Free)
                    </button>
                  )}
                  {order.days_remaining !== null && order.days_remaining > 5 && (
                    <button className="btn btn-outline" style={{borderColor:'var(--stone)',color:'var(--stone)',fontSize:'0.8rem'}}
                      onClick={() => handleExtension(order)} type="button">
                      🗂 Extend Storage (+30 days, $4.99)
                    </button>
                  )}
                  <button className="btn btn-outline"
                    style={{borderColor:'var(--error)',color:'var(--error)',fontSize:'0.8rem',padding:'6px 12px'}}
                    onClick={() => handleDeleteOrder(order)} type="button">
                    🗑 Delete
                  </button>
                </>
              ) : (
                <span style={{fontSize:'0.85rem',color:'var(--stone)',padding:'8px 0'}}>Payment pending</span>
              )}
            </div>
          </div>
          {!order.is_expired && order.is_paid && (
            <div style={{fontSize:'0.8rem',color:'var(--stone)',background:'var(--cream)',borderRadius:'var(--radius)',padding:'10px 14px'}}>
              <strong>Documents included:</strong> Complaint for Divorce · Marital Dissolution Agreement · Final Decree of Divorce
              {order.category >= 3 && ' · Permanent Parenting Plan Order'}
            </div>
          )}
        </div>
      ))}

      {/* Email Documents Modal */}
      {emailModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
          <div style={{background:'white',borderRadius:12,padding:32,maxWidth:480,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
            <h2 style={{color:'var(--forest)',fontFamily:'var(--ff-display)',fontSize:'1.3rem',marginBottom:8}}>Email Documents</h2>
            <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:24}}>
              Send your divorce documents to another person for review. The email will include all generated documents as attachments.
            </p>
            {emailResult?.success ? (
              <div>
                <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:8,padding:'16px 20px',marginBottom:20,textAlign:'center'}}>
                  <div style={{fontSize:'2rem',marginBottom:8}}>✅</div>
                  <div style={{fontWeight:600,color:'var(--forest)'}}>Documents sent successfully!</div>
                  <div style={{fontSize:'0.85rem',color:'var(--muted)',marginTop:4}}>Check your recipient's inbox.</div>
                </div>
                <button className="btn btn-outline" style={{width:'100%'}} onClick={() => { setEmailModal(null); setEmailResult(null); }} type="button">Close</button>
              </div>
            ) : (
              <>
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontWeight:600,fontSize:'0.88rem',marginBottom:6,color:'var(--ink)'}}>Recipient Email Address *</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={e => setEmailTo(e.target.value)}
                    placeholder="spouse@email.com"
                    style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}}
                  />
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontWeight:600,fontSize:'0.88rem',marginBottom:6,color:'var(--ink)'}}>Personal Message (optional)</label>
                  <textarea
                    value={emailMsg}
                    onChange={e => setEmailMsg(e.target.value)}
                    placeholder="Please review the attached divorce documents..."
                    rows={3}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box',resize:'vertical'}}
                  />
                </div>
                <div style={{background:'#f8f6f0',borderRadius:6,padding:'12px 14px',fontSize:'0.78rem',color:'var(--muted)',marginBottom:20,lineHeight:1.6}}>
                  The recipient will receive a message stating that <strong>{emailModal.customerName}</strong> has prepared the attached documents using myTNdivorce.com, a document preparation service, that they do not constitute legal advice, and that no attorney-client relationship has been created.
                </div>
                {emailResult?.error && (
                  <div style={{background:'#fff0f0',border:'1px solid #f0b0b0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'#b03030',marginBottom:16}}>
                    {emailResult.error}
                  </div>
                )}
                <div style={{display:'flex',gap:10}}>
                  <button className="btn btn-outline" style={{flex:1}} onClick={() => { setEmailModal(null); setEmailResult(null); }} type="button">Cancel</button>
                  <button className="btn btn-primary" style={{flex:2}} onClick={handleEmailDocs} disabled={emailSending || !emailTo} type="button">
                    {emailSending ? 'Sending...' : '📧 Send Documents'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div></div>
  );
}

// ─── UPGRADE FLOW ─────────────────────────────────────────────────────────────
// Cat1 → Cat2 / Cat3 → Cat4: customer fills real property questionnaire; upgrade is free (no payment)
function PageUpgrade({ order, authFetch, onDone, onBack }) {
  const [step, setStep]       = useState('intro');    // intro | questionnaire | confirm | paying
  const [formData, setFormData] = useState(order.form_data || {});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const realPropertyFields = [
    {id:'address',label:'Street Address',type:'text',placeholder:'456 Oak Drive',required:true},
    {id:'county',label:'County',type:'text',placeholder:'Hamilton County',required:true},
    {id:'state',label:'State and ZIP',type:'text',placeholder:'Tennessee 37411',required:true},
    {id:'title_holding',label:'How is title currently held?',type:'select',required:true,options:[{value:'joint',label:'Jointly — both spouses on the deed'},{value:'plaintiff_only',label:'In my name only'},{value:'defendant_only',label:"In my spouse's name only"}]},
    {id:'disposition',label:'What will happen to this property?',type:'select',required:true,options:[{value:'plaintiff_keeps',label:'I will keep the property'},{value:'defendant_keeps',label:'My spouse will keep the property'},{value:'sell',label:'We will sell it and divide the proceeds'},{value:'defer_sale',label:'We will defer the sale until a future event'}]},
    {id:'deed_transfer',label:'Type of deed for the transfer?',type:'select',required:true,conditional_on:{field:'disposition',value:['plaintiff_keeps','defendant_keeps']},options:[{value:'quitclaim',label:'Quitclaim Deed (most common in divorce)'},{value:'warranty',label:'Special Warranty Deed'},{value:'none_needed',label:"No deed needed — title already in keeping party's name"}]},
    {id:'equity_split',label:'How will equity be handled?',type:'select',required:true,conditional_on:{field:'disposition',value:['plaintiff_keeps','defendant_keeps']},options:[{value:'keeper_buys_out',label:'Keeping party pays the other for their equity share'},{value:'no_buyout',label:'No equity payment — keeping party takes all equity'}]},
    {id:'buyout_amount',label:'Agreed equity buyout amount',type:'text',placeholder:'$30,000.00',required:true,conditional_on:{field:'equity_split',value:'keeper_buys_out'},help:'e.g. $30,000.00'},
    {id:'buyout_method',label:'How will the buyout be paid?',type:'select',required:true,conditional_on:{field:'equity_split',value:'keeper_buys_out'},options:[{value:'refinance',label:'Refinance — pay buyout from new loan proceeds'},{value:'loan_assumption',label:'Loan Assumption — assume existing mortgage'},{value:'cash_at_closing',label:'Cash — lump sum payment'},{value:'payment_plan',label:'Installment Payments — paid over time'},{value:'deed_in_lieu',label:'Deed in Lieu — conveyance is full settlement'}]},
    {id:'buyout_deadline',label:'Deadline to complete the buyout',type:'text',placeholder:'within ninety (90) days of entry of the Final Decree',required:true,conditional_on:{field:'buyout_method',value:['refinance','loan_assumption','cash_at_closing']}},
    {id:'buyout_plan_terms',label:'Describe the installment payment schedule',type:'textarea',required:true,conditional_on:{field:'buyout_method',value:'payment_plan'}},
    {id:'if_refinance_fails',label:'If refinance or assumption cannot be completed by deadline, what happens?',type:'select',required:true,conditional_on:{field:'buyout_method',value:['refinance','loan_assumption']},options:[{value:'sell',label:'Property will be sold and proceeds divided equally'},{value:'extend',label:'Deadline extended up to 90 more days'},{value:'other',label:'Other (describe below)'}]},
    {id:'if_refinance_fails_other',label:'Describe the fallback provision',type:'textarea',required:true,conditional_on:{field:'if_refinance_fails',value:'other'}},
    {id:'mortgage_responsible_party',label:'Who is responsible for mortgage payments until transfer or sale is complete?',type:'select',required:true,options:[{value:'plaintiff',label:'I am (the Plaintiff)'},{value:'defendant',label:'My spouse'},{value:'joint',label:'We share equally'}]},
    {id:'sale_list_price_method',label:'How will the listing price be determined?',type:'select',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']},options:[{value:'agreed_value',label:'We have agreed on a specific price'},{value:'agent_recommendation',label:'A licensed agent will recommend the price'}]},
    {id:'sale_agreed_value',label:'Agreed listing price',type:'text',placeholder:'$275,000.00',required:true,conditional_on:{field:'sale_list_price_method',value:'agreed_value'}},
    {id:'sale_proceeds_split',label:'How will net sale proceeds be divided?',type:'text',placeholder:'50% to Wife and 50% to Husband',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']}},
    {id:'sale_expenses_split',label:'How will costs of sale be shared?',type:'select',required:true,conditional_on:{field:'disposition',value:['sell','defer_sale']},options:[{value:'50/50',label:'Equally — from sale proceeds'},{value:'plaintiff_pays',label:'I pay all costs'},{value:'defendant_pays',label:'My spouse pays all costs'}]},
    {id:'deferred_sale_trigger',label:'What event will trigger the sale?',type:'text',required:true,conditional_on:{field:'disposition',value:'defer_sale'}},
  ];

  // Local repeater state for real property items
  const [properties, setProperties] = useState(formData.real_property || [{}]);
  const [propErrors, setPropErrors]  = useState({});

  function validateProperties() {
    // Require at least one property with an address
    if (!properties.length || !properties[0].address) {
      return { _general: 'Please add at least one property.' };
    }
    return {};
  }

  async function handlePay() {
    setLoading(true); setError(null);
    const updatedFormData = { ...formData, real_property: properties };
    try {
      const res  = await authFetch('/create-upgrade-session', {
        method: 'POST',
        body: JSON.stringify({ orderId: order.id, formData: updatedFormData }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  if (step === 'intro') return (
    <div className="main-content"><div className="container"><div className="screener-wrap">
      <div className="card">
        <h2 style={{marginBottom:8,color:'var(--forest)'}}>Upgrade to Include Real Property</h2>
        <p style={{color:'var(--muted)',marginBottom:24}}>
          You purchased the <strong>No Real Property</strong> package. For a limited time, you can upgrade to include real property provisions in your Marital Dissolution Agreement at <strong>no additional charge</strong>.
        </p>
        <div className="alert alert-info" style={{marginBottom:24}}>
          <span>ℹ️</span>
          <div>
            <strong>What happens when you upgrade:</strong>
            <ul style={{marginTop:8,paddingLeft:20,fontSize:'0.85rem'}}>
              <li>You'll answer questions about your real property</li>
              <li>No payment required — the upgrade is free</li>
              <li>Your documents are automatically regenerated with real property provisions</li>
              <li>Your 30-day access window is unchanged</li>
            </ul>
          </div>
        </div>
        <div className="alert alert-warning" style={{marginBottom:24}}>
          <span>⚠️</span>
          <span>This upgrade is <strong>one-way only</strong>. Once upgraded to include real property, you cannot downgrade back to the No Real Property package.</span>
        </div>
        <div className="nav-buttons">
          <button className="btn btn-outline" onClick={onBack} type="button">← Cancel</button>
          <button className="btn btn-gold btn-lg" onClick={() => setStep('questionnaire')} type="button">
            Continue — Add Property Details →
          </button>
        </div>
      </div>
    </div></div></div>
  );

  if (step === 'questionnaire') return (
    <div className="main-content"><div className="container">
      <div className="card">
        <h2 style={{marginBottom:8,color:'var(--forest)'}}>Your Real Property</h2>
        <p className="form-section-intro">Answer these questions for each piece of real property. You can add multiple properties.</p>

        {properties.map((prop, idx) => (
          <div key={idx} className="repeater-item">
            <div className="repeater-item-header">
              <span className="repeater-item-title">Property {idx+1}</span>
              {properties.length > 1 && (
                <button className="btn-remove" onClick={() => setProperties(p => p.filter((_,i) => i !== idx))} type="button">Remove</button>
              )}
            </div>
            {realPropertyFields.map(field => {
              if (field.conditional_on) {
                const {field:cf, value:cv} = field.conditional_on;
                const condVals = Array.isArray(cv) ? cv : [cv];
                if (!condVals.includes(prop[cf])) return null;
              }
              return (
                <Field
                  key={field.id}
                  field={field}
                  value={prop[field.id]}
                  onChange={(id, val) => setProperties(ps => ps.map((p,i) => i===idx ? {...p,[id]:val} : p))}
                  errors={propErrors[idx] || {}}
                />
              );
            })}
          </div>
        ))}

        <button className="btn-add" onClick={() => setProperties(p => [...p, {}])} type="button">+ Add Another Property</button>

        {propErrors._general && <div className="alert alert-error" style={{marginTop:16}}><span>⚠️</span><span>{propErrors._general}</span></div>}

        <div className="nav-buttons">
          <button className="btn btn-outline" onClick={() => setStep('intro')} type="button">← Back</button>
          <button className="btn btn-primary btn-lg" onClick={() => {
            const errs = validateProperties();
            if (Object.keys(errs).length) { setPropErrors(errs); return; }
            setPropErrors({});
            setFormData(prev => ({...prev, real_property: properties}));
            setStep('confirm');
          }} type="button">Review & Pay →</button>
        </div>
      </div>
    </div></div>
  );

  if (step === 'confirm') return (
    <div className="main-content"><div className="container"><div className="screener-wrap">
      <div className="card">
        <h2 style={{marginBottom:8,color:'var(--forest)'}}>Confirm Upgrade</h2>
        <p style={{color:'var(--muted)',marginBottom:24}}>
          You are upgrading your package to include real property provisions. Review your property details, then confirm to regenerate your documents — no payment required.
        </p>

        {properties.map((p, i) => (
          <div key={i} style={{background:'var(--cream)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'16px',marginBottom:12}}>
            <div style={{fontWeight:600,marginBottom:8,color:'var(--forest)'}}>Property {i+1}</div>
            <div style={{fontSize:'0.88rem',color:'var(--ink)'}}>
              <div>{p.address}, {p.county}, {p.state}</div>
              <div style={{color:'var(--muted)',marginTop:4}}>
                Title: {p.title_holding} · Disposition: {p.disposition}
                {p.buyout_amount && ` · Buyout: ${p.buyout_amount}`}
              </div>
            </div>
          </div>
        ))}

        <div className="card" style={{borderColor:'var(--gold)',background:'#fffcf5',marginTop:16,marginBottom:24}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontWeight:700,color:'var(--forest)'}}>Upgrade: Add Real Property</div>
              <div style={{fontSize:'0.85rem',color:'var(--muted)'}}>Limited time — no additional charge</div>
            </div>
            <div style={{fontFamily:'var(--ff-display)',fontSize:'1.8rem',fontWeight:700,color:'var(--forest)'}}>FREE</div>
          </div>
        </div>

        {error && <div className="alert alert-error" style={{marginBottom:16}}><span>⚠️</span><span>{error}</span></div>}

        <div className="alert alert-info" style={{marginBottom:24}}>
          <span>ℹ️</span><span>Your upgraded documents will be ready on your dashboard in a moment.</span>
        </div>

        <div className="nav-buttons">
          <button className="btn btn-outline" onClick={() => setStep('questionnaire')} disabled={loading} type="button">← Edit Property Details</button>
          <button className="btn btn-gold btn-lg" onClick={handlePay} disabled={loading} type="button">
            {loading ? 'Upgrading…' : 'Confirm Free Upgrade →'}
          </button>
        </div>
      </div>
    </div></div></div>
  );

  return null;
}

// ─── EDIT & REGENERATE ────────────────────────────────────────────────────────
function PageEdit({ order, authFetch, onDone, onBack }) {
  const [formData, setFormData] = useState(order.form_data || {});
  const [step, setStep]         = useState('questionnaire'); // 'questionnaire' | 'confirm'
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(false);

  async function handleRegenerate() {
    setLoading(true); setError(null);
    try {
      const res  = await authFetch(`/dashboard/orders/${order.id}/regenerate`, {
        method:'POST', body: JSON.stringify({ formData }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  // Called from any page's "Save & Regenerate" button — saves current data and jumps to confirm
  function handleMidRegenerate(data) {
    setFormData(data);
    setStep('confirm');
    window.scrollTo(0, 0);
  }

  if (success) return (
    <div className="main-content"><div className="container">
      <div className="success-wrap">
        <div className="success-icon">✅</div>
        <h2>Documents Regenerated</h2>
        <p>Your updated documents are ready. Return to your dashboard to download them.</p>
        <button className="btn btn-primary btn-lg" onClick={onDone} type="button">Go to Dashboard</button>
      </div>
    </div></div>
  );

  if (step === 'confirm') return (
    <div className="main-content"><div className="container">
      <div className="card" style={{maxWidth:560,margin:'0 auto'}}>
        <h2 style={{marginBottom:8,color:'var(--forest)'}}>Regenerate Documents?</h2>
        <p style={{color:'var(--muted)',marginBottom:8}}>This will replace your previously generated documents with a new set based on your updated answers. This is free of charge.</p>
        <div className="alert alert-info" style={{marginBottom:20}}>
          <span>ℹ️</span>
          <span style={{fontSize:'0.85rem'}}>Your changes have been saved. Click <strong>Regenerate</strong> to produce new documents, or go back to keep editing.</span>
        </div>
        {error && <div className="alert alert-error" style={{marginBottom:16}}><span>⚠️</span><span>{error}</span></div>}
        <div className="nav-buttons">
          <button className="btn btn-outline" onClick={() => setStep('questionnaire')} disabled={loading} type="button">← Keep Editing</button>
          <button className="btn btn-primary btn-lg" onClick={handleRegenerate} disabled={loading} type="button">
            {loading ? 'Regenerating…' : '⟳ Regenerate My Documents'}
          </button>
        </div>
      </div>
    </div></div>
  );

  return (
    <StepQuestionnaire
      category={order.category}
      initialData={formData}
      isEditMode={true}
      onRegenerate={handleMidRegenerate}
      onNext={(data) => { setFormData(data); setStep('confirm'); }}
      onBack={onBack}
    />
  );
}

// ─── LOGIN PAGE (standalone) ──────────────────────────────────────────────────
function PageLogin({ auth, onDone }) {
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  async function handleSubmit() {
    setError(null);
    if (mode==='register' && password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/auth/${mode}`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({email,password}) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      auth.login(data.token, data.user);
      onDone();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="main-content"><div className="container"><div className="screener-wrap">
      <div className="card">
        <h2 style={{marginBottom:8,color:'var(--forest)'}}>{mode==='login' ? 'Log In to Your Account' : 'Create an Account'}</h2>
        <p className="text-muted" style={{marginBottom:24}}>Access your documents, edit answers, and re-download at any time.</p>
        <div className="form-field">
          <label>Email Address<span className="required-star">*</span></label>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="form-field">
          <label>Password<span className="required-star">*</span></label>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="At least 8 characters" />
        </div>
        {mode==='register' && (
          <div className="form-field">
            <label>Confirm Password<span className="required-star">*</span></label>
            <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} />
          </div>
        )}
        {error && <div className="alert alert-error" style={{marginBottom:16}}><span>⚠️</span><span>{error}</span></div>}
        <button className="btn btn-primary btn-lg" style={{width:'100%',marginBottom:16}} onClick={handleSubmit} disabled={loading} type="button">
          {loading ? 'Please wait…' : mode==='login' ? 'Log In' : 'Create Account'}
        </button>
        <p style={{textAlign:'center',fontSize:'0.88rem',color:'var(--muted)'}}>
          {mode==='login' ? "Don't have an account? " : 'Already have an account? '}
          <button className="btn" style={{background:'none',color:'var(--forest)',padding:0,fontWeight:600,fontSize:'0.88rem',textDecoration:'underline'}} onClick={() => {setMode(mode==='login'?'register':'login');setError(null);}} type="button">
            {mode==='login' ? 'Create one' : 'Log in instead'}
          </button>
        </p>
        {mode==='login' && (
          <p style={{textAlign:'center',fontSize:'0.85rem',marginTop:8}}>
            <button className="btn" style={{background:'none',color:'var(--stone)',padding:0,fontSize:'0.85rem',textDecoration:'underline'}}
              onClick={() => window.location.href = '/forgot-password'} type="button">
              Forgot your password?
            </button>
          </p>
        )}
      </div>
    </div></div></div>
  );
}

function PageWaitingPeriods({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Divorce Waiting Periods',
    'Tennessee requires a 60-day waiting period for divorces without children and 90 days with minor children. Learn what happens during the waiting period and how to schedule your final hearing.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Divorce Waiting Periods</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · T.C.A. §36-4-101</p>

      <p style={p}>Tennessee law requires a mandatory waiting period between the date the Complaint for Divorce is filed and the date the judge can sign the Final Decree of Divorce. This waiting period cannot be waived by either party or by the court. Here is everything you need to know.</p>

      <h2 style={h2}>The Two Waiting Periods</h2>
      <ul style={ul}>
        <li style={li}><strong>60 days</strong> — if the parties have no minor children</li>
        <li style={li}><strong>90 days</strong> — if the parties have one or more minor children under age 18</li>
      </ul>
      <p style={p}>The clock starts on the date the Complaint for Divorce is filed with the court clerk — not the date it is served on the other spouse, not the date the MDA is signed, and not the date of any hearing. It is the filing date only.</p>

      <h2 style={h2}>Why Does Tennessee Have a Waiting Period?</h2>
      <p style={p}>The waiting period exists to give couples time to reconsider their decision and to ensure that agreements involving children are carefully considered. The legislature determined that marriages should not be dissolved hastily and that a cooling-off period serves the public interest — particularly when children are involved.</p>

      <h2 style={h2}>What Happens During the Waiting Period?</h2>
      <p style={p}>During the waiting period you should:</p>
      <ul style={ul}>
        <li style={li}>Finalize and sign your Marital Dissolution Agreement if you have not already</li>
        <li style={li}>Complete the parent education seminar if you have minor children (required within 60 days of filing)</li>
        <li style={li}>Review your Permanent Parenting Plan and child support figures</li>
        <li style={li}>Contact the court clerk to schedule your final hearing after the waiting period expires</li>
        <li style={li}>Make any final edits to your documents — myTNdivorce.com allows unlimited edits for 30 days</li>
      </ul>

      <h2 style={h2}>Can the Waiting Period Be Shortened?</h2>
      <p style={p}>No. Tennessee's waiting periods are statutory and cannot be reduced or waived by agreement of the parties, by court order, or for any other reason. Even if both spouses agree and all paperwork is complete on day one, the judge cannot sign the Final Decree until the waiting period has fully elapsed.</p>

      <h2 style={h2}>Scheduling the Final Hearing</h2>
      <p style={p}>Once the waiting period expires, you need to schedule a final hearing with the court. Contact your county's Circuit or Chancery Court clerk to get on the docket. In most uncontested cases the hearing is brief — often just a few minutes. The Plaintiff (filing spouse) typically appears before the judge and answers a few basic questions confirming the agreement. Some courts allow the hearing to be conducted without either party appearing if all paperwork is in perfect order, but this varies by county and judge.</p>

      <h2 style={h2}>How Long Does the Entire Tennessee Divorce Process Take?</h2>
      <p style={p}>For a well-prepared uncontested divorce, the typical timeline is:</p>
      <ul style={ul}>
        <li style={li}><strong>Day 1:</strong> Prepare and file documents</li>
        <li style={li}><strong>Days 1–60 or 1–90:</strong> Mandatory waiting period</li>
        <li style={li}><strong>Shortly after waiting period:</strong> Final hearing scheduled and held</li>
        <li style={li}><strong>Total: 2–4 months</strong> for most uncontested divorces</li>
      </ul>
      <p style={p}>Delays typically occur when paperwork is incomplete, the hearing docket is backlogged, or one spouse is uncooperative. Having complete, court-ready documents from the start is the best way to avoid delays.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-waiting-periods" />
      <ContentCTA navigate={navigate} title="Prepare your Tennessee divorce documents and start the clock." />
    </div>
  );
}

function PageParentingPlan({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Parenting Plan Requirements',
    'Tennessee law requires a Permanent Parenting Plan in every divorce with minor children. Learn what must be included, how the residential schedule works, and decision-making rules.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Parenting Plan Requirements</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · T.C.A. §36-6-404</p>

      <p style={p}>If your Tennessee divorce involves minor children, a <strong>Permanent Parenting Plan Order</strong> is required by law. The parenting plan governs how you and your spouse will co-parent after the divorce — covering everything from the children's daily schedule to who makes major decisions. Here is what you need to know.</p>

      <h2 style={h2}>What Is a Permanent Parenting Plan?</h2>
      <p style={p}>A Permanent Parenting Plan (PPP) is a court-ordered document that establishes the rights and responsibilities of each parent after divorce. Despite the word "permanent," it can be modified later if there is a significant change in circumstances. Tennessee courts will not finalize a divorce involving minor children without an approved Permanent Parenting Plan.</p>

      <h2 style={h2}>What Must a Tennessee Parenting Plan Include?</h2>
      <p style={p}>Under T.C.A. §36-6-404, a Tennessee Permanent Parenting Plan must address all of the following:</p>

      <h3 style={h3}>1. Primary Residential Parent (PRP)</h3>
      <p style={p}>The plan must designate one parent as the Primary Residential Parent — the parent with whom the child primarily resides. The other parent is the Alternate Residential Parent (ARP). In cases of equal parenting time, the parties may designate joint primary residential parents, though this requires careful agreement and affects child support calculations.</p>

      <h3 style={h3}>2. Residential Schedule</h3>
      <p style={p}>The plan must specify exactly when the child will be with each parent — a day-by-day schedule. Common schedules include:</p>
      <ul style={ul}>
        <li style={li}><strong>Standard schedule:</strong> Primary parent has the child most of the time; alternate parent has every other weekend plus one weeknight</li>
        <li style={li}><strong>Week on/week off:</strong> Equal alternating weeks with each parent</li>
        <li style={li}><strong>Custom schedule:</strong> Any other arrangement the parties agree upon</li>
      </ul>

      <h3 style={h3}>3. Holiday and Vacation Schedule</h3>
      <p style={p}>The plan must specify how major holidays and school vacations are divided. Tennessee courts expect the plan to address at minimum: Christmas, Thanksgiving, spring break, summer vacation, Mother's Day, Father's Day, and the child's birthday.</p>

      <h3 style={h3}>4. Decision-Making Authority</h3>
      <p style={p}>The plan must specify which parent has authority to make major decisions for the child in four areas:</p>
      <ul style={ul}>
        <li style={li}>Educational decisions</li>
        <li style={li}>Non-emergency healthcare decisions</li>
        <li style={li}>Religious upbringing</li>
        <li style={li}>Extracurricular activities</li>
      </ul>
      <p style={p}>Most uncontested parenting plans assign joint decision-making authority in all four areas, meaning both parents must agree before major decisions are made.</p>

      <h3 style={h3}>5. Child Support</h3>
      <p style={p}>The plan must incorporate the child support obligation calculated on the official Tennessee Child Support Worksheet. Child support cannot be set at zero without documented findings by the court.</p>

      <h3 style={h3}>6. Parent Education Class</h3>
      <p style={p}>Both parents must complete a parent education seminar within 60 days of filing for divorce. The plan must reflect the status of this requirement.</p>

      <h2 style={h2}>Rights of Parents Under Tennessee Law</h2>
      <p style={p}>Every Tennessee parenting plan must acknowledge that both parents retain certain rights regardless of custody arrangement, including the right to unimpeded telephone contact with the child, the right to receive educational and medical records, and the right to participate in school and extracurricular activities (T.C.A. §36-6-101).</p>

      <h2 style={h2}>Parental Relocation</h2>
      <p style={p}>If a parent wants to relocate more than 50 miles from the other parent or outside Tennessee, they must provide at least 60 days written notice by certified mail under T.C.A. §36-6-108. The non-relocating parent has 30 days to object. Failure to give proper notice can result in contempt of court.</p>

      <h2 style={h2}>Can the Parenting Plan Be Modified?</h2>
      <p style={p}>Yes. A Permanent Parenting Plan can be modified by the court if there has been a material change in circumstances affecting the child's best interest. Common reasons for modification include a parent's relocation, a significant change in the child's needs, or a substantial change in either parent's work schedule.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-parenting-plan-requirements" />
      <ContentCTA navigate={navigate} title="Prepare your Tennessee Permanent Parenting Plan today." />
    </div>
  );
}

function PagePropertyDivision({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'How to Divide Property in a Tennessee Divorce',
    'Tennessee is an equitable distribution state. Learn how marital and separate property is divided, how to handle the family home, retirement accounts, and debts in a Tennessee divorce.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>How to Divide Property in a Tennessee Divorce</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · T.C.A. §36-4-121</p>

      <p style={p}>One of the most important aspects of any divorce is dividing the marital estate. Tennessee law provides a framework for property division, but in an uncontested divorce the spouses have wide latitude to divide their assets and debts however they mutually agree. Here is what you need to know.</p>

      <h2 style={h2}>Tennessee Is an Equitable Distribution State</h2>
      <p style={p}>Tennessee follows the principle of <strong>equitable distribution</strong>, meaning marital property is divided fairly — but not necessarily equally. In practice, courts often divide marital property close to 50/50, but they consider many factors. In an uncontested divorce, you and your spouse decide the division yourselves, and the court will generally approve any reasonable agreement.</p>

      <h2 style={h2}>Marital Property vs. Separate Property</h2>
      <h3 style={h3}>Marital Property</h3>
      <p style={p}>Marital property generally includes everything acquired by either spouse during the marriage, regardless of whose name it is in. This includes:</p>
      <ul style={ul}>
        <li style={li}>The marital home and any other real estate purchased during the marriage</li>
        <li style={li}>Vehicles purchased during the marriage</li>
        <li style={li}>Bank accounts, savings accounts, and investment accounts</li>
        <li style={li}>Retirement accounts and pensions accumulated during the marriage</li>
        <li style={li}>Business interests developed during the marriage</li>
        <li style={li}>Debts incurred during the marriage</li>
      </ul>
      <h3 style={h3}>Separate Property</h3>
      <p style={p}>Separate property generally includes assets owned before the marriage or received during the marriage as a gift or inheritance — provided those assets were kept separate and not commingled with marital funds. Separate property is not subject to division in a divorce.</p>

      <h2 style={h2}>Dividing the Marital Home</h2>
      <p style={p}>The family home is often the largest marital asset. In an uncontested divorce, common approaches include:</p>
      <ul style={ul}>
        <li style={li}><strong>One spouse keeps the home</strong> and buys out the other's equity, either with cash or by refinancing the mortgage in their name alone</li>
        <li style={li}><strong>Sell the home</strong> and divide the proceeds</li>
        <li style={li}><strong>Defer sale</strong> — one spouse continues to live there temporarily (common when children are involved) and the home is sold at a later date</li>
      </ul>
      <p style={p}>If one spouse is keeping the home, a <strong>Quitclaim Deed</strong> must be prepared and recorded with the county Register of Deeds to transfer the departing spouse's ownership interest. myTNdivorce.com includes real property provisions in the Marital Dissolution Agreement for packages that include real property.</p>

      <h2 style={h2}>Dividing Retirement Accounts</h2>
      <p style={p}>Retirement accounts accumulated during the marriage are marital property. Dividing a 401(k) or pension typically requires a <strong>Qualified Domestic Relations Order (QDRO)</strong> — a separate court order directing the plan administrator to divide the account. A QDRO must be prepared carefully to avoid tax penalties. This is one area where consulting an attorney is strongly recommended.</p>

      <h2 style={h2}>Dividing Debts</h2>
      <p style={p}>Debts incurred during the marriage are generally considered marital debts and must be allocated in the Marital Dissolution Agreement. Important to note: if a debt is assigned to one spouse in the MDA but that spouse fails to pay it, the creditor can still pursue the other spouse if they were originally on the account. Refinancing or closing joint accounts is the cleanest way to separate marital debt.</p>

      <h2 style={h2}>The Marital Dissolution Agreement</h2>
      <p style={p}>In a Tennessee uncontested divorce, all property and debt division is documented in the <strong>Marital Dissolution Agreement (MDA)</strong>. The MDA is a binding contract between the spouses that the court incorporates into the Final Decree of Divorce. Once signed by both parties and approved by the court, it is enforceable as a court order.</p>
      <p style={p}>myTNdivorce.com prepares a complete, court-ready Marital Dissolution Agreement based on your answers, covering personal property, vehicles, bank accounts, real property, and debts.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-property-division" />
      <ContentCTA navigate={navigate} title="Prepare your Tennessee Marital Dissolution Agreement today." />
    </div>
  );
}

function PageAlimony({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Alimony Guide',
    'Learn about the four types of alimony in Tennessee, how courts decide whether to award spousal support, and how alimony works in an uncontested divorce.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Alimony Guide</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · T.C.A. §36-5-121</p>

      <p style={p}>Alimony — also called spousal support — is financial support paid by one spouse to the other after a divorce. In Tennessee, alimony is not automatically awarded in every divorce. Whether alimony is paid, how much, and for how long depends on the specific circumstances of each marriage. In an uncontested divorce, the spouses decide alimony terms themselves.</p>

      <h2 style={h2}>Types of Alimony in Tennessee</h2>
      <p style={p}>Tennessee recognizes four types of alimony under T.C.A. §36-5-121:</p>

      <h3 style={h3}>1. Alimony in Futuro (Long-Term Alimony)</h3>
      <p style={p}>Periodic payments made indefinitely or until a specific event occurs (such as the recipient's remarriage or death). Typically awarded in long marriages where one spouse has significantly lower earning capacity and cannot become self-supporting.</p>

      <h3 style={h3}>2. Rehabilitative Alimony</h3>
      <p style={p}>The most common type in Tennessee. Paid for a defined period while the recipient spouse gains education, job training, or work experience to become self-supporting. Ends when the recipient achieves economic rehabilitation or the defined period expires.</p>

      <h3 style={h3}>3. Transitional Alimony</h3>
      <p style={p}>Short-term support to help the lower-earning spouse adjust to the economic consequences of the divorce — such as covering moving costs or establishing a new household. Not intended to rehabilitate but to ease the transition.</p>

      <h3 style={h3}>4. Alimony in Solido (Lump Sum Alimony)</h3>
      <p style={p}>A fixed total amount paid either in a lump sum or in installments over a defined period. Unlike other types of alimony, alimony in solido is not modifiable and does not terminate upon remarriage of the recipient.</p>

      <h2 style={h2}>Factors Courts Consider for Alimony</h2>
      <p style={p}>When determining whether to award alimony and in what amount, Tennessee courts consider:</p>
      <ul style={ul}>
        <li style={li}>The length of the marriage</li>
        <li style={li}>Each spouse's earning capacity, education, and employability</li>
        <li style={li}>The standard of living during the marriage</li>
        <li style={li}>Each spouse's financial resources and separate assets</li>
        <li style={li}>Contributions of each spouse to the marriage (including homemaking)</li>
        <li style={li}>Age and health of each spouse</li>
        <li style={li}>Fault in the divorce (in some circumstances)</li>
      </ul>

      <h2 style={h2}>Alimony in an Uncontested Divorce</h2>
      <p style={p}>In an uncontested divorce, the spouses negotiate alimony terms themselves and document them in the Marital Dissolution Agreement. The court will generally approve any reasonable alimony agreement between consenting adults. Common outcomes in uncontested cases include:</p>
      <ul style={ul}>
        <li style={li}><strong>No alimony</strong> — both spouses waive any claim to alimony</li>
        <li style={li}><strong>Lump sum payment</strong> — one spouse pays the other a fixed amount</li>
        <li style={li}><strong>Short-term rehabilitative alimony</strong> — monthly payments for a defined period</li>
      </ul>

      <h2 style={h2}>Can Alimony Be Modified?</h2>
      <p style={p}>Alimony in futuro and rehabilitative alimony can be modified if there is a substantial and material change in circumstances. Alimony in solido generally cannot be modified once set. Any alimony obligation automatically terminates upon the death of either party or the remarriage of the recipient, unless the agreement specifically provides otherwise.</p>

      <h2 style={h2}>Tax Considerations</h2>
      <p style={p}>For divorces finalized after December 31, 2018, alimony payments are no longer deductible by the paying spouse or includable as income by the receiving spouse under federal tax law. Consult a tax professional for advice specific to your situation.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-alimony-guide" />
      <ContentCTA navigate={navigate} title="Document your alimony agreement in a court-ready Tennessee MDA." />
    </div>
  );
}

function PageNameChange({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'How to Change Your Name After Divorce in Tennessee',
    'Learn how to restore your former name through a Tennessee divorce and the step-by-step process for updating Social Security, your driver license, passport, and financial accounts.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>How to Change Your Name After Divorce in Tennessee</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026</p>

      <p style={p}>If you wish to restore your former or maiden name as part of your Tennessee divorce, the process is straightforward — but it must be done correctly in the divorce documents to be legally effective. Here is a complete guide to changing your name through a Tennessee divorce.</p>

      <h2 style={h2}>Who Can Request a Name Change in a Tennessee Divorce?</h2>
      <p style={p}>In Tennessee, either spouse may request a name restoration as part of the divorce. In practice, this is almost always the wife requesting restoration of her maiden or former name. The request must be included in the divorce documents and ordered by the court in the Final Decree of Divorce.</p>

      <h2 style={h2}>How to Include Name Restoration in Your Divorce Documents</h2>
      <p style={p}>To restore your former name through your Tennessee divorce:</p>
      <ul style={ul}>
        <li style={li}>Indicate in your questionnaire that you want your name restored</li>
        <li style={li}>Provide your <strong>complete full legal name</strong> — first, middle, and last — exactly as it should appear on the court order</li>
        <li style={li}>myTNdivorce.com will automatically include the name restoration request in your Complaint for Divorce and the ordered name change in your Final Decree</li>
      </ul>
      <p style={p}><strong>Important:</strong> The name on the court order must match exactly the name you want restored. If your middle name is different or you want a variation of your former name, specify it precisely. The court order will reflect exactly what you request.</p>

      <h2 style={h2}>After the Divorce — Updating Your Name</h2>
      <p style={p}>Once the judge signs the Final Decree of Divorce with the name restoration provision, you will need to update your name with various agencies and institutions. Get at least two certified copies of your Final Decree from the court clerk — you will need them.</p>

      <h3 style={h3}>Step 1: Social Security Administration</h3>
      <p style={p}>Update your name with the Social Security Administration first — other agencies will rely on your SSA record. Visit your local SSA office or apply online at ssa.gov with your certified Final Decree and current photo ID. There is no fee for a name change with SSA.</p>

      <h3 style={h3}>Step 2: Tennessee Driver's License</h3>
      <p style={p}>Visit a Tennessee Driver Services Center with your updated Social Security card and certified Final Decree. You will receive a new license with your restored name.</p>

      <h3 style={h3}>Step 3: U.S. Passport</h3>
      <p style={p}>If your passport was issued less than one year ago, use Form DS-5504 (no fee). If issued more than one year ago, use Form DS-82 (fee applies). Submit with your certified Final Decree.</p>

      <h3 style={h3}>Step 4: Financial Accounts</h3>
      <ul style={ul}>
        <li style={li}>Bank accounts and credit cards</li>
        <li style={li}>Investment and retirement accounts</li>
        <li style={li}>Employer HR records and payroll</li>
        <li style={li}>Health insurance</li>
        <li style={li}>Life insurance beneficiary designations</li>
      </ul>

      <h3 style={h3}>Step 5: Other Important Updates</h3>
      <ul style={ul}>
        <li style={li}>Voter registration</li>
        <li style={li}>Vehicle title and registration</li>
        <li style={li}>Property records (if you own real estate)</li>
        <li style={li}>Professional licenses</li>
        <li style={li}>Email addresses and online accounts</li>
        <li style={li}>Will and estate planning documents</li>
      </ul>

      <h2 style={h2}>Can I Change to a Completely New Name Through Divorce?</h2>
      <p style={p}>No. A divorce can only restore a name you previously held — typically your maiden name or a name from a previous marriage. If you want to adopt an entirely new name, you must file a separate legal name change petition with the court.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-name-change-after-divorce" />
      <ContentCTA navigate={navigate} title="Include name restoration in your Tennessee divorce documents." />
    </div>
  );
}

// ─── CONTENT PAGES ────────────────────────────────────────────────────────────

const contentStyles = {
  wrap: { fontFamily:'Georgia,serif', maxWidth:780, margin:'0 auto', padding:'40px 24px 80px', color:'#222', lineHeight:1.75 },
  h1:   { fontFamily:"'Playfair Display',Georgia,serif", fontSize:'1.9rem', color:'#1a3a2a', marginBottom:8 },
  h2:   { fontFamily:"'Playfair Display',Georgia,serif", fontSize:'1.25rem', color:'#1a3a2a', marginTop:'2rem', marginBottom:'0.5rem' },
  h3:   { fontSize:'1rem', color:'#1a3a2a', marginTop:'1.4rem', marginBottom:'0.3rem', fontWeight:700 },
  p:    { margin:'0.75rem 0' },
  ul:   { paddingLeft:'1.5rem', margin:'0.5rem 0' },
  li:   { margin:'0.4rem 0' },
  cta:  { background:'#f0f7f2', border:'1.5px solid #1a3a2a', borderRadius:10, padding:'24px 28px', marginTop:40 },
};

// Per-page meta description updater with dynamic schema injection
function usePageMeta(title, description) {
  React.useEffect(() => {
    document.title = title + ' | myTNdivorce.com';
    // Meta description
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
    // OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title + ' | myTNdivorce.com');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://mytndivorce.com' + window.location.pathname);
    // Dynamic article schema
    const schemaId = 'dynamic-article-schema';
    let script = document.getElementById(schemaId);
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: 'https://mytndivorce.com' + window.location.pathname,
      datePublished: '2026-04-01',
      dateModified: new Date().toISOString().split('T')[0],
      publisher: { '@type': 'Organization', name: 'myTNdivorce.com', url: 'https://mytndivorce.com' },
    });
    return () => {
      document.title = 'myTNdivorce.com — Tennessee Uncontested Divorce Documents Online';
      if (meta) meta.setAttribute('content', 'Prepare your Tennessee uncontested divorce documents online in minutes. Court-ready forms for all Tennessee counties. No attorney required. Only $29.99 per package.');
      if (ogTitle) ogTitle.setAttribute('content', 'myTNdivorce.com — Tennessee Uncontested Divorce Documents Online');
      if (ogDesc) ogDesc.setAttribute('content', 'Prepare your Tennessee uncontested divorce documents online in minutes. Court-ready forms for all Tennessee counties. No attorney required. Only $29.99 per package.');
      if (script) script.textContent = '';
    };
  }, [title, description]);
}

// Related articles component
const CONTENT_LINKS = [
  { label:'How to File for Divorce in Tennessee',   path:'/how-to-file-divorce-tennessee' },
  { label:'Tennessee Divorce Requirements',          path:'/tennessee-divorce-requirements' },
  { label:'Tennessee Divorce FAQ',                   path:'/tennessee-divorce-faq' },
  { label:'Tennessee Divorce Checklist',             path:'/tennessee-divorce-checklist' },
  { label:'Tennessee Divorce Waiting Periods',       path:'/tennessee-divorce-waiting-periods' },
  { label:'How Child Support Is Calculated',         path:'/tennessee-child-support-calculator' },
  { label:'Tennessee Parenting Plan Requirements',   path:'/tennessee-parenting-plan-requirements' },
  { label:'How to Divide Property in Tennessee',     path:'/tennessee-divorce-property-division' },
  { label:'Tennessee Alimony Guide',                 path:'/tennessee-alimony-guide' },
  { label:'Name Change After Divorce in Tennessee',  path:'/tennessee-name-change-after-divorce' },
];

function RelatedArticles({ navigate, currentPath }) {
  const related = CONTENT_LINKS.filter(l => l.path !== currentPath).slice(0, 5);
  return (
    <div style={{borderTop:'1px solid #d8d0c0',marginTop:40,paddingTop:24}}>
      <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:'1rem',color:'#1a3a2a',fontWeight:700,marginBottom:12}}>
        Related Tennessee Divorce Guides
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8}}>
        {related.map(l => (
          <button key={l.path} onClick={() => navigate(l.path)}
            style={{textAlign:'left',padding:'10px 14px',border:'1px solid #d8d0c0',borderRadius:6,background:'white',cursor:'pointer',color:'#1a3a2a',fontSize:'0.85rem',fontWeight:500,lineHeight:1.4}}>
            {l.label} →
          </button>
        ))}
      </div>
    </div>
  );
}

function ContentNav({ navigate }) {
  return (
    <p>
      <button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',fontSize:'0.9rem',padding:0}}>← myTNdivorce.com</button>
    </p>
  );
}

function ContentCTA({ navigate, title='Ready to prepare your Tennessee divorce documents?' }) {
  return (
    <div style={contentStyles.cta}>
      <div style={{fontWeight:700,fontSize:'1.1rem',color:'#1a3a2a',marginBottom:8}}>{title}</div>
      <p style={{color:'#5a5545',fontSize:'0.9rem',margin:'0 0 16px'}}>
        myTNdivorce.com prepares court-ready Tennessee uncontested divorce documents in minutes. Only $29.99 per package. No attorney required.
      </p>
      <button className="btn btn-gold" onClick={() => navigate('/start')} style={{fontSize:'1rem',padding:'12px 28px'}}>
        Get Started →
      </button>
      <p style={{fontSize:'0.78rem',color:'#8a8070',marginTop:10,marginBottom:0}}>
        Disclaimer: myTNdivorce.com is a document preparation service, not a law firm. Nothing on this page constitutes legal advice.
      </p>
    </div>
  );
}

function PageFAQ({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Divorce FAQ — Frequently Asked Questions',
    'Answers to the most common questions about Tennessee divorce — residency, waiting periods, costs, child support, property division, and more.'
  );

  const faqs = [
    {
      q: 'How long does a divorce take in Tennessee?',
      a: 'An uncontested Tennessee divorce takes a minimum of 60 days from filing if you have no minor children, or 90 days if you have minor children. These are mandatory waiting periods that cannot be shortened. After the waiting period, you schedule a final hearing with the court. Most uncontested divorces are finalized within 2 to 4 months total.'
    },
    {
      q: 'How much does a divorce cost in Tennessee?',
      a: 'An uncontested Tennessee divorce typically costs $300 to $600 total. This includes document preparation ($29.99 through myTNdivorce.com), court filing fees ($150 to $350 depending on the county), notary fees ($5 to $15), and certified copies of the Final Decree ($5 to $10 each). A contested divorce with attorneys can cost $3,000 to $20,000 or more.'
    },
    {
      q: 'Can I get a divorce in Tennessee without my spouse signing?',
      a: 'If your spouse refuses to cooperate, you cannot have an uncontested divorce. You would need to serve them with the divorce papers formally and proceed with a contested divorce, which typically requires an attorney. myTNdivorce.com is designed for uncontested divorces where both spouses agree on all terms.'
    },
    {
      q: 'Does it matter who files for divorce first in Tennessee?',
      a: 'In an uncontested divorce, it generally does not matter who files first. The filing spouse is the Plaintiff and the other spouse is the Defendant, but these labels have no practical significance in an uncontested case where both parties have already agreed on all terms.'
    },
    {
      q: 'Do I need a lawyer to get divorced in Tennessee?',
      a: 'No. Tennessee allows individuals to represent themselves in court (called appearing pro se). For a straightforward uncontested divorce where both spouses agree on all issues, many couples successfully complete the process without an attorney. myTNdivorce.com prepares all required documents. However, if your situation involves complex assets, significant debt disputes, domestic abuse, or any disagreement, consulting an attorney is strongly recommended.'
    },
    {
      q: 'What is an uncontested divorce in Tennessee?',
      a: 'An uncontested divorce means both spouses agree on all aspects of the divorce — including property division, debt allocation, alimony, child custody, parenting schedule, and child support. If you agree on everything, you can file jointly using a Marital Dissolution Agreement and avoid a contested court battle.'
    },
    {
      q: 'How long do I have to live in Tennessee before I can file for divorce?',
      a: 'At least one spouse must have been a bona fide Tennessee resident for a minimum of six months before filing for divorce (T.C.A. 36-4-104). You file in the county where either spouse currently resides.'
    },
    {
      q: 'Can I file for divorce in Tennessee if my spouse lives in another state?',
      a: 'Yes, as long as you have lived in Tennessee for at least six months. You file in the Tennessee county where you reside. Your spouse will need to sign the Marital Dissolution Agreement for an uncontested divorce, which can be done remotely with a notary in their state.'
    },
    {
      q: 'What grounds for divorce are available in Tennessee?',
      a: 'Tennessee recognizes both fault and no-fault grounds for divorce. For an uncontested divorce, most couples use irreconcilable differences (T.C.A. 36-4-101(11)) — a no-fault ground that simply means the marriage cannot be saved. Both spouses must agree to use this ground.'
    },
    {
      q: 'What documents do I need for a Tennessee divorce?',
      a: 'For an uncontested divorce without children or real property, you need a Complaint for Divorce, Marital Dissolution Agreement, and Final Decree of Divorce. If you have minor children, you also need a Permanent Parenting Plan Order and Child Support Worksheet. myTNdivorce.com generates all of these documents automatically.'
    },
    {
      q: 'How is property divided in a Tennessee divorce?',
      a: 'Tennessee is an equitable distribution state, meaning marital property is divided fairly but not necessarily equally. In an uncontested divorce, you and your spouse decide how to divide property yourselves in the Marital Dissolution Agreement. The court will generally approve any reasonable agreement between the parties.'
    },
    {
      q: 'Is Tennessee a 50/50 divorce state?',
      a: 'No. Tennessee uses equitable distribution, not community property. Courts divide marital property fairly based on the circumstances — which often results in a roughly equal split but is not required to be exactly 50/50. In an uncontested divorce, you and your spouse decide the split yourselves.'
    },
    {
      q: 'How is child support calculated in Tennessee?',
      a: 'Tennessee uses the Income Shares Model. Child support is based on both parents gross monthly incomes, the number of children, parenting time (days per year with each parent), health insurance costs, and work-related childcare expenses. The calculation uses the official Tennessee Department of Human Services Income Shares Schedule. myTNdivorce.com calculates child support automatically and generates the required Child Support Worksheet.'
    },
    {
      q: 'Can parents agree to no child support in Tennessee?',
      a: 'Generally no. Tennessee courts require child support to be set in accordance with the state guidelines. A deviation below the guideline amount requires written findings that the guidelines would be unjust or inappropriate and that the deviation serves the best interest of the child. Judges rarely approve zero child support agreements.'
    },
    {
      q: 'What is a Permanent Parenting Plan in Tennessee?',
      a: 'A Permanent Parenting Plan is a court-ordered document required in every Tennessee divorce involving minor children. It designates the Primary Residential Parent, establishes a day-to-day parenting schedule, addresses holidays and vacations, allocates major decision-making authority, and specifies how child support will be paid. myTNdivorce.com generates a complete Permanent Parenting Plan.'
    },
    {
      q: 'Do I have to go to court for an uncontested divorce in Tennessee?',
      a: 'In most Tennessee counties, the filing spouse (Plaintiff) must appear at a brief final hearing before the judge. This hearing typically lasts less than 15 minutes for an uncontested divorce. Some counties allow the hearing to be waived if all paperwork is in perfect order, but this varies by county and judge. The other spouse generally does not need to appear.'
    },
    {
      q: 'Can I change my name back after a Tennessee divorce?',
      a: 'Yes. You can request restoration of your former name as part of the divorce. The request must be included in your divorce documents and ordered by the court in the Final Decree. You must provide your complete full legal name — first, middle, and last — exactly as you want it to appear on the court order.'
    },
    {
      q: 'How do I serve my spouse with divorce papers in Tennessee?',
      a: 'In an uncontested divorce, formal service of process is typically waived. Your spouse signs the Marital Dissolution Agreement, which includes a waiver of formal service. This eliminates the need to hire a process server or go through the sheriff. This is one of the key advantages of an uncontested divorce.'
    },
    {
      q: 'Can I file for divorce online in Tennessee?',
      a: 'You cannot file court documents online in most Tennessee counties — you must physically file at the courthouse. However, you can prepare all your divorce documents online through myTNdivorce.com. Once your documents are ready, you print them, sign them before a notary, and take them to your county courthouse to file.'
    },
    {
      q: 'What happens to the house in a Tennessee divorce?',
      a: 'In an uncontested divorce, you and your spouse decide what happens to the house. Common options are: one spouse keeps the home and buys out the other equity (often by refinancing), the home is sold and proceeds divided, or one spouse stays temporarily and the home is sold later. If one spouse is keeping the home, a Quitclaim Deed transfers ownership. myTNdivorce.com handles the real property provisions in the Marital Dissolution Agreement.'
    },
  ];

  const [openIdx, setOpenIdx] = React.useState(null);

  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Divorce FAQ</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:8}}>Last updated: April 2026</p>
      <p style={{...contentStyles.p, marginBottom:32}}>Answers to the most common questions about filing for divorce in Tennessee. Click any question to expand the answer.</p>

      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {faqs.map((faq, i) => (
          <div key={i} style={{border:'1px solid #d8d0c0',borderRadius:8,overflow:'hidden'}}>
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              style={{width:'100%',textAlign:'left',padding:'14px 18px',background:openIdx===i?'#f0f7f2':'white',border:'none',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}
            >
              <span style={{fontWeight:600,color:'#1a3a2a',fontSize:'0.95rem',lineHeight:1.4}}>{faq.q}</span>
              <span style={{color:'#1a3a2a',fontSize:'1.1rem',flexShrink:0}}>{openIdx===i?'−':'+'}</span>
            </button>
            {openIdx === i && (
              <div style={{padding:'4px 18px 16px',background:'#f0f7f2',fontSize:'0.9rem',lineHeight:1.75,color:'#222'}}>
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-faq" />
      <ContentCTA navigate={navigate} title="Ready to prepare your Tennessee divorce documents?" />
    </div>
  );
}

function PageHowToFile({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'How to File for Uncontested Divorce in Tennessee',
    'Complete step-by-step guide to filing an uncontested divorce in Tennessee. Learn about residency requirements, required documents, filing fees, waiting periods, and the final hearing.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>How to File for Uncontested Divorce in Tennessee</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · For Tennessee Circuit and Chancery Courts</p>

      <p style={p}>Filing for divorce in Tennessee does not have to be expensive or complicated — especially if you and your spouse agree on all the terms. An <strong>uncontested divorce</strong> allows couples to end their marriage cooperatively, without a judge deciding the outcome for them. This guide explains the complete process from start to finish.</p>

      <h2 style={h2}>What Is an Uncontested Divorce in Tennessee?</h2>
      <p style={p}>An uncontested divorce means both spouses agree on every aspect of the divorce, including:</p>
      <ul style={ul}>
        <li style={li}>The grounds for divorce</li>
        <li style={li}>Division of marital property and debts</li>
        <li style={li}>Alimony (if any)</li>
        <li style={li}>Child custody and parenting time (if children are involved)</li>
        <li style={li}>Child support (calculated using Tennessee's Income Shares Guidelines)</li>
      </ul>
      <p style={p}>If you and your spouse cannot agree on any of these issues, the divorce is contested and you will likely need an attorney to represent you in court.</p>

      <h2 style={h2}>Tennessee Residency Requirements</h2>
      <p style={p}>Before filing for divorce in Tennessee, at least one spouse must have been a <strong>bona fide resident of Tennessee for at least six months</strong> prior to filing. You file in the county where either spouse currently resides.</p>

      <h2 style={h2}>Grounds for Divorce in Tennessee</h2>
      <p style={p}>For an uncontested divorce, most couples use <strong>irreconcilable differences</strong> under T.C.A. §36-4-101(11). This is a no-fault ground that simply means the marriage cannot be saved. Both spouses must agree to use this ground, and it cannot be used if the divorce is contested.</p>

      <h2 style={h2}>Step-by-Step: How to File an Uncontested Divorce in Tennessee</h2>

      <h3 style={h3}>Step 1: Prepare Your Documents</h3>
      <p style={p}>An uncontested Tennessee divorce requires the following documents:</p>
      <ul style={ul}>
        <li style={li}><strong>Complaint for Divorce</strong> — filed by the Plaintiff (the spouse initiating the divorce)</li>
        <li style={li}><strong>Marital Dissolution Agreement (MDA)</strong> — the written agreement covering property, debts, and alimony</li>
        <li style={li}><strong>Final Decree of Divorce</strong> — signed by the judge to finalize the divorce</li>
        <li style={li}><strong>Permanent Parenting Plan</strong> — required if you have minor children</li>
        <li style={li}><strong>Child Support Worksheet</strong> — required if you have minor children; uses the official Tennessee DHS Income Shares Schedule</li>
      </ul>
      <p style={p}>myTNdivorce.com prepares all of these documents based on your answers to a guided questionnaire.</p>

      <h3 style={h3}>Step 2: Both Spouses Sign the MDA</h3>
      <p style={p}>The Marital Dissolution Agreement must be signed by both spouses <strong>in front of a notary public</strong>. You can typically find a notary at your bank, UPS Store, or local library. The Defendant (the other spouse) also waives formal service of process by signing the MDA, which saves time and money.</p>

      <h3 style={h3}>Step 3: File With the Court Clerk</h3>
      <p style={p}>Take your signed, notarized documents to the Circuit or Chancery Court Clerk in your county. File the Complaint for Divorce and the Marital Dissolution Agreement. Pay the filing fee — fees vary by county, typically ranging from $150 to $350. If you cannot afford the filing fee, ask the clerk about a pauper's oath (fee waiver).</p>

      <h3 style={h3}>Step 4: Wait the Mandatory Period</h3>
      <p style={p}>Tennessee law requires a mandatory waiting period after the Complaint is filed before the judge can sign the Final Decree:</p>
      <ul style={ul}>
        <li style={li}><strong>60 days</strong> — if you have no minor children</li>
        <li style={li}><strong>90 days</strong> — if you have minor children under 18</li>
      </ul>
      <p style={p}>Use this time to make sure your agreement is final. You can still make changes to your documents during this period.</p>

      <h3 style={h3}>Step 5: Attend the Final Hearing</h3>
      <p style={p}>Schedule a final hearing with the court after the waiting period expires. For an uncontested divorce, this is typically a brief proceeding — often less than 15 minutes. The filing spouse (Plaintiff) usually appears before the judge. Some courts allow the hearing to be waived entirely if all paperwork is in order. The judge reviews the agreement and, if satisfied, signs the Final Decree of Divorce.</p>

      <h3 style={h3}>Step 6: Obtain Certified Copies</h3>
      <p style={p}>After the judge signs the Final Decree, obtain at least two certified copies from the clerk — one for each spouse. You will need these to update your name (if applicable), change beneficiaries, update financial accounts, and for other legal purposes.</p>

      <h2 style={h2}>How Much Does an Uncontested Divorce Cost in Tennessee?</h2>
      <p style={p}>The total cost of an uncontested divorce in Tennessee typically includes:</p>
      <ul style={ul}>
        <li style={li}><strong>Document preparation:</strong> $29.99 (myTNdivorce.com)</li>
        <li style={li}><strong>Court filing fee:</strong> $150–$350 (varies by county)</li>
        <li style={li}><strong>Notary fee:</strong> $5–$15 per signature</li>
        <li style={li}><strong>Certified copies:</strong> $5–$10 each</li>
      </ul>
      <p style={p}>Total cost for most uncontested divorces: <strong>$300–$600</strong>, compared to $3,000–$10,000 or more for a contested divorce with attorneys.</p>

      <h2 style={h2}>Name Restoration</h2>
      <p style={p}>If the Wife wishes to have her former name restored, this can be included in the divorce documents and ordered by the court in the Final Decree at no additional cost. The name restoration must include the full legal name (first, middle, and last) exactly as it should appear on the court order.</p>

      <h2 style={h2}>Can I File Without an Attorney?</h2>
      <p style={p}>Yes. Tennessee allows parties to represent themselves in uncontested divorce proceedings. This is called appearing <em>pro se</em>. The court clerk can answer procedural questions but cannot give legal advice. If your situation involves complex assets, significant debt, domestic abuse, or a disagreement on any issue, you should consult a licensed Tennessee attorney.</p>

      <h2 style={h2}>Which Court Do I File In?</h2>
      <p style={p}>Uncontested divorces in Tennessee are filed in the <strong>Circuit Court</strong> or <strong>Chancery Court</strong> of the county where either spouse resides. Some counties have combined courts. Check your <button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',padding:0,textDecoration:'underline',fontSize:'inherit'}}>county's specific courthouse information</button> for details.</p>

      <RelatedArticles navigate={navigate} currentPath="/how-to-file-divorce-tennessee" />
      <ContentCTA navigate={navigate} />
    </div>
  );
}

function PageRequirements({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Divorce Requirements',
    'Everything you need to qualify for a Tennessee divorce: residency requirements, grounds for divorce, waiting periods, property division rules, and child-related requirements.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Divorce Requirements</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · Applicable statewide in Tennessee</p>

      <p style={p}>Before filing for divorce in Tennessee, you must meet certain legal requirements. This page covers residency, grounds, waiting periods, property rules, and child-related requirements for a Tennessee divorce.</p>

      <h2 style={h2}>1. Residency Requirement</h2>
      <p style={p}>At least one spouse must have been a <strong>bona fide Tennessee resident for a minimum of six months</strong> before filing for divorce (T.C.A. §36-4-104). You file in the county where either spouse currently lives. If neither spouse currently lives in Tennessee but the grounds for divorce occurred in Tennessee, special rules may apply — consult an attorney.</p>

      <h2 style={h2}>2. Grounds for Divorce</h2>
      <p style={p}>Tennessee recognizes both fault and no-fault grounds for divorce. For an uncontested divorce, the most commonly used ground is:</p>
      <ul style={ul}>
        <li style={li}><strong>Irreconcilable differences</strong> (T.C.A. §36-4-101(11)) — a no-fault ground requiring mutual agreement</li>
      </ul>
      <p style={p}>Fault grounds (such as adultery, cruel and inhuman treatment, or abandonment) are also available but require proof and are typically used in contested divorces.</p>

      <h2 style={h2}>3. Mandatory Waiting Periods</h2>
      <p style={p}>Tennessee law requires a waiting period after the Complaint for Divorce is filed before the court can enter a Final Decree:</p>
      <ul style={ul}>
        <li style={li}><strong>60 days</strong> — couples with no minor children</li>
        <li style={li}><strong>90 days</strong> — couples with one or more minor children under 18</li>
      </ul>
      <p style={p}>These waiting periods cannot be waived. The clock starts when the Complaint is filed with the court clerk.</p>

      <h2 style={h2}>4. Property Division Requirements</h2>
      <p style={p}>Tennessee is an <strong>equitable distribution</strong> state, meaning marital property is divided fairly — not necessarily equally. In an uncontested divorce, the spouses decide how to divide property themselves in a Marital Dissolution Agreement. The court generally approves any reasonable agreement between the parties.</p>
      <h3 style={h3}>Marital Property vs. Separate Property</h3>
      <p style={p}><strong>Marital property</strong> generally includes assets and debts acquired during the marriage. <strong>Separate property</strong> generally includes assets owned before marriage or received as gifts or inheritance during the marriage and kept separate. The MDA should clearly identify which assets belong to each spouse.</p>
      <h3 style={h3}>Real Property (Home/Land)</h3>
      <p style={p}>If you own real estate together, the MDA must address how it is divided — whether one spouse keeps it, it is sold, or ownership is otherwise transferred. A Quitclaim Deed may be needed to transfer ownership after the divorce is finalized.</p>

      <h2 style={h2}>5. Alimony (Spousal Support)</h2>
      <p style={p}>Tennessee courts may award alimony based on factors including length of the marriage, each spouse's earning capacity, and standard of living. In an uncontested divorce, the parties decide whether alimony will be paid, how much, and for how long. Common types include:</p>
      <ul style={ul}>
        <li style={li}><strong>Transitional alimony</strong> — short-term support to help the lower-earning spouse adjust</li>
        <li style={li}><strong>Rehabilitative alimony</strong> — support while one spouse gains education or job skills</li>
        <li style={li}><strong>Alimony in futuro</strong> — long-term periodic support</li>
        <li style={li}><strong>Alimony in solido</strong> — a lump-sum payment</li>
      </ul>

      <h2 style={h2}>6. Requirements When Minor Children Are Involved</h2>
      <p style={p}>If the marriage produced minor children (under 18), additional requirements apply.</p>
      <h3 style={h3}>Permanent Parenting Plan</h3>
      <p style={p}>Tennessee requires a <strong>Permanent Parenting Plan Order</strong> in every divorce involving minor children (T.C.A. §36-6-404). The plan must designate a Primary Residential Parent (PRP), establish a residential schedule, address holiday and vacation time, assign decision-making authority, and specify how child support will be paid.</p>
      <h3 style={h3}>Child Support</h3>
      <p style={p}>Child support in Tennessee is calculated using the <strong>Income Shares Model</strong> (T.C.A. §36-5-101), which considers both parents' gross incomes, the number of children, parenting time, health insurance costs, and other expenses. The calculation must be documented on the official Tennessee Department of Human Services Child Support Worksheet, which myTNdivorce.com generates automatically.</p>
      <h3 style={h3}>Parent Education Class</h3>
      <p style={p}>Tennessee courts require both parents to complete a <strong>parent education seminar</strong> within 60 days of filing (T.C.A. §36-6-408). The seminar addresses the impact of divorce on children. Failure to complete the class is punishable by contempt of court.</p>

      <h2 style={h2}>7. Name Restoration</h2>
      <p style={p}>Either spouse may request restoration of a former name as part of the divorce. The request must be included in the divorce documents and ordered by the court. The full legal name (first, middle, and last) must be specified exactly as it should appear on the court order.</p>

      <h2 style={h2}>8. Required Documents</h2>
      <p style={p}>A complete Tennessee uncontested divorce filing includes:</p>
      <ul style={ul}>
        <li style={li}>Complaint for Divorce</li>
        <li style={li}>Marital Dissolution Agreement (signed and notarized by both parties)</li>
        <li style={li}>Final Decree of Divorce</li>
        <li style={li}>Permanent Parenting Plan Order (if minor children)</li>
        <li style={li}>Child Support Worksheet (if minor children)</li>
      </ul>
      <p style={p}>myTNdivorce.com generates all of these documents, properly formatted for any Tennessee Circuit or Chancery Court.</p>

      <h2 style={h2}>9. Filing Fees</h2>
      <p style={p}>Filing fees vary by county and are paid to the court clerk at the time of filing. Fees typically range from $150 to $350. Check your <button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',padding:0,textDecoration:'underline',fontSize:'inherit'}}>county's courthouse information</button> for current fee amounts. Fee waivers are available for those who qualify based on income.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-requirements" />
      <ContentCTA navigate={navigate} title="Ready to get started with your Tennessee divorce?" />
    </div>
  );
}

function PageChildSupport({ navigate }) {
  const { wrap, h1, h2, h3, p, ul, li } = contentStyles;
  usePageMeta(
    'How Is Child Support Calculated in Tennessee?',
    'Tennessee uses the Income Shares Model to calculate child support. Learn the 6-step formula, how parenting time affects support, and how deviations work.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>How Is Child Support Calculated in Tennessee?</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026 · Tennessee Income Shares Guidelines (2021)</p>

      <p style={p}>Tennessee uses the <strong>Income Shares Model</strong> to calculate child support. This model is based on the idea that a child should receive the same proportion of parental income that they would have received if the parents had remained together. Here is a plain-language explanation of how the calculation works.</p>

      <h2 style={h2}>The Basic Formula</h2>
      <p style={p}>Child support in Tennessee is determined by five main factors:</p>
      <ul style={ul}>
        <li style={li}><strong>Both parents' gross monthly incomes</strong></li>
        <li style={li}><strong>Number of children</strong></li>
        <li style={li}><strong>Parenting time</strong> (days per year each parent has the child)</li>
        <li style={li}><strong>Health insurance costs</strong> for the children</li>
        <li style={li}><strong>Work-related childcare expenses</strong></li>
      </ul>

      <h2 style={h2}>Step 1: Calculate Each Parent's Adjusted Gross Income (AGI)</h2>
      <p style={p}>Start with each parent's gross monthly income from all sources — wages, self-employment, Social Security, rental income, and any other regular income. Then subtract:</p>
      <ul style={ul}>
        <li style={li}>Self-employment tax paid</li>
        <li style={li}>Credits for other children living in the home</li>
        <li style={li}>Credits for other children not in the home (with court-ordered support)</li>
      </ul>

      <h2 style={h2}>Step 2: Look Up the Basic Child Support Obligation (BCSO)</h2>
      <p style={p}>The combined AGI of both parents is used to look up the <strong>Basic Child Support Obligation</strong> from the official Tennessee Income Shares Schedule — a table published by the Tennessee Department of Human Services. The BCSO represents the total amount both parents together should contribute to the child's support.</p>

      <h2 style={h2}>Step 3: Determine Each Parent's Percentage Share</h2>
      <p style={p}>Each parent's share of the BCSO is proportional to their share of the combined income. For example, if Mother earns $2,000/month and Father earns $3,000/month, the combined income is $5,000. Mother's share is 40% and Father's share is 60%.</p>

      <h2 style={h2}>Step 4: Apply the Parenting Time Adjustment</h2>
      <p style={p}>If the Alternate Residential Parent (ARP) has the child for <strong>92 or more days per year</strong>, a parenting time adjustment reduces the child support obligation to account for the direct costs the ARP incurs during their parenting time. The more time the ARP has, the greater the reduction.</p>

      <h2 style={h2}>Step 5: Add Additional Expenses</h2>
      <p style={p}>Health insurance premiums and work-related childcare costs are added to the base obligation and shared proportionally between the parents.</p>

      <h2 style={h2}>Step 6: Calculate the Presumptive Child Support Order (PCSO)</h2>
      <p style={p}>The final PCSO is the difference between what each parent owes after all adjustments. The parent with the higher obligation pays the difference to the other parent. The PCSO is <strong>always rounded down to the nearest whole dollar</strong> under Tennessee guidelines.</p>

      <h2 style={h2}>Can Parents Agree to a Different Amount?</h2>
      <p style={p}>Yes — but only under limited circumstances. Parents may deviate from the guideline amount if they document written findings showing that the guideline amount is unjust or inappropriate and that the deviation serves the best interest of the child. Upward deviations (paying more) are generally easier to approve than downward deviations.</p>

      <h2 style={h2}>How Long Does Child Support Last in Tennessee?</h2>
      <p style={p}>Child support continues until the child turns <strong>18</strong> or graduates from high school, whichever occurs later — but not past age 19. Support may continue past 18 if the child is severely disabled.</p>

      <h2 style={h2}>How Is Child Support Paid?</h2>
      <p style={p}>Tennessee strongly recommends payment through the <strong>Central Child Support Receipting Unit</strong> in Nashville, which maintains a record of all payments. This protects both parents — the paying parent has proof of payment, and the receiving parent has a clear record if enforcement becomes necessary.</p>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-child-support-calculator" />
      <ContentCTA navigate={navigate} title="Calculate child support and prepare your parenting plan documents." />
    </div>
  );
}

function PageDivorceChecklist({ navigate }) {
  const { wrap, h1, h2, p, ul, li } = contentStyles;
  usePageMeta(
    'Tennessee Uncontested Divorce Checklist',
    'A complete step-by-step checklist for your Tennessee uncontested divorce — from gathering documents and filing to the final hearing and post-divorce updates.'
  );
  return (
    <div style={wrap}>
      <ContentNav navigate={navigate} />
      <h1 style={h1}>Tennessee Uncontested Divorce Checklist</h1>
      <p style={{color:'#8a8070',fontSize:'0.85rem',marginBottom:24}}>Last updated: April 2026</p>

      <p style={p}>Use this checklist to make sure you have completed every step of your Tennessee uncontested divorce. Print it out and check off each item as you go.</p>

      <h2 style={h2}>Before You File</h2>
      <ul style={ul}>
        <li style={li}>☐ Confirm at least one spouse has lived in Tennessee for 6+ months</li>
        <li style={li}>☐ Confirm your divorce is uncontested — you agree on all terms</li>
        <li style={li}>☐ Gather both spouses' monthly income information</li>
        <li style={li}>☐ List all marital property (home, vehicles, bank accounts, retirement accounts)</li>
        <li style={li}>☐ List all marital debts (mortgage, car loans, credit cards, student loans)</li>
        <li style={li}>☐ If children: confirm custody arrangement and parenting schedule</li>
        <li style={li}>☐ If children: have both parents' income ready for child support calculation</li>
        <li style={li}>☐ Decide on alimony (if any)</li>
        <li style={li}>☐ Decide on name restoration (if Wife wants former name restored)</li>
      </ul>

      <h2 style={h2}>Prepare Your Documents</h2>
      <ul style={ul}>
        <li style={li}>☐ Complete the myTNdivorce.com questionnaire</li>
        <li style={li}>☐ Review all generated documents carefully</li>
        <li style={li}>☐ Complaint for Divorce ✓</li>
        <li style={li}>☐ Marital Dissolution Agreement ✓</li>
        <li style={li}>☐ Final Decree of Divorce ✓</li>
        <li style={li}>☐ Permanent Parenting Plan (if children) ✓</li>
        <li style={li}>☐ Child Support Worksheet (if children) ✓</li>
        <li style={li}>☐ Download and save copies of all documents</li>
      </ul>

      <h2 style={h2}>Signing and Notarization</h2>
      <ul style={ul}>
        <li style={li}>☐ Both spouses sign the Marital Dissolution Agreement</li>
        <li style={li}>☐ Both signatures notarized before a notary public</li>
        <li style={li}>☐ If children: both spouses sign the Permanent Parenting Plan</li>
        <li style={li}>☐ Plaintiff signs the Complaint for Divorce (notarized)</li>
      </ul>

      <h2 style={h2}>Filing With the Court</h2>
      <ul style={ul}>
        <li style={li}>☐ Find your <button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',padding:0,textDecoration:'underline',fontSize:'inherit'}}>county courthouse address and phone number</button></li>
        <li style={li}>☐ Call ahead to confirm current filing fees and office hours</li>
        <li style={li}>☐ Take original signed documents plus at least 2 copies</li>
        <li style={li}>☐ File Complaint for Divorce and MDA with the Circuit or Chancery Court Clerk</li>
        <li style={li}>☐ Pay the filing fee (or file pauper's oath if unable to pay)</li>
        <li style={li}>☐ Get your case number from the clerk</li>
        <li style={li}>☐ Note the date filed — your waiting period clock starts now</li>
      </ul>

      <h2 style={h2}>Waiting Period</h2>
      <ul style={ul}>
        <li style={li}>☐ Wait 60 days (no minor children) or 90 days (minor children) from filing date</li>
        <li style={li}>☐ If children: both parents complete the parent education seminar within 60 days of filing</li>
      </ul>

      <h2 style={h2}>Final Hearing</h2>
      <ul style={ul}>
        <li style={li}>☐ Contact court clerk to schedule final hearing after waiting period</li>
        <li style={li}>☐ Bring original Final Decree of Divorce to the hearing</li>
        <li style={li}>☐ Attend hearing (Plaintiff typically appears; some courts waive appearance)</li>
        <li style={li}>☐ Judge signs Final Decree of Divorce — divorce is final</li>
        <li style={li}>☐ Obtain at least 2 certified copies of the Final Decree from the clerk</li>
      </ul>

      <h2 style={h2}>After the Divorce</h2>
      <ul style={ul}>
        <li style={li}>☐ Update your name on Social Security card (if name was restored)</li>
        <li style={li}>☐ Update driver's license</li>
        <li style={li}>☐ Update passport</li>
        <li style={li}>☐ Update bank accounts and credit cards</li>
        <li style={li}>☐ Update beneficiary designations on life insurance and retirement accounts</li>
        <li style={li}>☐ Update your will and estate planning documents</li>
        <li style={li}>☐ Transfer vehicle titles if needed</li>
        <li style={li}>☐ Record Quitclaim Deed if real property was transferred</li>
        <li style={li}>☐ Set up child support payment through the Central Receipting Unit (if applicable)</li>
      </ul>

      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-checklist" />
      <RelatedArticles navigate={navigate} currentPath="/tennessee-divorce-checklist" />
      <ContentCTA navigate={navigate} title="Ready to prepare your Tennessee divorce documents?" />
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
function PageTerms({ navigate }) {
  const s = { fontFamily:'Georgia,serif', maxWidth:780, margin:'0 auto', padding:'40px 24px 80px', color:'#222', lineHeight:1.7 };
  const h2 = { fontSize:'1.05rem', color:'#1a3a2a', marginTop:'1.8rem', marginBottom:'0.4rem' };
  return (
    <div style={s}>
      <p><button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',fontSize:'0.9rem',padding:0}}>← myTNdivorce.com</button></p>
      <h1 style={{fontSize:'1.6rem',color:'#1a3a2a',borderBottom:'2px solid #1a3a2a',paddingBottom:12}}>Terms of Service</h1>
      <p style={{fontSize:'0.85rem',color:'#666',marginBottom:'2rem'}}>Last updated: June 18, 2026</p>
      <h2 style={h2}>1. Nature of Service</h2>
      <p>myTNdivorce.com ("the Service," "we," "us") is a document preparation service that assists individuals in completing Tennessee uncontested divorce forms. <strong>We are not a law firm. We do not provide legal advice. No attorney-client relationship is created by your use of this Service.</strong> The documents generated through this Service are based solely on the information you provide.</p>
      <p>Use of this Service does not substitute for the advice of a licensed Tennessee attorney. You are strongly encouraged to consult with a licensed attorney before filing any documents with any court.</p>
      <h2 style={h2}>2. No Warranties</h2>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE DOCUMENTS GENERATED WILL BE ACCEPTED BY ANY COURT OR THAT THEY ARE APPROPRIATE FOR YOUR SPECIFIC CIRCUMSTANCES.</p>
      <h2 style={h2}>3. No Legal Advice</h2>
      <p>Nothing on this website, in the documents generated, or in any communication from myTNdivorce.com constitutes legal advice. The information and forms provided are for general informational and self-help purposes only. Every divorce involves unique facts and circumstances that may significantly affect the legal outcome. Only a licensed attorney who has reviewed the specific facts of your case can provide legal advice.</p>
      <h2 style={h2}>4. Changes in the Law</h2>
      <p>We make reasonable efforts to keep the forms and information provided through this Service accurate and current. However, the law changes frequently, may differ from county to county, and may be subject to interpretation by different courts and judges. We cannot guarantee that all information or forms provided through the Service are completely current, correct, or applicable to your specific circumstances. No general legal tool or self-help form can account for every situation. If your circumstances are unusual or complex, or if you are unsure whether these documents are appropriate for your case, you should consult a licensed Tennessee attorney.</p>
      <h2 style={h2}>5. Eligibility</h2>
      <p>This Service is designed for uncontested Tennessee divorces only. By using this Service, you represent that your divorce is uncontested, that you meet Tennessee residency requirements, and that the information you provide is accurate and complete. If your divorce is contested or involves complex issues such as business interests, significant debt disputes, or domestic abuse, you should consult an attorney.</p>
      <h2 style={h2}>6. Additional Court Requirements</h2>
      <p>The documents prepared through this Service may not include every form required by the specific court in which you file. Individual Tennessee courts may require additional documents, such as cover sheets, divorce certificates, statistical or vital-records forms, or other local forms. You are solely responsible for determining and obtaining any additional documents required by your court, which are typically available from the clerk's office of the court where you file. We do not guarantee that the documents generated through this Service constitute a complete filing package for your particular court.</p>
      <h2 style={h2}>7. Payment and Refund Policy</h2>
      <p>All fees are charged at the time of purchase and are <strong>non-refundable</strong>. Because documents are generated immediately upon payment, we are unable to offer refunds once the document generation process has begun. If you experience a technical problem that prevents you from receiving your documents, please contact us at <a href="mailto:info@mytndivorce.com" style={{color:'#1a3a2a'}}>info@mytndivorce.com</a> and we will work to resolve the issue.</p>
      <h2 style={h2}>8. Document Storage and Access</h2>
      <p>Your generated documents are stored securely for 30 days from the date of purchase. After 30 days, your documents are permanently and automatically deleted from our servers. <strong>You are strongly encouraged to download your documents promptly after generation.</strong> You may extend your storage period for an additional 30 days for $4.99.</p>
      <h2 style={h2}>9. Privacy and Confidentiality</h2>
      <p>The administrator of myTNdivorce.com does not access, review, or retain the personal information you enter or the documents you generate. Please review our <a href="/privacy" style={{color:'#1a3a2a'}}>Privacy Policy</a> for full details.</p>
      <h2 style={h2}>10. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, myTNdivorce.com SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE.</p>
      <h2 style={h2}>11. Governing Law</h2>
      <p>These Terms shall be governed by Tennessee law. Any legal action shall be brought exclusively in the courts of Hamilton County, Tennessee.</p>
      <h2 style={h2}>12. Dispute Resolution; Arbitration; Class Action Waiver</h2>
      <p>Any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Service shall be resolved by binding arbitration on an individual basis, rather than in court, except that either party may bring qualifying claims in small claims court. Arbitration shall be conducted in Hamilton County, Tennessee, under the rules of the American Arbitration Association. <strong>YOU AND myTNdivorce.com AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.</strong> By agreeing to these Terms, you and myTNdivorce.com each waive the right to a trial by jury and the right to participate in a class action.</p>
      <h2 style={h2}>13. Contact</h2>
      <p>Questions? Contact us at <a href="mailto:info@mytndivorce.com" style={{color:'#1a3a2a'}}>info@mytndivorce.com</a>.</p>
    </div>
  );
}

function PagePrivacy({ navigate }) {
  const s = { fontFamily:'Georgia,serif', maxWidth:780, margin:'0 auto', padding:'40px 24px 80px', color:'#222', lineHeight:1.7 };
  const h2 = { fontSize:'1.05rem', color:'#1a3a2a', marginTop:'1.8rem', marginBottom:'0.4rem' };
  return (
    <div style={s}>
      <p><button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'#1a3a2a',cursor:'pointer',fontSize:'0.9rem',padding:0}}>← myTNdivorce.com</button></p>
      <h1 style={{fontSize:'1.6rem',color:'#1a3a2a',borderBottom:'2px solid #1a3a2a',paddingBottom:12}}>Privacy Policy</h1>
      <p style={{fontSize:'0.85rem',color:'#666',marginBottom:'2rem'}}>Last updated: April 28, 2026</p>
      <h2 style={h2}>1. Our Commitment to Your Privacy</h2>
      <p>myTNdivorce.com is committed to protecting the privacy and confidentiality of your personal information. <strong>The administrator of this Service does not access, review, or retain the personal information you enter or the documents you generate.</strong> You are strongly encouraged to download your completed documents as soon as they are generated.</p>
      <h2 style={h2}>2. Information We Collect</h2>
      <p>We collect only: your email address and hashed password for account access; questionnaire responses stored temporarily to allow document editing during the 30-day access period; payment confirmation from Stripe (we never store your card details); and basic server logs for security purposes.</p>
      <h2 style={h2}>3. How We Use Your Information</h2>
      <p>Your information is used solely to generate your documents, allow access during the 30-day storage period, send transactional emails, and process payment. We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
      <h2 style={h2}>4. Administrator Access</h2>
      <p>The administrator of myTNdivorce.com does not access or review the personal information you enter into the questionnaire or the documents generated on your behalf. Your data exists on our servers solely for your access during the 30-day storage period.</p>
      <h2 style={h2}>5. Document Storage and Deletion</h2>
      <p>Your questionnaire responses and generated documents are <strong>permanently and automatically deleted</strong> after 30 days. This deletion is irreversible. <strong>Download your documents as soon as they are generated.</strong></p>
      <h2 style={h2}>6. Data Security</h2>
      <p>We use HTTPS/TLS encryption, hashed password storage, secure database access controls, and automatic data deletion. No electronic storage is 100% secure, but we use commercially reasonable measures to protect your information.</p>
      <h2 style={h2}>7. Third-Party Services</h2>
      <p>We use Stripe for payment processing, SendGrid for transactional email, Render for server hosting, and Netlify for website hosting. These providers are contractually obligated to protect your information.</p>
      <h2 style={h2}>8. Cookies</h2>
      <p>We use only essential authentication cookies. We do not use tracking, advertising, or analytics cookies.</p>
      <h2 style={h2}>9. Contact</h2>
      <p>Questions? Contact us at <a href="mailto:info@mytndivorce.com" style={{color:'#1a3a2a'}}>info@mytndivorce.com</a>.</p>
    </div>
  );
}

function PageCountyGuide({ countyName, navigate, onStart }) {
  const { COUNTIES } = require('./counties');
  const county = COUNTIES.find(c => c.name.toLowerCase() === (countyName || '').toLowerCase());

  if (!county) {
    // County list page
    return (
      <div style={{maxWidth:900,margin:'0 auto',padding:'40px 24px 80px'}}>
        <p><button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:0}}>← myTNdivorce.com</button></p>
        <h1 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.8rem',marginBottom:8}}>
          How to File for Divorce in Tennessee — by County
        </h1>
        <p style={{color:'var(--muted)',marginBottom:32,maxWidth:620}}>
          Select your county below for specific courthouse information, court type, and filing guidance for your Tennessee uncontested divorce.
        </p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8}}>
          {[...COUNTIES].sort((a,b)=>a.name.localeCompare(b.name)).map(c => (
            <button key={c.name} onClick={() => navigate(`/divorce/${c.name.toLowerCase().replace(/ /g,'-')}-county`)}
              style={{textAlign:'left',padding:'10px 14px',border:'1px solid var(--border)',borderRadius:6,background:'white',cursor:'pointer',color:'var(--forest)',fontSize:'0.88rem',fontWeight:500}}>
              {c.name} County
            </button>
          ))}
        </div>
      </div>
    );
  }

  const waitingPeriod = '60 days (no minor children) or 90 days (with minor children)';

  // Major county additional context
  const majorCountyInfo = {
    Shelby: {
      context: "Shelby County is Tennessee's most populous county and home to Memphis, the state's largest city. The Shelby County divorce courts handle thousands of cases annually. Shelby County has a dedicated Family Law Division within the Circuit Court that handles divorce and custody matters.",
      tip: "Shelby County's courts are among the busiest in Tennessee. File your documents early in the day and allow extra time. E-filing is available for some Chancery Court matters."
    },
    Davidson: {
      context: "Davidson County is home to Nashville, Tennessee's capital and largest metropolitan area. The Metropolitan Government of Nashville and Davidson County operates combined courts. Divorce cases are handled in the 3rd and 4th Circuit Courts or Chancery Court.",
      tip: "Nashville's courts offer e-filing through the Tennessee Courts electronic filing system. Check the Chancery Court website for current e-filing availability before visiting in person."
    },
    Knox: {
      context: "Knox County is home to Knoxville and the University of Tennessee. The Knox County court system is well-organized and efficient. Both Circuit Court and Chancery Court handle divorce matters in Knox County.",
      tip: "Knox County's clerk's offices are located in the City-County Building in downtown Knoxville. Parking can be limited — plan accordingly and allow extra time."
    },
    Hamilton: {
      context: "Hamilton County is home to Chattanooga, Tennessee's fourth-largest city. Hamilton County has separate Circuit Court and Chancery Court divisions, both located at the Hamilton County Courthouse on Georgia Avenue.",
      tip: "Hamilton County's courts are accessible by public transit and have convenient parking nearby. The clerk's offices are generally efficient for uncontested divorce filings."
    },
    Rutherford: {
      context: "Rutherford County is one of Tennessee's fastest-growing counties, home to Murfreesboro and Middle Tennessee State University. The court system has expanded to accommodate rapid population growth.",
      tip: "Due to rapid population growth, Rutherford County courts can have busy dockets. File as early in the morning as possible and expect some wait times."
    },
    Williamson: {
      context: "Williamson County is Tennessee's wealthiest county and one of its fastest-growing, home to Franklin and Brentwood. The court system serves a large and growing population in the Nashville suburbs.",
      tip: "Williamson County courts are located in Franklin. The area has significant traffic congestion — allow extra travel time especially during morning rush hours."
    },
  };

  const extra = majorCountyInfo[county.name];

  useEffect(() => {
    document.title = `How to File for Divorce in ${county.name} County, Tennessee | myTNdivorce.com`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', `Complete guide to filing an uncontested divorce in ${county.name} County, Tennessee. Courthouse address, phone, filing fees, required documents, waiting periods, and step-by-step instructions.`);
    return () => {
      document.title = 'myTNdivorce.com — Tennessee Uncontested Divorce Documents Online';
      if (meta) meta.setAttribute('content', 'Prepare your Tennessee uncontested divorce documents online in minutes. Court-ready forms for all Tennessee counties. No attorney required. Only $29.99 per package.');
    };
  }, [county.name]);

  return (
    <div style={{maxWidth:780,margin:'0 auto',padding:'40px 24px 80px',fontFamily:'Georgia,serif',color:'#222',lineHeight:1.7}}>
      <p style={{fontFamily:'var(--ff-body)'}}>
        <button onClick={() => navigate('/divorce')} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:0}}>← All Counties</button>
        {' '}<span style={{color:'#aaa'}}>·</span>{' '}
        <button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:0}}>myTNdivorce.com</button>
      </p>

      <h1 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.7rem',marginBottom:4}}>
        How to File for Divorce in {county.name} County, Tennessee
      </h1>
      <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:32}}>
        A guide to filing an uncontested divorce in {county.name} County, Tennessee — courthouse information, court type, waiting periods, and what to expect.
      </p>

      <div style={{background:'#f0f7f2',border:'1.5px solid var(--forest)',borderRadius:10,padding:'20px 24px',marginBottom:32,fontFamily:'var(--ff-body)',fontSize:'0.9rem'}}>
        <div style={{fontWeight:700,color:'var(--forest)',marginBottom:12,fontSize:'1rem'}}>📍 {county.name} County Courthouse</div>
        <div style={{display:'grid',gap:6}}>
          <div><strong>Courthouse:</strong> {county.courthouse}</div>
          <div><strong>Phone:</strong> {county.phone}</div>
          <div><strong>Court type:</strong> {county.court} Court</div>
          <div><strong>Judicial District:</strong> {county.district}</div>
          <div><strong>Seat:</strong> {county.city}, Tennessee</div>
          <div><a href={county.website} target="_blank" rel="noopener noreferrer" style={{color:'var(--forest)'}}>Official Court Website →</a></div>
        </div>
        <p style={{marginTop:12,marginBottom:0,fontSize:'0.82rem',color:'var(--stone)'}}>
          ⚠️ Court fees change periodically. Always call the clerk's office to confirm current filing fees before visiting.
        </p>
      </div>

      {extra && (
        <div style={{background:'#f8f6f0',border:'1px solid var(--border)',borderRadius:8,padding:'16px 20px',marginBottom:24,fontSize:'0.9rem',lineHeight:1.7}}>
          <div style={{fontWeight:700,color:'var(--forest)',marginBottom:6}}>About {county.name} County Courts</div>
          <p style={{margin:'0 0 8px'}}>{extra.context}</p>
          <p style={{margin:0,color:'var(--muted)'}}><strong>Tip:</strong> {extra.tip}</p>
        </div>
      )}

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        Which Court Handles Divorce in {county.name} County?
      </h2>
      <p>{county.notes} In Tennessee, uncontested divorces may be filed in either the Circuit Court or Chancery Court of the county where either spouse resides. If you are unsure which court to use, call the clerk's office and ask — they will direct you to the correct division.</p>

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        Tennessee Residency Requirements
      </h2>
      <p>To file for divorce in {county.name} County, at least one spouse must have been a Tennessee resident for at least six months before filing. The divorce should be filed in the county where either spouse currently resides.</p>

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        Waiting Period
      </h2>
      <p>Tennessee law requires a mandatory waiting period after the Complaint for Divorce is filed: <strong>{waitingPeriod}</strong>. The judge cannot sign the Final Decree until this waiting period has passed. Both spouses do not need to be present at the final hearing for an uncontested divorce, though some judges prefer both parties to appear.</p>

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        Documents Required for an Uncontested Divorce in {county.name} County
      </h2>
      <p>For an uncontested divorce with no minor children and no real property, you will need:</p>
      <ul style={{paddingLeft:'1.5rem'}}>
        <li>Complaint for Divorce</li>
        <li>Marital Dissolution Agreement</li>
        <li>Final Decree of Divorce</li>
        <li>Divorce Certificate (obtained from the clerk)</li>
      </ul>
      <p>If you have minor children, you will also need:</p>
      <ul style={{paddingLeft:'1.5rem'}}>
        <li>Permanent Parenting Plan Order</li>
        <li>Child Support Worksheet (official Tennessee DHS form)</li>
      </ul>
      <p>If you own real property (home, land), your Marital Dissolution Agreement must include a real property division section, and you may also need a Quitclaim Deed.</p>

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        Filing Fees in {county.name} County
      </h2>
      <p>Filing fees vary by county and change periodically. Contact the {county.name} County {county.court} Court Clerk at <strong>{county.phone}</strong> for current filing fees. Fees typically range from $150–$350 depending on the county and whether children are involved. If you cannot afford the filing fee, you may apply for a fee waiver (pauper's oath) from the clerk's office.</p>

      <h2 style={{fontFamily:'var(--ff-display)',color:'var(--forest)',fontSize:'1.2rem',marginTop:'2rem',marginBottom:'0.5rem'}}>
        How to File Your Divorce in {county.name} County
      </h2>
      <ol style={{paddingLeft:'1.5rem',lineHeight:2}}>
        <li>Prepare your divorce documents (myTNdivorce.com handles this step)</li>
        <li>Both spouses sign and notarize the Marital Dissolution Agreement</li>
        <li>File the Complaint and MDA with the {county.name} County {county.court} Court Clerk</li>
        <li>Pay the filing fee (or file a pauper's oath if unable to pay)</li>
        <li>Wait the mandatory {waitingPeriod}</li>
        <li>Attend the final hearing (some counties require both spouses; most only require the filing spouse)</li>
        <li>The judge signs the Final Decree — your divorce is final</li>
      </ol>

      <div style={{background:'#f8f6f0',border:'1px solid var(--border)',borderRadius:10,padding:'24px 28px',marginTop:40,fontFamily:'var(--ff-body)'}}>
        <div style={{fontWeight:700,fontSize:'1.1rem',color:'var(--forest)',marginBottom:8}}>
          Ready to prepare your {county.name} County divorce documents?
        </div>
        <p style={{color:'var(--muted)',fontSize:'0.9rem',margin:'0 0 16px'}}>
          myTNdivorce.com prepares court-ready Tennessee uncontested divorce documents in minutes — formatted for {county.name} County {county.court} Court. Only $29.99 per package.
        </p>
        <button className="btn btn-gold" onClick={onStart} style={{fontSize:'1rem',padding:'12px 28px'}}>
          Get Started →
        </button>
      </div>

      <p style={{marginTop:32,fontSize:'0.8rem',color:'var(--stone)',fontFamily:'var(--ff-body)'}}>
        <strong>Disclaimer:</strong> This page provides general information only and does not constitute legal advice. Court procedures, fees, and requirements change. Always verify current information with the {county.name} County court clerk. myTNdivorce.com is a document preparation service, not a law firm. No attorney-client relationship is created by use of this site.
      </p>

      <div style={{borderTop:'1px solid #d8d0c0',marginTop:32,paddingTop:24}}>
        <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:'1rem',color:'var(--forest)',fontWeight:700,marginBottom:12}}>
          Tennessee Divorce Guides
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8}}>
          {CONTENT_LINKS.map(l => (
            <button key={l.path} onClick={() => navigate(l.path)}
              style={{textAlign:'left',padding:'10px 14px',border:'1px solid #d8d0c0',borderRadius:6,background:'white',cursor:'pointer',color:'var(--forest)',fontSize:'0.85rem',fontWeight:500,lineHeight:1.4}}>
              {l.label} →
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PageAccountSettings({ authFetch, onBack }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) return setResult({ error: 'New passwords do not match' });
    if (newPassword.length < 8) return setResult({ error: 'Password must be at least 8 characters' });
    setLoading(true); setResult(null);
    try {
      const res  = await authFetch('/auth/change-password', { method:'POST', body: JSON.stringify({ currentPassword, newPassword }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResult({ success: 'Password changed successfully.' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { setResult({ error: err.message }); }
    finally { setLoading(false); }
  }

  return (
    <div className="main-content"><div className="container" style={{maxWidth:500}}>
      <button onClick={onBack} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:'0 0 16px',display:'block'}}>← Back to Dashboard</button>
      <h2 style={{color:'var(--forest)',fontFamily:'var(--ff-display)',marginBottom:24}}>Account Settings</h2>

      <div style={{background:'white',border:'1px solid var(--border)',borderRadius:10,padding:28,marginBottom:24}}>
        <h3 style={{color:'var(--forest)',marginBottom:20,fontSize:'1rem'}}>Change Password</h3>
        <form onSubmit={handleChangePassword}>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>Current Password</label>
            <input type="password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} required
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>New Password</label>
            <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} required minLength={8}
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} required
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
          </div>
          {result?.error   && <div style={{background:'#fff0f0',border:'1px solid #f0b0b0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'#b03030',marginBottom:14}}>{result.error}</div>}
          {result?.success && <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'var(--forest)',marginBottom:14}}>{result.success}</div>}
          <button className="btn btn-primary" type="submit" disabled={loading} style={{width:'100%'}}>
            {loading ? 'Saving...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div></div>
  );
}

function PageForgotPassword({ onBack, navigate }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/auth/forgot-password`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSent(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="main-content"><div className="container" style={{maxWidth:460}}>
      <button onClick={onBack} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:'0 0 16px',display:'block'}}>← Back to Login</button>
      <h2 style={{color:'var(--forest)',fontFamily:'var(--ff-display)',marginBottom:8}}>Forgot Password</h2>
      {sent ? (
        <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:10,padding:24,textAlign:'center'}}>
          <div style={{fontSize:'2rem',marginBottom:12}}>📧</div>
          <div style={{fontWeight:600,color:'var(--forest)',marginBottom:8}}>Check your email</div>
          <p style={{fontSize:'0.88rem',color:'var(--muted)'}}>If an account exists for <strong>{email}</strong>, we sent a password reset link. Check your inbox and spam folder. The link expires in 1 hour.</p>
          <button className="btn btn-outline" style={{marginTop:20}} onClick={onBack}>Back to Login</button>
        </div>
      ) : (
        <>
          <p style={{color:'var(--muted)',fontSize:'0.88rem',marginBottom:24}}>Enter your email address and we will send you a link to reset your password.</p>
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>Email Address</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="your@email.com"
                style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
            </div>
            {error && <div style={{background:'#fff0f0',border:'1px solid #f0b0b0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'#b03030',marginBottom:14}}>{error}</div>}
            <button className="btn btn-primary" type="submit" disabled={loading} style={{width:'100%'}}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        </>
      )}
    </div></div>
  );
}

function PageResetPassword({ navigate }) {
  const token = new URLSearchParams(window.location.search).get('token');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) return setResult({ error: 'Passwords do not match' });
    if (newPassword.length < 8) return setResult({ error: 'Password must be at least 8 characters' });
    setLoading(true); setResult(null);
    try {
      const res  = await fetch(`${API}/auth/reset-password`, {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResult({ success: true });
    } catch (err) { setResult({ error: err.message }); }
    finally { setLoading(false); }
  }

  if (!token) return (
    <div className="main-content"><div className="container" style={{maxWidth:460,textAlign:'center',paddingTop:60}}>
      <div style={{fontSize:'2rem',marginBottom:12}}>⚠️</div>
      <p>Invalid reset link. <button onClick={() => navigate('/')} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',textDecoration:'underline'}}>Return to home</button></p>
    </div></div>
  );

  return (
    <div className="main-content"><div className="container" style={{maxWidth:460}}>
      <h2 style={{color:'var(--forest)',fontFamily:'var(--ff-display)',marginBottom:8}}>Set New Password</h2>
      {result?.success ? (
        <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:10,padding:24,textAlign:'center'}}>
          <div style={{fontSize:'2rem',marginBottom:12}}>✅</div>
          <div style={{fontWeight:600,color:'var(--forest)',marginBottom:8}}>Password reset successfully</div>
          <p style={{fontSize:'0.88rem',color:'var(--muted)',marginBottom:20}}>You can now log in with your new password.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Log In</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{marginTop:24}}>
          <div style={{marginBottom:14}}>
            <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>New Password</label>
            <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} required minLength={8}
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:'block',fontWeight:600,fontSize:'0.85rem',marginBottom:6}}>Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} required
              style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border)',borderRadius:6,fontSize:'0.9rem',boxSizing:'border-box'}} />
          </div>
          {result?.error && <div style={{background:'#fff0f0',border:'1px solid #f0b0b0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'#b03030',marginBottom:14}}>{result.error}</div>}
          <button className="btn btn-primary" type="submit" disabled={loading} style={{width:'100%'}}>
            {loading ? 'Saving...' : 'Set New Password'}
          </button>
        </form>
      )}
    </div></div>
  );
}

function PageHowItWorks({ navigate }) {
  usePageMeta(
    'How It Works — myTNdivorce.com',
    'Watch step-by-step video guides showing how to prepare your Tennessee uncontested divorce documents online with myTNdivorce.com.'
  );

  const videos = [
    {
      title: 'Step 1: Create an Account',
      description: 'Learn how to create your account and begin the questionnaire.',
      embed: `<iframe src="https://scribehow.com/embed/Create_an_Account_for_Divorce_Document_Preparation__GlLzvqcYRvKqxo8we6fIcQ?as=video" width="100%" height="800" allow="fullscreen" style="aspect-ratio: 16 / 12; border: 0; min-height: 480px"></iframe>`,
    },
    {
      title: 'Step 2: Completing a Divorce Filing',
      description: 'See how to answer each section of the questionnaire, including child support calculations.',
      embed: `<iframe src="https://scribehow.com/embed/Completing_a_Divorce_Filing_on_MyTN_Divorce__UnhE2opyRpGQfn9FZScmjg?as=video" width="100%" height="800" allow="fullscreen" style="aspect-ratio: 16 / 12; border: 0; min-height: 480px"></iframe>`,
    },
    {
      title: 'Step 3: Update Terms, Email Documents, and Regenerate',
      description: 'Learn how to edit your answers, regenerate updated documents, and email them directly to your spouse.',
      embed: `<iframe src="https://scribehow.com/embed/How_to_Update_Alimony_Terms_and_Email_Divorce_Documents__MrRHLYDPSMeuiNfYGaIMIg?as=video" width="100%" height="800" allow="fullscreen" style="aspect-ratio: 16 / 12; border: 0; min-height: 480px"></iframe>`,
    },
  ];

  return (
    <div style={{fontFamily:'Georgia,serif',maxWidth:860,margin:'0 auto',padding:'40px 24px 80px',color:'#222'}}>
      <ContentNav navigate={navigate} />
      <h1 style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:'1.9rem',color:'#1a3a2a',marginBottom:8}}>
        How It Works
      </h1>
      <p style={{color:'#5a5545',fontSize:'0.95rem',marginBottom:40,maxWidth:620}}>
        Watch these short step-by-step guides to see exactly how myTNdivorce.com works — from creating your account to downloading your completed Tennessee divorce documents.
      </p>

      {/* Limited-time pricing notice — guides may show older prices */}
      <div style={{background:'linear-gradient(135deg,#fff8e6 0%,#fdf0cf 100%)',border:'2px solid #c9a227',borderRadius:10,padding:'16px 22px',marginBottom:40,textAlign:'center'}}>
        <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontWeight:700,fontSize:'1.15rem',color:'#1a3a2a',marginBottom:6}}>
          ★ LIMITED-TIME PRICING — ALL PACKAGES $29.99
        </div>
        <div style={{color:'#5a5545',fontSize:'0.92rem'}}>
          Every package — with or without children or real property — is <strong style={{color:'#1a3a2a'}}>$29.99</strong> for a limited time. The guides below may show our previous pricing — please disregard any older amounts shown in the videos.
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:48}}>
        {videos.map((video, i) => (
          <div key={i} style={{borderBottom:'1px solid #d8d0c0',paddingBottom:48}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8}}>
              <div style={{width:32,height:32,borderRadius:'50%',background:'#1a3a2a',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'0.9rem',flexShrink:0}}>
                {i + 1}
              </div>
              <h2 style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:'1.25rem',color:'#1a3a2a',margin:0}}>
                {video.title}
              </h2>
            </div>
            <p style={{color:'#5a5545',fontSize:'0.88rem',marginBottom:20,marginLeft:44}}>
              {video.description}
            </p>
            <div style={{borderRadius:10,overflow:'hidden',boxShadow:'0 4px 20px rgba(0,0,0,0.1)',background:'#f0f7f2',minHeight:200,display:'flex',alignItems:'center',justifyContent:'center'}}>
              {video.embed ? (
                <div style={{width:'100%'}} dangerouslySetInnerHTML={{__html: video.embed}} />
              ) : (
                <div style={{textAlign:'center',padding:40,color:'#8a8070'}}>
                  <div style={{fontSize:'2rem',marginBottom:8}}>🎬</div>
                  <div style={{fontSize:'0.85rem'}}>Video coming soon</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{background:'#f0f7f2',border:'1.5px solid #1a3a2a',borderRadius:10,padding:'24px 28px',marginTop:40}}>
        <div style={{fontWeight:700,fontSize:'1.1rem',color:'#1a3a2a',marginBottom:8}}>
          Ready to get started?
        </div>
        <p style={{color:'#5a5545',fontSize:'0.9rem',margin:'0 0 16px'}}>
          Tennessee uncontested divorce documents for only $29.99 per package. No attorney required.
        </p>
        <button className="btn btn-gold" onClick={() => navigate('/start')} style={{fontSize:'1rem',padding:'12px 28px'}}>
          Start My Questionnaire →
        </button>
      </div>
    </div>
  );
}

const COUPON_INP = { width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:'0.88rem', boxSizing:'border-box' };

function CouponForm({ onSubmit, isEdit, form, setForm, saving, onCancel }) {
  return (
    <div style={{background:'white',border:'1px solid var(--border)',borderRadius:10,padding:24,marginBottom:24}}>
      <h3 style={{color:'var(--forest)',marginBottom:16}}>{isEdit ? `Edit ${form.code}` : 'New Coupon'}</h3>
      <form onSubmit={onSubmit}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Code *</label>
            <input style={{...COUPON_INP, opacity: isEdit ? 0.6 : 1}} value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} placeholder="SAVE99" required readOnly={isEdit} />
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Description</label>
            <input style={COUPON_INP} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="$99 for all packages" />
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Discount Type</label>
            <select style={COUPON_INP} value={form.discount_type} onChange={e=>setForm({...form,discount_type:e.target.value,discount_value:e.target.value==='free'?'':form.discount_value})}>
              <option value="free">Free — 100% off (bypasses Stripe)</option>
              <option value="fixed">Fixed $ off all packages</option>
              <option value="percent">Percentage % off all packages</option>
              <option value="flat">Flat price for all packages (e.g. $99)</option>
              <option value="per_package">Custom price per package</option>
            </select>
          </div>
          {form.discount_type !== 'per_package' && form.discount_type !== 'free' && (
            <div>
              <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>
                {form.discount_type==='fixed' ? 'Dollars off' : form.discount_type==='flat' ? 'Flat price ($)' : 'Percent off'} *
              </label>
              <input style={COUPON_INP} type="number" min="0" step="0.01" value={form.discount_value}
                onChange={e=>setForm({...form,discount_value:e.target.value})}
                placeholder={form.discount_type==='fixed'?'26':form.discount_type==='flat'?'99':'20'} required />
            </div>
          )}
          {form.discount_type === 'free' && (
            <div style={{background:'#f0f7f2',border:'1px solid #a8c8a0',borderRadius:6,padding:'10px 14px',fontSize:'0.85rem',color:'var(--forest)',display:'flex',alignItems:'center'}}>
              Free coupons bypass Stripe — customer gets documents at no charge.
            </div>
          )}
          {form.discount_type === 'per_package' && (<>
            <div>
              <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Cat 1 — No Kids, No Property</label>
              <input style={COUPON_INP} type="number" min="0" step="0.01" value={form.price_cat1} onChange={e=>setForm({...form,price_cat1:e.target.value})} placeholder="blank = no discount" />
            </div>
            <div>
              <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Cat 2 — No Kids, With Property</label>
              <input style={COUPON_INP} type="number" min="0" step="0.01" value={form.price_cat2} onChange={e=>setForm({...form,price_cat2:e.target.value})} placeholder="blank = no discount" />
            </div>
            <div>
              <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Cat 3 — With Kids, No Property</label>
              <input style={COUPON_INP} type="number" min="0" step="0.01" value={form.price_cat3} onChange={e=>setForm({...form,price_cat3:e.target.value})} placeholder="blank = no discount" />
            </div>
            <div>
              <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Cat 4 — With Kids, With Property</label>
              <input style={COUPON_INP} type="number" min="0" step="0.01" value={form.price_cat4} onChange={e=>setForm({...form,price_cat4:e.target.value})} placeholder="blank = no discount" />
            </div>
          </>)}
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Valid From (optional)</label>
            <input style={COUPON_INP} type="date" value={form.valid_from} onChange={e=>setForm({...form,valid_from:e.target.value})} />
          </div>
          <div>
            <label style={{display:'block',fontWeight:600,fontSize:'0.82rem',marginBottom:4}}>Valid Until (optional)</label>
            <input style={COUPON_INP} type="date" value={form.valid_until} onChange={e=>setForm({...form,valid_until:e.target.value})} />
          </div>
        </div>
        <div style={{display:'flex',gap:20,marginBottom:16}}>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.88rem',cursor:'pointer'}}>
            <input type="checkbox" checked={form.multi_use} onChange={e=>setForm({...form,multi_use:e.target.checked})} />
            Multi-use (once per customer)
          </label>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.88rem',cursor:'pointer'}}>
            <input type="checkbox" checked={form.active} onChange={e=>setForm({...form,active:e.target.checked})} />
            Active
          </label>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-outline" type="button" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Coupon'}</button>
        </div>
      </form>
    </div>
  );
}

function PageAdminCoupons({ authFetch, onBack }) {
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const emptyForm = { code:'', description:'', discount_type:'free', discount_value:'', price_cat1:'', price_cat2:'', price_cat3:'', price_cat4:'', multi_use:true, active:true, valid_from:'', valid_until:'' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadCoupons(); }, []);

  async function loadCoupons() {
    setLoading(true);
    try {
      const res = await authFetch('/admin/coupons');
      const data = await res.json();
      if (res.ok) setCoupons(data);
    } finally { setLoading(false); }
  }

  function buildBody(f) {
    return {
      ...f,
      discount_value: f.discount_value !== '' ? parseFloat(f.discount_value) : null,
      price_cat1: f.price_cat1 !== '' ? parseFloat(f.price_cat1) : null,
      price_cat2: f.price_cat2 !== '' ? parseFloat(f.price_cat2) : null,
      price_cat3: f.price_cat3 !== '' ? parseFloat(f.price_cat3) : null,
      price_cat4: f.price_cat4 !== '' ? parseFloat(f.price_cat4) : null,
      valid_from: f.valid_from || null,
      valid_until: f.valid_until || null,
    };
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res  = await authFetch('/admin/coupons', { method:'POST', body: JSON.stringify(buildBody(form)) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setShowForm(false); setForm(emptyForm); loadCoupons();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`/admin/coupons/${editCoupon.id}`, { method:'PATCH', body: JSON.stringify(buildBody(form)) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditCoupon(null); setForm(emptyForm); loadCoupons();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  function startEdit(c) {
    setForm({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: c.discount_value != null ? String(c.discount_value) : '',
      price_cat1: c.price_cat1 != null ? String(c.price_cat1) : '',
      price_cat2: c.price_cat2 != null ? String(c.price_cat2) : '',
      price_cat3: c.price_cat3 != null ? String(c.price_cat3) : '',
      price_cat4: c.price_cat4 != null ? String(c.price_cat4) : '',
      multi_use: c.multi_use,
      active: c.active,
      valid_from: c.valid_from ? c.valid_from.split('T')[0] : '',
      valid_until: c.valid_until ? c.valid_until.split('T')[0] : '',
    });
    setEditCoupon(c);
    setShowForm(false);
  }

  async function toggleActive(coupon) {
    try {
      const res = await authFetch(`/admin/coupons/${coupon.id}`, { method:'PATCH', body: JSON.stringify({ active: !coupon.active }) });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      loadCoupons();
    } catch (err) { alert(err.message); }
  }

  async function deleteCoupon(coupon) {
    if (!window.confirm(`Delete coupon "${coupon.code}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/admin/coupons/${coupon.id}`, { method:'DELETE' });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      loadCoupons();
    } catch (err) { alert(err.message); }
  }

  const inp = { width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:'0.88rem', boxSizing:'border-box' };

  return (
    <div className="main-content"><div className="container">
      <button onClick={onBack} style={{background:'none',border:'none',color:'var(--forest)',cursor:'pointer',fontSize:'0.9rem',padding:'0 0 16px',display:'block'}}>← Back to Dashboard</button>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24}}>
        <h2 style={{margin:0,color:'var(--forest)',fontFamily:'var(--ff-display)'}}>Coupon Codes</h2>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setEditCoupon(null); setForm(emptyForm); }} type="button">+ Create Coupon</button>
      </div>

      {showForm && !editCoupon && <CouponForm onSubmit={handleCreate} isEdit={false} form={form} setForm={setForm} saving={saving} onCancel={() => { setShowForm(false); setEditCoupon(null); setForm(emptyForm); }} />}
      {editCoupon && <CouponForm onSubmit={handleEdit} isEdit={true} form={form} setForm={setForm} saving={saving} onCancel={() => { setEditCoupon(null); setForm(emptyForm); }} />}

      {loading ? <div className="spinner" /> : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {coupons.length === 0 && <p style={{color:'var(--muted)'}}>No coupon codes yet.</p>}
          {coupons.map(c => (
            <div key={c.id} style={{background:'white',border:'1px solid var(--border)',borderRadius:8,padding:'14px 18px',display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
              <div style={{fontFamily:'monospace',fontWeight:700,fontSize:'1rem',color:'var(--forest)',minWidth:100}}>{c.code}</div>
              <div style={{flex:1,minWidth:200}}>
                <div style={{fontSize:'0.85rem',fontWeight:600}}>
                  {c.discount_type === 'free' ? '100% off (Free)' : c.discount_type === 'fixed' ? `$${c.discount_value} off` : c.discount_type === 'flat' ? `Flat $${c.discount_value}` : c.discount_type === 'per_package' ? 'Per-package pricing' : `${c.discount_value}% off`}
                  {' · '}{c.multi_use ? 'Multi-use' : 'Single-use'}
                </div>
                <div style={{fontSize:'0.78rem',color:'var(--muted)'}}>
                  {c.description && <span>{c.description} · </span>}
                  {c.use_count} use{c.use_count !== '1' ? 's' : ''}
                  {c.valid_from && ` · From ${new Date(c.valid_from).toLocaleDateString()}`}
                  {c.valid_until && ` · Until ${new Date(c.valid_until).toLocaleDateString()}`}
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:'0.8rem',fontWeight:600,color:c.active?'var(--forest)':'var(--stone)'}}>
                  {c.active ? '● Active' : '○ Inactive'}
                </span>
                <button className="btn btn-outline" style={{fontSize:'0.78rem',padding:'5px 12px'}}
                  onClick={() => startEdit(c)} type="button">
                  Edit
                </button>
                <button className="btn btn-outline" style={{fontSize:'0.78rem',padding:'5px 12px'}}
                  onClick={() => toggleActive(c)} type="button">
                  {c.active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn btn-outline" style={{fontSize:'0.78rem',padding:'5px 12px',borderColor:'var(--error)',color:'var(--error)'}}
                  onClick={() => deleteCoupon(c)} type="button">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div></div>
  );
}

export default function App() {
  const auth = useAuth();

  // Determine initial page from URL
  const path = window.location.pathname;
  const getInitialPage = () => {
    if (path === '/dashboard' || window.location.search.includes('session_id') || window.location.search.includes('upgrade_session_id') || window.location.search.includes('extension_session_id')) return 'dashboard';
    if (path === '/login') return 'login';
    if (path === '/terms' || path === '/terms.html') return 'terms';
    if (path === '/privacy' || path === '/privacy.html') return 'privacy';
    if (path.startsWith('/divorce/') || path === '/divorce') return 'county';
    if (path === '/how-it-works') return 'howitworks';
    if (path === '/forgot-password') return 'forgotpassword';
    if (path === '/reset-password') return 'resetpassword';
    if (path === '/admin/coupons') return 'admincoupons';
    if (path === '/account-settings') return 'accountsettings';
    if (path === '/tennessee-divorce-faq') return 'faq';
    if (path === '/how-to-file-divorce-tennessee') return 'howtofile';
    if (path === '/tennessee-divorce-requirements') return 'requirements';
    if (path === '/tennessee-child-support-calculator') return 'childsupport';
    if (path === '/tennessee-divorce-checklist') return 'checklist';
    if (path === '/tennessee-divorce-waiting-periods') return 'waitingperiods';
    if (path === '/tennessee-parenting-plan-requirements') return 'parentingplan';
    if (path === '/tennessee-divorce-property-division') return 'propertydivision';
    if (path === '/tennessee-alimony-guide') return 'alimony';
    if (path === '/tennessee-name-change-after-divorce') return 'namechange';
    return 'landing';
  };

  const getInitialCounty = () => {
    if (path.startsWith('/divorce/')) return decodeURIComponent(path.replace('/divorce/', '').replace(/-county/i, '').replace(/-/g, ' '));
    return null;
  };

  const [page, setPage]         = useState(getInitialPage);
  const [county, setCounty]     = useState(getInitialCounty);
  const [step, setStep]         = useState(1);
  const [category, setCategory] = useState(null);
  const [formData, setFormData] = useState({});
  const [coupon,   setCoupon]   = useState(null); // validated coupon object from /coupons/validate
  const [excludedReason, setExcludedReason] = useState(null);
  const [editingOrder,   setEditingOrder]   = useState(null);
  const [upgradingOrder, setUpgradingOrder] = useState(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  const [savedDraft, setSavedDraft] = useState(() => {
    try {
      const raw = localStorage.getItem('mytndivorce_draft');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Discard drafts older than 30 days
      if (Date.now() - (parsed.savedAt || 0) > 30 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem('mytndivorce_draft');
        return null;
      }
      return parsed;
    } catch { return null; }
  });

  function resumeDraft() {
    if (!savedDraft) return;
    setCategory(savedDraft.category);
    setFormData(savedDraft.data || {});
    setSavedDraft(null);
    setPage('questionnaire');
  }

  function dismissDraft() {
    try { localStorage.removeItem('mytndivorce_draft'); } catch {}
    setSavedDraft(null);
  }

  // Central navigation function — use this instead of <a href> for all internal links
  function navigate(path) {
    window.scrollTo(0, 0);
    if (path === '/' || path === '') { setPage('landing'); window.history.pushState({}, '', '/'); }
    else if (path === '/dashboard') { setPage('dashboard'); window.history.pushState({}, '', '/dashboard'); }
    else if (path === '/terms') { setPage('terms'); window.history.pushState({}, '', '/terms'); }
    else if (path === '/privacy') { setPage('privacy'); window.history.pushState({}, '', '/privacy'); }
    else if (path === '/divorce') { setCounty(null); setPage('county'); window.history.pushState({}, '', '/divorce'); }
    else if (path.startsWith('/divorce/')) {
      const slug = path.replace('/divorce/', '').replace(/-county$/i, '').replace(/-/g, ' ');
      setCounty(slug); setPage('county');
      window.history.replaceState({}, '', path);
    }
    else if (path === '/how-it-works') { setPage('howitworks'); window.history.pushState({}, '', path); }
    else if (path === '/forgot-password') { setPage('forgotpassword'); window.history.pushState({}, '', path); }
    else if (path === '/admin/coupons') { setPage('admincoupons'); window.history.pushState({}, '', path); }
    else if (path === '/account-settings') { setPage('accountsettings'); window.history.pushState({}, '', path); }
    else if (path === '/reset-password') { setPage('resetpassword'); window.history.pushState({}, '', path + window.location.search); }
    else if (path === '/tennessee-divorce-faq') { setPage('faq'); window.history.pushState({}, '', path); }
    else if (path === '/how-to-file-divorce-tennessee') { setPage('howtofile'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-divorce-requirements') { setPage('requirements'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-child-support-calculator') { setPage('childsupport'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-divorce-checklist') { setPage('checklist'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-divorce-waiting-periods') { setPage('waitingperiods'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-parenting-plan-requirements') { setPage('parentingplan'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-divorce-property-division') { setPage('propertydivision'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-alimony-guide') { setPage('alimony'); window.history.pushState({}, '', path); }
    else if (path === '/tennessee-name-change-after-divorce') { setPage('namechange'); window.history.pushState({}, '', path); }
    else if (path === '/start') { setPage('screener'); window.history.pushState({}, '', '/'); }
  }

  // Redirect to dashboard if logged in and on login page
  useEffect(() => {
    if (!auth.loading && auth.user && page === 'login') setPage('dashboard');
  }, [auth.loading, auth.user, page]);

  // Handle browser back/forward button
  useEffect(() => {
    function handlePopState() {
      const p = window.location.pathname;
      if (p === '/dashboard') { setPage('dashboard'); }
      else if (p === '/terms') { setPage('terms'); }
      else if (p === '/privacy') { setPage('privacy'); }
      else if (p.startsWith('/divorce/')) {
        const countySlug = decodeURIComponent(p.replace('/divorce/', '').replace(/-county$/i, '').replace(/-/g, ' '));
        setCounty(countySlug);
        setPage('county');
      }
      else if (p === '/divorce') { setCounty(null); setPage('county'); }
      else if (p === '/how-it-works') { setPage('howitworks'); }
      else if (p === '/forgot-password') { setPage('forgotpassword'); }
      else if (p === '/admin/coupons') { setPage('admincoupons'); }
      else if (p === '/account-settings') { setPage('accountsettings'); }
      else if (p === '/reset-password') { setPage('resetpassword'); }
      else if (p === '/tennessee-divorce-faq') { setPage('faq'); }
      else if (p === '/how-to-file-divorce-tennessee') { setPage('howtofile'); }
      else if (p === '/tennessee-divorce-requirements') { setPage('requirements'); }
      else if (p === '/tennessee-child-support-calculator') { setPage('childsupport'); }
      else if (p === '/tennessee-divorce-checklist') { setPage('checklist'); }
      else if (p === '/tennessee-divorce-waiting-periods') { setPage('waitingperiods'); }
      else if (p === '/tennessee-parenting-plan-requirements') { setPage('parentingplan'); }
      else if (p === '/tennessee-divorce-property-division') { setPage('propertydivision'); }
      else if (p === '/tennessee-alimony-guide') { setPage('alimony'); }
      else if (p === '/tennessee-name-change-after-divorce') { setPage('namechange'); }
      else { setPage('landing'); }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const showProgress = ['screener','package','account','questionnaire','review'].includes(page);
  const stepNum = { screener:1, package:2, account:3, questionnaire:4, review:5 }[page] || 1;

  function handleLogout() { auth.logout(); setPage('landing'); setStep(1); }

  function handleDashboard() {
    if (page === 'questionnaire') {
      if (!window.confirm('Your progress has been saved to your browser. Go to My Account?')) return;
    }
    setPage('dashboard');
  }

  if (auth.loading) return (
    <div className="page-wrap">
      <Header user={null} onLogout={handleLogout} onDashboard={handleDashboard} navigate={navigate} />
      <div className="main-content" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div className="spinner" />
      </div>
    </div>
  );

  return (
    <div className="page-wrap">
      <Header user={auth.user} onLogout={handleLogout} onDashboard={handleDashboard} navigate={navigate} />
      {showProgress && <ProgressBar currentStep={stepNum} />}

      {page === 'landing' && savedDraft && (
        <div style={{background:'#f0f7f2',borderBottom:'1.5px solid var(--forest)',padding:'14px 24px'}}>
          <div className="container" style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <span style={{fontSize:'1.1rem'}}>👋</span>
            <span style={{flex:1,fontSize:'0.9rem',color:'var(--forest)',fontWeight:500}}>
              Welcome back — we saved your questionnaire progress. Pick up where you left off?
            </span>
            <button className="btn btn-primary" style={{padding:'8px 18px',fontSize:'0.85rem'}} onClick={resumeDraft}>
              Continue My Questionnaire →
            </button>
            <button className="btn btn-outline" style={{padding:'8px 14px',fontSize:'0.82rem'}} onClick={dismissDraft}>
              Start Over
            </button>
          </div>
        </div>
      )}
      {page === 'landing' && <PageLanding onStart={() => setPage('screener')} navigate={navigate} coupon={coupon} setCoupon={setCoupon} authFetch={auth.authFetch} />}

      {page === 'screener' && (
        <StepScreener
          onNext={cat => { setCategory(cat); setPage('package'); }}
          onExcluded={reason => { setExcludedReason(reason); setPage('excluded'); }}
        />
      )}

      {page === 'excluded' && <PageExcluded reason={excludedReason} onBack={() => setPage('screener')} />}

      {page === 'package' && (
        <StepPackage
          category={category}
          coupon={coupon}
          onNext={() => setPage(auth.user ? 'questionnaire' : 'account')}
          onBack={() => setPage('screener')}
        />
      )}

      {page === 'account' && (
        <StepAccount
          onNext={() => setPage('questionnaire')}
          onBack={() => setPage('package')}
          auth={auth}
        />
      )}

      {page === 'questionnaire' && (
        <StepQuestionnaire
          category={category}
          initialData={formData}
          onNext={data => { setFormData(data); setPage('review'); }}
          onBack={() => {
            // Save draft before leaving questionnaire
            setPage(auth.user ? 'package' : 'account');
          }}
        />
      )}

      {page === 'review' && (
        <StepReview
          category={category}
          formData={formData}
          onBack={() => setPage('questionnaire')}
          authFetch={auth.authFetch}
          coupon={coupon}
          setCoupon={setCoupon}
        />
      )}

      {page === 'dashboard' && !auth.user && (
        <PageLogin auth={auth} onDone={() => setPage('dashboard')} />
      )}

      {page === 'dashboard' && auth.user && !editingOrder && !upgradingOrder && (
        <PageDashboard
          key={dashboardKey}
          authFetch={auth.authFetch}
          navigate={navigate}
          onStartEdit={order => setEditingOrder(order)}
          onStartUpgrade={order => setUpgradingOrder(order)}
        />
      )}

      {page === 'dashboard' && auth.user && upgradingOrder && (
        <PageUpgrade
          order={upgradingOrder}
          authFetch={auth.authFetch}
          onDone={() => setUpgradingOrder(null)}
          onBack={() => setUpgradingOrder(null)}
        />
      )}

      {page === 'dashboard' && auth.user && editingOrder && (
        <PageEdit
          order={editingOrder}
          authFetch={auth.authFetch}
          onDone={() => { setEditingOrder(null); setDashboardKey(k => k + 1); }}
          onBack={() => setEditingOrder(null)}
        />
      )}

      {page === 'login' && <PageLogin auth={auth} onDone={() => setPage('dashboard')} />}
      {page === 'howitworks'    && <PageHowItWorks    navigate={navigate} />}
      {page === 'forgotpassword' && <PageForgotPassword onBack={() => navigate('/dashboard')} navigate={navigate} />}
      {page === 'resetpassword'  && <PageResetPassword navigate={navigate} />}
      {page === 'admincoupons'  && auth.user && (
        <PageAdminCoupons authFetch={auth.authFetch} onBack={() => navigate('/dashboard')} />
      )}
      {page === 'accountsettings' && auth.user && (
        <PageAccountSettings
          authFetch={auth.authFetch}
          onBack={() => navigate('/dashboard')}
        />
      )}
      {page === 'terms'       && <PageTerms         navigate={navigate} />}
      {page === 'privacy'     && <PagePrivacy       navigate={navigate} />}
      {page === 'faq'          && <PageFAQ            navigate={navigate} />}
      {page === 'howtofile'     && <PageHowToFile       navigate={navigate} />}
      {page === 'requirements'  && <PageRequirements    navigate={navigate} />}
      {page === 'childsupport'  && <PageChildSupport    navigate={navigate} />}
      {page === 'checklist'     && <PageDivorceChecklist navigate={navigate} />}
      {page === 'waitingperiods'&& <PageWaitingPeriods  navigate={navigate} />}
      {page === 'parentingplan' && <PageParentingPlan   navigate={navigate} />}
      {page === 'propertydivision' && <PagePropertyDivision navigate={navigate} />}
      {page === 'alimony'       && <PageAlimony         navigate={navigate} />}
      {page === 'namechange'    && <PageNameChange      navigate={navigate} />}
      {page === 'county'  && <PageCountyGuide
        countyName={county}
        navigate={navigate}
        onStart={() => navigate('/start')}
      />}

      <Footer navigate={navigate} />
    </div>
  );
}
